import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createLocalDatabaseClient } from './lib/localDatabaseClient';
import { getAppDemoUrl, isAppDemoLocation, isMarketingHostname } from './utils/demoRouting';
import { getLegalPageKey } from './utils/legalRouting';
import { Home, RefreshCw } from 'lucide-react';
import { buildSalesAnalysisSnapshot } from './utils/salesAnalysis';
import { getArticlePricing } from './utils/articleMargin';
import { LIGHT_MODE_COLORS } from './utils/constants';
import { getOrderStatusLabel } from './utils/jtlStatusMatrix';
import {
    NOTIFICATION_CATEGORY_DEFAULTS,
    getNotificationPreferenceCategory,
    isInsideNotificationWindow,
    readDashboardUiState,
    readStoredTenantId,
    readUserPreferences,
    writeDashboardUiState,
    writeStoredTenantId,
    writeUserPreferences
} from './utils/dashboardStorage';
import {
    DEFAULT_WARNING_SETTINGS,
    WARNING_SETTINGS_UPDATED_EVENT,
    readWarningSettings,
    writeWarningSettings
} from './utils/warningSettings';
import {
    getAlertInstanceKey,
    getAlertLifecycleKey,
    getNotificationSeverityRank,
    readNotificationLifecycle,
    reconcileNotificationLifecycle
} from './utils/notificationLifecycle';
import {
    normalizeNotificationArticleIds,
    validateNotificationAlertTargets
} from './utils/notificationConsistency';

// Import Components
import Auth from './components/Auth';
import DemoLoadingScreen from './components/DemoLoadingScreen';
import Sidebar from './components/Sidebar';
import DateRangePicker from './components/DateRangePicker';
import HeaderFilterPanel, { ActiveFiltersContext } from './components/HeaderFilterPanel';
import HeaderNotifications from './components/HeaderNotifications';
import HeaderUserMenu from './components/HeaderUserMenu';
import LoadingSkeleton from './components/LoadingSkeleton';

import LandingPage from './pages/LandingPage';
import LegalPage from './pages/LegalPage';
import SupportPage from './pages/SupportPage';
import SeoTopicPage, { isSeoTopicPath } from './pages/SeoTopicPage';
import BlogPage, { isBlogPath } from './pages/BlogPage';

// Dashboard pages are loaded on demand so the login and first screen do not
// download every analysis module up front.
const Overview = React.lazy(() => import('./pages/Overview'));
const MapPage = React.lazy(() => import('./pages/MapPage'));
const Customers = React.lazy(() => import('./pages/Customers'));
const Comparison = React.lazy(() => import('./pages/Comparison'));
const Costs = React.lazy(() => import('./pages/Costs'));
const Admin = React.lazy(() => import('./pages/Admin'));
const Articles = React.lazy(() => import('./pages/Articles'));
const Returns = React.lazy(() => import('./pages/Returns'));
const Warehouse = React.lazy(() => import('./pages/Warehouse'));
const WarehouseOperations = React.lazy(() => import('./pages/WarehouseOperations'));
const DhlBillingPage = React.lazy(() => import('./pages/DhlBillingPage'));
const Reports = React.lazy(() => import('./pages/Reports'));
const KpiWidgets = React.lazy(() => import('./pages/KpiWidgets'));
const ControllingReports = React.lazy(() => import('./pages/ControllingReports'));
const DataQuality = React.lazy(() => import('./pages/DataQuality'));
const ProfileSettings = React.lazy(() => import('./pages/ProfileSettings'));



const MoonIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size || '24'} height={props.size || '24'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401" />
    </svg>
);

const DashboardViewportLock = () => {
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const viewportElements = [document.documentElement, document.body, document.getElementById('root')].filter(Boolean);
        const previousStyles = viewportElements.map(element => ({
            element,
            height: element.style.height,
            overflow: element.style.overflow,
            overscrollBehavior: element.style.overscrollBehavior,
        }));

        viewportElements.forEach(element => {
            element.style.height = '100%';
            element.style.overflow = 'hidden';
            element.style.overscrollBehavior = 'none';
        });

        return () => {
            previousStyles.forEach(({ element, height, overflow, overscrollBehavior }) => {
                element.style.height = height;
                element.style.overflow = overflow;
                element.style.overscrollBehavior = overscrollBehavior;
            });
        };
    }, []);

    return null;
};

const MissingConfigurationScreen = ({ message }) => (
    <div className="min-h-screen bg-[#070C12] px-6 py-10 text-white">
        <div className="mx-auto flex min-h-[70vh] max-w-2xl items-center justify-center">
            <div className="w-full rounded-xl border border-[#263445] bg-[#0D151F] p-6 shadow-2xl">
                <p className="text-sm font-semibold uppercase tracking-wide text-[#E55C1A]">Setup erforderlich</p>
                <h1 className="mt-3 text-2xl font-bold">Dashboard kann noch nicht starten</h1>
                <p className="mt-3 text-sm leading-6 text-slate-300">
                    {message}
                </p>
                <div className="mt-5 rounded-lg border border-[#263445] bg-[#09111C] p-4 text-sm text-slate-200">
                    Pruefe die lokale API-Konfiguration und starte den Dashboard-Server erneut.
                    <div className="mt-3 space-y-1 font-mono text-xs text-[#9FD88D]">
                        <div>REACT_APP_LOCAL_DATABASE_URL</div>
                        <div>REACT_APP_LOCAL_DATABASE_ANON_KEY</div>
                        <div>REACT_APP_REPORTS_API_URL</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

class DashboardErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        console.error('Dashboard render failed:', error, info);
    }

    render() {
        if (this.state.error) {
            return (
                <MissingConfigurationScreen
                    message={`Die App wurde geladen, aber React ist beim Rendern abgestuerzt: ${this.state.error?.message || 'Unbekannter Fehler'}`}
                />
            );
        }

        return this.props.children;
    }
}

const safeNumber = (...values) => {
    for (const value of values) {
        if (!hasValue(value)) continue;
        const number = Number(value);
        if (Number.isFinite(number)) return number;
    }
    return 0;
};

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const formatNotificationCurrency = (value) => Number(value || 0).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
});

const getNotificationArticleName = (item) => String(item?.name || item?.article_name || item?.sku || 'Unbenannter Artikel');

const normalizeNotificationKey = (value) => String(value || '').trim().toLocaleLowerCase('de-DE');

const normalizeNotificationList = (values = []) => (
    Array.isArray(values)
        ? values
        : String(values || '').split(/[,\n;]/g)
)
    .map(value => String(value || '').trim())
    .filter(Boolean);

const notificationIdentityValues = (item = {}) => [
    item.article_id,
    item.id,
    item.name,
    item.article_name,
    item.sku,
    item.article_number,
    item.cArtNr
].map(value => String(value || '').trim()).filter(Boolean);

const isNotificationProductExcluded = (item, excludedProducts) => (
    notificationIdentityValues(item).some(value => excludedProducts.has(normalizeNotificationKey(value)))
);

const isNotificationMarketplaceExcluded = (item, excludedMarketplaces) => (
    [item.platform_group, item.main_channel, item.platform, item.channel, item.marketplace, item.name]
        .map(value => String(value || '').trim())
        .filter(Boolean)
        .some(value => excludedMarketplaces.has(normalizeNotificationKey(value)))
);

const NOTIFICATION_READ_STORAGE_KEY = 'jtl-dashboard-notifications-read-v2';
const NOTIFICATION_PUSHED_STORAGE_KEY = 'jtl-dashboard-notifications-pushed-v1';

const getReportsApiUrl = () => {
    const configuredUrl = (process.env.REACT_APP_REPORTS_API_URL || '').trim();
    if (configuredUrl) return configuredUrl.replace(/\/$/, '');
    return (process.env.REACT_APP_LOCAL_API_URL || 'http://127.0.0.1:8090').trim().replace(/\/$/, '');
};
const REPORTS_API_URL = getReportsApiUrl();

const readNotificationReadKeys = () => {
    if (typeof window === 'undefined') return [];
    try {
        const parsed = JSON.parse(window.localStorage.getItem(NOTIFICATION_READ_STORAGE_KEY) || '[]');
        return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
    } catch {
        return [];
    }
};


const readNotificationPushedKeys = () => {
    if (typeof window === 'undefined') return [];
    try {
        const parsed = JSON.parse(window.localStorage.getItem(NOTIFICATION_PUSHED_STORAGE_KEY) || '[]');
        return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
    } catch {
        return [];
    }
};


const getBundeslandByPLZ = (plz) => {
    if (!plz) return 'Unknown';
    const p = parseInt(String(plz).substring(0, 2), 10);
    if (isNaN(p)) return 'Unknown';

    if (p >= 1 && p <= 9) return 'Sachsen';
    if (p >= 10 && p <= 14) return 'Berlin';
    if (p >= 15 && p <= 19) return 'Brandenburg';
    if (p >= 20 && p <= 29) return 'Hamburg';
    if (p >= 30 && p <= 31) return 'Niedersachsen';
    if (p >= 32 && p <= 33) return 'Nordrhein-Westfalen';
    if (p >= 34 && p <= 36) return 'Hessen';
    if (p >= 37 && p <= 38) return 'Niedersachsen';
    if (p >= 39 && p <= 39) return 'Sachsen-Anhalt';
    if (p >= 40 && p <= 48) return 'Nordrhein-Westfalen';
    if (p >= 49 && p <= 49) return 'Niedersachsen';
    if (p >= 50 && p <= 53) return 'Nordrhein-Westfalen';
    if (p >= 54 && p <= 56) return 'Rheinland-Pfalz';
    if (p >= 57 && p <= 59) return 'Nordrhein-Westfalen';
    if (p >= 60 && p <= 65) return 'Hessen';
    if (p >= 66 && p <= 67) return 'Saarland';
    if (p >= 68 && p <= 69) return 'Baden-Württemberg';
    if (p >= 70 && p <= 79) return 'Baden-Württemberg';
    if (p >= 80 && p <= 89) return 'Bayern';
    if (p >= 90 && p <= 97) return 'Bayern';
    if (p >= 98 && p <= 99) return 'Thüringen';
    return 'Unknown';
};

export const getPlatformLogoPath = (platformName) => {
    if (!platformName) return null;
    const nameLower = platformName.toLowerCase();
    if (nameLower.includes('speickshop')) return '/speickshop.png';
    if (nameLower.includes('amazon')) return '/amazon.png';
    if (nameLower.includes('ebay')) return '/eBay.png';
    return null;
};

const LOCAL_API_URL = (process.env.REACT_APP_LOCAL_API_URL || 'http://127.0.0.1:8090').trim().replace(/\/$/, '');
const CUSTOMER_RPC_TIMEOUT_MS = 8000;
const database = createLocalDatabaseClient(LOCAL_API_URL);
const dashboardRefreshStampFormatter = new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
});
const dashboardRefreshInlineFormatter = new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
});
const dashboardRefreshDateFormatter = new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
});

const formatDashboardRefreshStamp = (value) => {
    if (!value) return 'Unbekannt';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unbekannt';
    return dashboardRefreshStampFormatter.format(date);
};

const formatDashboardRefreshInlineStamp = (value) => {
    if (!value) return 'Unbekannt';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unbekannt';
    return dashboardRefreshInlineFormatter.format(date);
};

const formatDashboardRefreshDate = (value) => {
    if (!value) return null;
    const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(date.getTime())) return null;
    return dashboardRefreshDateFormatter.format(date);
};

const fetchAllLOCAL_DATABASERows = async (buildQuery, pageSize = 1000) => {
    let rows = [];
    let from = 0;

    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) {
        console.error('[LOCAL_DATABASE ERROR]', error);
        throw new Error(error.message || JSON.stringify(error));
    }

    let chunk = data || [];
    rows = rows.concat(chunk);

    if (chunk.length < pageSize) return rows;
    from += pageSize;

    const batchSize = 10;

    while (true) {
        const promises = [];
        for (let i = 0; i < batchSize; i++) {
            promises.push(buildQuery().range(from + i * pageSize, from + (i + 1) * pageSize - 1));
        }

        const results = await Promise.all(promises);
        let hitEnd = false;

        for (const res of results) {
            if (res.error) {
                console.error('[LOCAL_DATABASE PAGE ERROR]', res.error);
                throw new Error(res.error.message || JSON.stringify(res.error));
            }

            const resChunk = res.data || [];
            if (resChunk.length > 0) rows = rows.concat(resChunk);
            if (resChunk.length < pageSize) {
                hitEnd = true;
                break;
            }
        }

        if (hitEnd) break;
        from += pageSize * batchSize;
    }

    return rows;
};

const normalizeArticleSku = (value) => String(value ?? '').trim();

const mergeArticleImageOverrides = (articles = [], imageOverrides = []) => {
    if (!Array.isArray(articles) || articles.length === 0) return [];
    if (!Array.isArray(imageOverrides) || imageOverrides.length === 0) return articles;

    const imageBySku = new Map();
    imageOverrides.forEach(row => {
        const sku = normalizeArticleSku(row.sku);
        const imageUrl = String(row.image_url || '').trim();
        if (sku && imageUrl) imageBySku.set(sku, imageUrl);
    });

    if (!imageBySku.size) return articles;

    return articles.map(article => {
        const imageUrl = imageBySku.get(normalizeArticleSku(article.sku));
        return imageUrl ? { ...article, image_url: imageUrl, image_override_url: imageUrl } : article;
    });
};
const mergeArticlePurchasePriceOverrides = (articles = [], purchasePriceOverrides = []) => {
    if (!Array.isArray(articles) || articles.length === 0) return [];
    if (!Array.isArray(purchasePriceOverrides) || purchasePriceOverrides.length === 0) return articles;

    const overrideByArticleId = new Map();
    purchasePriceOverrides.forEach(row => {
        const articleId = String(row?.article_id ?? '').trim();
        const value = Number(row?.backup_purchase_price);
        if (articleId && Number.isFinite(value) && value > 0) overrideByArticleId.set(articleId, value);
    });

    if (!overrideByArticleId.size) return articles;

    return articles.map(article => {
        const override = overrideByArticleId.get(String(article?.article_id ?? '').trim());
        return Number.isFinite(override) ? { ...article, purchase_price_override: override } : article;
    });
};

const logRpcTiming = (name, elapsed, details = {}, options = {}) => {
    const context = typeof options === 'string' ? options : options?.context;
    if (process.env.NODE_ENV !== 'development' || !context) return;

    console.info(`[RPC TIMING] ${context} - ${name}`, {
        ms: elapsed,
        ...(options?.label ? { label: options.label } : {}),
        ...details
    });
};

const fetchRpcRows = async (name, params, options = {}) => {
    const startedAt = Date.now();
    const { data, error } = await database.rpc(name, params);
    const elapsed = Date.now() - startedAt;

    if (error) {
        console.error(`[RPC ERROR] ${name} after ${elapsed}ms`, error);
        throw new Error(`${name}: ${error.message || JSON.stringify(error)}`);
    }

    const rows = data || [];
    logRpcTiming(name, elapsed, { rows: rows.length }, options);
    if (process.env.NODE_ENV === 'development' && elapsed > 1000) {
        console.debug(`[RPC SLOW] ${name}`, { ms: elapsed, rows: rows.length });
    }

    return rows;
};

const withTimeout = (promise, timeoutMs, label = 'Operation') => {
    let timeoutHandle = null;

    const timeoutPromise = new Promise((_, reject) => {
        timeoutHandle = window.setTimeout(() => {
            reject(new Error(`${label} timeout after ${timeoutMs}ms`));
        }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]).finally(() => {
        if (timeoutHandle) window.clearTimeout(timeoutHandle);
    });
};

const fetchRpcSingle = async (name, params, options = {}) => {
    const startedAt = Date.now();
    const { data, error } = await database.rpc(name, params);
    const elapsed = Date.now() - startedAt;

    if (error) {
        console.error(`[RPC SINGLE ERROR] ${name} after ${elapsed}ms`, error);
        throw new Error(`${name}: ${error.message || JSON.stringify(error)}`);
    }

    const result = Array.isArray(data) ? (data[0] || null) : (data || null);
    logRpcTiming(name, elapsed, { hasResult: !!result }, options);
    if (process.env.NODE_ENV === 'development' && elapsed > 1000) {
        console.debug(`[RPC SLOW] ${name}`, { ms: elapsed, hasResult: !!result });
    }

    return result;
};

const fetchRpcRowsPaged = async (name, params, pageSize = 1000, options = {}) => {
    if (typeof pageSize === 'object' && pageSize !== null) {
        options = pageSize;
        pageSize = 1000;
    }

    let rows = [];
    let from = 0;
    let pages = 0;
    const startedAt = Date.now();

    while (true) {
        const pageStartedAt = Date.now();

        const { data, error } = await database
            .rpc(name, params)
            .range(from, from + pageSize - 1);

        pages += 1;

        const pageElapsed = Date.now() - pageStartedAt;

        if (error) {
            console.error(`[RPC PAGE ERROR] ${name} after ${pageElapsed}ms`, error);
            throw new Error(`${name}: ${error.message || JSON.stringify(error)}`);
        }

        const chunk = data || [];
        rows = rows.concat(chunk);
        if (process.env.NODE_ENV === 'development' && pageElapsed > 1000) {
            console.debug(`[RPC PAGE SLOW] ${name}`, { page: pages, rowsInPage: chunk.length, ms: pageElapsed });
        }

        if (chunk.length < pageSize) break;
        from += pageSize;
    }

    const elapsed = Date.now() - startedAt;
    logRpcTiming(name, elapsed, { pages, rows: rows.length }, options);
    if (process.env.NODE_ENV === 'development' && elapsed > 1000) {
        console.debug(`[RPC PAGED SLOW] ${name}`, { ms: elapsed, pages, totalRows: rows.length });
    }

    return rows;
};

// Utility: Calculate days between dates to determine expected query time
const getDateRangeDays = (startDate, endDate) => {
    const start = parseISODateOnly(startDate);
    const end = parseISODateOnly(endDate);
    if (!start || !end) return 0;
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
};

// Get appropriate timeout based on date range size
const getTimeoutForPeriod = (startDate, endDate) => {
    const days = getDateRangeDays(startDate, endDate);
    // Scale timeout: base 8s + 1ms per day
    // 30 days: 8s, 180 days: 11s, 365 days: 15s, 730 days: 23s
    const timeoutMs = Math.min(Math.max(8000, 8000 + (days * 10)), 60000);
    return timeoutMs;
};

// Enhanced fetch with timeout for large periods
const fetchRpcRowsWithTimeout = async (name, params, startDate, endDate, options = {}) => {
    const timeoutMs = getTimeoutForPeriod(startDate, endDate);
    const startedAt = Date.now();
    let timeoutHandle = null;
    
    try {
        const rpcPromise = database.rpc(name, params);
        
        // Create timeout promise
        const timeoutPromise = new Promise((_, reject) => {
            timeoutHandle = setTimeout(() => {
                reject(new Error(`RPC timeout after ${timeoutMs}ms for large date range`));
            }, timeoutMs);
        });
        
        const { data, error } = await Promise.race([
            rpcPromise,
            timeoutPromise
        ]);
        
        if (timeoutHandle) clearTimeout(timeoutHandle);
        
        const elapsed = Date.now() - startedAt;
        
        if (error) {
            console.error(`[RPC ERROR] ${name} after ${elapsed}ms`, error);
            throw new Error(`${name}: ${error.message || JSON.stringify(error)}`);
        }
        
        const rows = data || [];
        logRpcTiming(name, elapsed, { rows: rows.length, timeoutMs }, options);
        if (elapsed > Math.max(timeoutMs * 0.7, 1000)) {
            console.warn(`[RPC APPROACHING TIMEOUT] ${name}`, { ms: elapsed, timeoutMs, percentOfTimeout: ((elapsed / timeoutMs) * 100).toFixed(1) + '%' });
        }
        
        return rows;
    } catch (err) {
        if (timeoutHandle) clearTimeout(timeoutHandle);
        const elapsed = Date.now() - startedAt;
        console.error(`[RPC FETCH ERROR] ${name} after ${elapsed}ms`, err);
        throw err;
    }
};

const parseISODateOnly = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    const text = String(value).slice(0, 10);
    const [year, month, day] = text.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
};

const toDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const toDayStartIso = (value) => {
    const date = parseISODateOnly(value);
    if (!date) return null;
    return toDateKey(date);
};

const toNextDayStartIso = (value) => {
    const date = parseISODateOnly(value);
    if (!date) return null;
    date.setDate(date.getDate() + 1);
    return toDateKey(date);
};

const ARTICLE_SELECT = 'article_id,name,sku,category,product_group,stock_level,purchase_price,average_purchase_price,net_price,image_url';
const ARTICLE_PURCHASE_PRICE_OVERRIDE_SELECT = 'article_id,backup_purchase_price';
const LAGERBESTAND_SELECT = 'kArtikel,fBestand';
const ARTICLE_COMPONENT_SELECT = 'parent_article_id,parent_sku,parent_name,component_article_id,component_sku,component_name,component_quantity,component_stock,calculated_bundle_stock,jtl_bundle_stock,article_type';
const SUPPLIER_ORDER_POSITION_SELECT = 'kLieferantenBestellungPos,kLieferantenBestellung,kArtikel,cArtNr,cLieferantenArtNr,cName,cLieferantenBezeichnung,fMenge,fEKNetto,cNameLieferant,nLiefertage,dLieferdatum,is_open';
const ARTICLE_PURCHASE_PRICE_HISTORY_SELECT = 'article_id,source,source_id,event_date,price,quantity,supplier_name';

const RANGE_CACHE_PREFIX = 'dashboard_range_v20:';
const RANGE_CACHE_FAMILY_PREFIX = 'dashboard_range_v';
const DASHBOARD_REFRESH_REVISION_PREFIX = 'dashboard_refresh_revision:';
const RANGE_CACHE_TTL_MS = Math.min(
    30 * 60 * 1000,
    Math.max(60 * 1000, Number(process.env.REACT_APP_RANGE_CACHE_TTL_MS || 5 * 60 * 1000))
);
const MS_PER_DAY = 86400000;

const CUSTOMER_SEGMENT_FILTER_OPTIONS = [
    { value: 'all', label: 'Alle Segmente', hint: 'Alle Kunden' },
    { value: 'VIP', label: 'VIP', hint: '5+ Bestellungen' },
    { value: 'Loyal', label: 'Loyal', hint: '3-4 Bestellungen' },
    { value: 'Repeat', label: 'Repeat', hint: '2 Bestellungen' },
    { value: 'New', label: 'New', hint: '1 Bestellung' }
];

const GLOBAL_SALES_FILTER_VIEWS = new Set(['overview', 'map', 'customers', 'returns', 'comparison', 'kpiWidgets']);
const PAGE_FILTER_PANEL_VIEWS = new Set([
    'overview',
    'kpiWidgets',
    'comparison',
    'controllingReports',
    'articles',
    'customers',
    'warehouse',
    'warehouseOperations',
    'returns',
    'map',
    'costs',
    'dataQuality'
]);

const unwrapDashboardCustomerSummary = (summary) => {
    if (!summary) return null;

    if (typeof summary === 'string') {
        try {
            return JSON.parse(summary);
        } catch {
            return null;
        }
    }

    if (Array.isArray(summary)) {
        return unwrapDashboardCustomerSummary(summary[0]);
    }

    if (summary.dashboard_customers_summary) {
        return unwrapDashboardCustomerSummary(summary.dashboard_customers_summary);
    }

    return summary;
};

const WRITABLE_TENANT_ROLES = new Set(['member', 'owner']);
const normalizeTenantRole = (role) => (
    ['viewer', 'member', 'owner'].includes(role) ? role : 'member'
);
const isMissingColumnError = (error, columnName) => {
    if (!error) return false;
    const message = String(error.message || '');
    const columnPattern = columnName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?:column .*${columnPattern}.* does not exist|spalte .*${columnPattern}.* existiert nicht)`, 'i').test(message);
};
const isMissingRpcSignatureError = (error) => (
    error?.code === 'PGRST202'
    || /schema cache|could not find the function|function .* not found|no function matches/i.test(String(error?.message || ''))
);

const selectTenantsWithDemoFlag = async (client) => {
    const result = await client
        .from('tenants')
        .select('id,name,is_demo,logo_url')
        .order('name', { ascending: true });

    if (!isMissingColumnError(result.error, 'is_demo') && !isMissingColumnError(result.error, 'logo_url')) return result;

    const fallback = await client
        .from('tenants')
        .select('id,name')
        .order('name', { ascending: true });

    return {
        ...fallback,
        data: (fallback.data || []).map(tenant => ({ ...tenant, is_demo: false, logo_url: '' }))
    };
};

const selectTenantMembershipsWithRole = async (client, userId) => {
    const result = await client
        .from('tenant_users')
        .select('tenant_id,role')
        .eq('user_id', userId);

    if (!isMissingColumnError(result.error, 'role')) return result;

    const fallback = await client
        .from('tenant_users')
        .select('tenant_id')
        .eq('user_id', userId);

    return {
        ...fallback,
        data: (fallback.data || []).map(row => ({ ...row, role: 'member' }))
    };
};

const clampEndDateToToday = (endDate) => {
    const end = parseISODateOnly(endDate);
    const today = parseISODateOnly(toDateKey(new Date()));

    if (!end || !today) return endDate;
    return end > today ? toDateKey(today) : toDateKey(end);
};

const normalizeDateRange = (start, end) => {
    const startDate = parseISODateOnly(start);
    const endDate = parseISODateOnly(end);
    const startKey = startDate ? toDateKey(startDate) : String(start || '').slice(0, 10);
    const endKey = endDate ? toDateKey(endDate) : String(end || '').slice(0, 10);

    if (!startDate || !endDate) return { start: startKey, end: endKey };

    return startDate <= endDate
        ? { start: startKey, end: endKey }
        : { start: endKey, end: startKey };
};

const isFreshRangeCacheEntry = (entry, now = Date.now()) => {
    const cachedAt = Number(entry?.cachedAt || 0);
    return cachedAt > 0 && now - cachedAt <= RANGE_CACHE_TTL_MS;
};

const getFreshMemoryRangeCache = (cache, cacheKey) => {
    const entry = cache.get(cacheKey);
    if (isFreshRangeCacheEntry(entry)) return entry;
    if (entry) cache.delete(cacheKey);
    return null;
};

const readSessionRangeCache = (cacheKey) => {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    try {
        const storageKey = `${RANGE_CACHE_PREFIX}${cacheKey}`;
        const cached = window.sessionStorage.getItem(storageKey);
        const parsed = cached ? JSON.parse(cached) : null;
        if (isFreshRangeCacheEntry(parsed)) return parsed;
        if (cached) window.sessionStorage.removeItem(storageKey);
        return null;
    } catch {
        return null;
    }
};

const writeSessionRangeCache = (cacheKey, payload) => {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
        window.sessionStorage.setItem(`${RANGE_CACHE_PREFIX}${cacheKey}`, JSON.stringify(payload));
    } catch {
        // Ignore storage issues.
    }
};

const clearSessionRangeCache = () => {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
        Object.keys(window.sessionStorage)
            .filter(key => key.startsWith(RANGE_CACHE_FAMILY_PREFIX))
            .forEach(key => window.sessionStorage.removeItem(key));
    } catch {
        // Ignore storage issues.
    }
};

const normalizeSummaryFacts = (rows) => (rows || []).map(row => ({
    order_date: row.order_date ? String(row.order_date).slice(0, 10) : null,
    revenue: Number(row.revenue || 0),
    profit: Number(row.profit || 0),
    order_count: Number(row.order_count || 0),
    platform: row.platform || 'Unknown',
    platform_group: row.platform_group || 'Other',
    country: row.country || 'Unknown',
    plz: row.plz_prefix && row.plz_prefix !== 'Unknown' ? row.plz_prefix : null,
    status: Number(row.status || 0),
    status_label: row.status_label || 'Unbekannt',
    customer_group: row.customer_group || row.KundenGruppe || '',
    customer_group_id: row.customer_group_id ?? row.kKundenGruppe ?? row.kundengruppe_id ?? null,
    items: []
}));

const normalizeCustomerGroupOptions = (values = []) => (
    Array.from(new Set((Array.isArray(values) ? values : [])
        .map(value => String(value || '').trim())
        .filter(value => value && value !== 'Unknown' && value !== 'Ohne Kundengruppe')))
        .sort((a, b) => a.localeCompare(b, 'de'))
);

const getDateRangeCacheKey = (start, end, tenantId = '') => {
    const range = normalizeDateRange(start, end);
    return `${tenantId || 'no-tenant'}|${range.start}|${range.end}`;
};

const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

const addMonthsClamped = (date, months) => {
    const result = new Date(date);
    const day = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + months);
    const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(day, lastDay));
    return result;
};

const addYearsClamped = (date, years) => {
    const result = new Date(date);
    const day = result.getDate();
    result.setDate(1);
    result.setFullYear(result.getFullYear() + years);
    const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(day, lastDay));
    return result;
};

const getAnchorDate = (anchorDate) => {
    const parsed = parseISODateOnly(anchorDate);
    const anchor = parsed || parseISODateOnly(toDateKey(new Date()));
    if (anchor) anchor.setHours(0, 0, 0, 0);
    return anchor;
};

const getPresetRange = (key, anchorDate) => {
    const today = getAnchorDate(anchorDate);
    if (!today) return null;
    const todayKey = toDateKey(today);

    switch (key) {
        case 'today':
            return { start: todayKey, end: todayKey };
        case 'thisWeek': {
            const dow = (today.getDay() + 6) % 7;
            const monday = addDays(today, -dow);
            return { start: toDateKey(monday), end: todayKey };
        }
        case 'lastWeek': {
            const dow = (today.getDay() + 6) % 7;
            const monday = addDays(today, -dow - 7);
            const sunday = addDays(monday, 6);
            return { start: toDateKey(monday), end: toDateKey(sunday) };
        }
        case 'last7': {
            const start = addDays(today, -6);
            return { start: toDateKey(start), end: todayKey };
        }
        case 'thisMonth': {
            const first = new Date(today.getFullYear(), today.getMonth(), 1);
            return { start: toDateKey(first), end: todayKey };
        }
        case 'lastMonth': {
            const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const last = new Date(today.getFullYear(), today.getMonth(), 0);
            return { start: toDateKey(first), end: toDateKey(last) };
        }
        case 'thisYear': {
            const first = new Date(today.getFullYear(), 0, 1);
            return { start: toDateKey(first), end: todayKey };
        }
        default:
            return null;
    }
};

const getPresetKeyForRange = (startDate, endDate, anchorDate) => {
    const current = normalizeDateRange(startDate, endDate);
    const presetKeys = ['today', 'thisWeek', 'lastWeek', 'last7', 'thisMonth', 'lastMonth', 'thisYear'];

    return presetKeys.find((key) => {
        const presetRange = getPresetRange(key, anchorDate);
        if (!presetRange) return false;
        const normalizedPreset = normalizeDateRange(presetRange.start, presetRange.end);
        return normalizedPreset.start === current.start && normalizedPreset.end === current.end;
    }) || 'custom';
};

const applyFactFilters = (rows, {
    rangeStart,
    rangeEnd,
    drilldownPlatform,
    selectedPlatform,
    selectedCountry,
    orderStatusFilter = 'all'
}) => {
    if (!rows?.length) return [];

    return rows.filter(row => {
        if (rangeStart && row.order_date < rangeStart) return false;
        if (rangeEnd && row.order_date > rangeEnd) return false;
        if (drilldownPlatform && row.platform_group !== drilldownPlatform) return false;
        if (selectedPlatform && row.platform !== selectedPlatform) return false;
        if (selectedCountry && row.country !== selectedCountry) return false;
        if (orderStatusFilter !== 'all') {
            const status = getOrderStatusLabel(row) === 'Storniert' ? 'cancelled' : 'active';
            if (status !== orderStatusFilter) return false;
        }
        return true;
    });
};

const isPlatformGroupDrilldown = (platformName) => (
    ['amazon', 'ebay', 'shop'].includes(String(platformName || '').trim().toLowerCase())
);

const getPreviousDateRange = (startDate, endDate, anchorDate) => {
    const start = parseISODateOnly(startDate);
    const end = parseISODateOnly(endDate);

    if (!start || !end) {
        return {
            prevStart: null,
            prevEnd: null
        };
    }

    const normalizedStart = start <= end ? start : end;
    const normalizedEnd = start <= end ? end : start;
    const presetKey = getPresetKeyForRange(toDateKey(normalizedStart), toDateKey(normalizedEnd), anchorDate);

    if (presetKey === 'today') {
        const previous = addDays(normalizedStart, -1);
        return {
            prevStart: toDateKey(previous),
            prevEnd: toDateKey(previous)
        };
    }

    if (presetKey === 'thisWeek' || presetKey === 'lastWeek') {
        return {
            prevStart: toDateKey(addDays(normalizedStart, -7)),
            prevEnd: toDateKey(addDays(normalizedEnd, -7))
        };
    }

    if (presetKey === 'thisMonth') {
        return {
            prevStart: toDateKey(addMonthsClamped(normalizedStart, -1)),
            prevEnd: toDateKey(addMonthsClamped(normalizedEnd, -1))
        };
    }

    if (presetKey === 'lastMonth') {
        const previousMonthStart = new Date(normalizedStart.getFullYear(), normalizedStart.getMonth() - 1, 1);
        const previousMonthEnd = new Date(normalizedStart.getFullYear(), normalizedStart.getMonth(), 0);
        return {
            prevStart: toDateKey(previousMonthStart),
            prevEnd: toDateKey(previousMonthEnd)
        };
    }

    if (presetKey === 'thisYear') {
        return {
            prevStart: toDateKey(addYearsClamped(normalizedStart, -1)),
            prevEnd: toDateKey(addYearsClamped(normalizedEnd, -1))
        };
    }

    const diffTime = normalizedEnd.getTime() - normalizedStart.getTime();
    const prevEndObj = new Date(normalizedStart.getTime() - MS_PER_DAY);
    const prevStartObj = new Date(prevEndObj.getTime() - diffTime);

    return {
        prevStart: toDateKey(prevStartObj),
        prevEnd: toDateKey(prevEndObj)
    };
};

const getAutoChartGrouping = (startDate, endDate) => {
    const s = parseISODateOnly(startDate);
    const e = parseISODateOnly(endDate);
    if (!s || !e) return 'month';
    const diffDays = Math.ceil((e - s) / MS_PER_DAY);
    if (diffDays <= 31) return 'day';
    if (diffDays <= 180) return 'week';
    if (diffDays <= 730) return 'month';
    return 'year';
};

const resolveChartGrouping = (startDate, endDate, selectedGrouping) => (
    selectedGrouping === 'auto'
        ? getAutoChartGrouping(startDate, endDate)
        : (selectedGrouping || 'month')
);

const buildOverviewChartData = (facts, appliedStartDate, appliedEndDate, chartGrouping) => {
    const baseStart = parseISODateOnly(appliedStartDate);
    const baseEnd = parseISODateOnly(appliedEndDate);
    if (!baseStart || !baseEnd) return [];

    const start = baseStart <= baseEnd ? baseStart : baseEnd;
    const end = baseStart <= baseEnd ? baseEnd : baseStart;

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const baseStartYear = start.getFullYear();
    const baseStartMonth = start.getMonth();
    const baseTime = start.getTime();
    const map = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const ensureBucket = (offset, name, periodStart = null) => {
        if (!map[offset]) {
            map[offset] = {
                name,
                periodStart,
                sortIndex: offset,
                revenue: 0,
                profit: 0,
                orders: 0
            };
        }
        return map[offset];
    };

    const getBucketForDate = (rowDate) => {
        const rYear = rowDate.getFullYear();
        const rMonth = rowDate.getMonth();
        const dayOffset = Math.floor((rowDate.getTime() - baseTime) / MS_PER_DAY);

        if (chartGrouping === 'day') {
            return {
                offset: dayOffset,
                name: rowDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
                periodStart: toDateKey(rowDate)
            };
        }

        if (chartGrouping === 'week') {
            const weekOffset = Math.floor(dayOffset / 7);
            const weekDate = new Date(baseTime + weekOffset * 7 * MS_PER_DAY);
            return {
                offset: weekOffset,
                name: `Week of ${weekDate.getDate()}.${weekDate.getMonth() + 1}`,
                periodStart: toDateKey(weekDate)
            };
        }

        if (chartGrouping === 'year') {
            return {
                offset: rYear - baseStartYear,
                name: String(rYear),
                periodStart: `${rYear}-01-01`
            };
        }

        const monthOffset = (rYear - baseStartYear) * 12 + rMonth - baseStartMonth;
        const absTotalMonths = baseStartMonth + monthOffset;
        const outMonth = ((absTotalMonths % 12) + 12) % 12;
        const outYear = baseStartYear + Math.floor(absTotalMonths / 12);

        return {
            offset: monthOffset,
            name: `${months[outMonth]} ${outYear.toString().substring(2)}`,
            periodStart: `${outYear}-${String(outMonth + 1).padStart(2, '0')}-01`
        };
    };

    if (chartGrouping === 'day') {
        const cursor = new Date(start);
        while (cursor <= end) {
            const bucket = getBucketForDate(cursor);
            ensureBucket(bucket.offset, bucket.name, bucket.periodStart);
            cursor.setDate(cursor.getDate() + 1);
        }
    } else if (chartGrouping === 'week') {
        const maxWeekOffset = Math.floor((end.getTime() - baseTime) / MS_PER_DAY / 7);
        for (let offset = 0; offset <= maxWeekOffset; offset++) {
            const weekDate = new Date(baseTime + offset * 7 * MS_PER_DAY);
            ensureBucket(offset, `Week of ${weekDate.getDate()}.${weekDate.getMonth() + 1}`, toDateKey(weekDate));
        }
    } else if (chartGrouping === 'year') {
        for (let year = start.getFullYear(); year <= end.getFullYear(); year++) {
            ensureBucket(year - baseStartYear, String(year), `${year}-01-01`);
        }
    } else {
        const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
        const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
        while (cursor <= endMonth) {
            const bucket = getBucketForDate(cursor);
            ensureBucket(bucket.offset, bucket.name, bucket.periodStart);
            cursor.setMonth(cursor.getMonth() + 1);
        }
    }

    (facts || []).forEach(row => {
        const dateStr = String(row.order_date || '');
        if (dateStr.length < 10) return;

        const rYear = parseInt(dateStr.substring(0, 4), 10);
        const rMonth = parseInt(dateStr.substring(5, 7), 10) - 1;
        const rDay = parseInt(dateStr.substring(8, 10), 10);
        if (!Number.isFinite(rYear) || !Number.isFinite(rMonth) || !Number.isFinite(rDay)) return;

        const rowDate = new Date(rYear, rMonth, rDay);
        if (rowDate < start || rowDate > end) return;

        const bucket = getBucketForDate(rowDate);
        const target = ensureBucket(bucket.offset, bucket.name, bucket.periodStart);
        target.revenue += Number(row.revenue || 0);
        target.profit += Number(row.profit || 0);
        target.orders += Number(row.order_count || 0);
    });

    return Object.values(map).sort((a, b) => a.sortIndex - b.sortIndex);
};

const buildPlzFacts = (facts) => {
    const plzMap = {};
    facts.forEach(fact => {
        if (fact.country === 'DE' && fact.plz && fact.order_date) {
            const prefix = String(fact.plz).substring(0, 3);
            const year = parseInt(fact.order_date.substring(0, 4), 10);
            const key = `${prefix}_${fact.order_date}_${fact.status}`;
            if (!plzMap[key]) plzMap[key] = { plz_prefix: prefix, order_date: fact.order_date, year, revenue: 0, profit: 0, order_count: 0, status: fact.status };
            plzMap[key].revenue += fact.revenue;
            plzMap[key].profit += fact.profit;
            plzMap[key].order_count += Number(fact.order_count || 0);
        }
    });
    return Object.values(plzMap);
};

const readInviteTokenFromUrl = () => {
    if (typeof window === 'undefined') return '';
    try {
        const url = new URL(window.location.href);
        // Prefer the fragment form used by newly generated links. Keep reading
        // the query form so existing invitation links remain redeemable.
        const queryToken = url.searchParams.get('invite_token');
        if (queryToken) return queryToken;
        const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
        return hashParams.get('invite_token') || '';
    } catch {
        return '';
    }
};

const clearInviteTokenFromUrl = () => {
    if (typeof window === 'undefined') return;
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete('invite_token');
        const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
        hashParams.delete('invite_token');
        url.hash = hashParams.toString() ? `#${hashParams.toString()}` : '';
        window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    } catch {
        // Ignore URL cleanup failures.
    }
};

const readLandingPreviewFromUrl = () => {
    if (typeof window === 'undefined') return false;
    try {
        const url = new URL(window.location.href);
        if (readInviteTokenFromUrl()) return false;
        const pathname = url.pathname.replace(/\/+$/, '') || '/';
        const previewValue = String(url.searchParams.get('landing') || '').toLowerCase();
        return pathname === '/'
            || pathname === '/landing'
            || (pathname === '/demo' && !isAppDemoLocation(url))
            || url.hash === '#landing'
            || ['1', 'true', 'preview'].includes(previewValue);
    } catch {
        return false;
    }
};

const normalizeLegacyLandingUrl = () => {
    if (typeof window === 'undefined') return;
    try {
        const url = new URL(window.location.href);
        const pathname = url.pathname.replace(/\/+$/, '') || '/';
        const previewValue = String(url.searchParams.get('landing') || '').toLowerCase();
        const hasLegacyLandingIntent = pathname === '/landing'
            || url.hash === '#landing'
            || ['1', 'true', 'preview'].includes(previewValue);

        if (!hasLegacyLandingIntent) return;

        url.searchParams.delete('landing');
        const nextHash = url.hash === '#landing' ? '' : url.hash;
        window.history.replaceState({}, document.title, `/${url.search}${nextHash}`);
    } catch {
        // Ignore URL cleanup failures.
    }
};

const readDashboardRequestedFromUrl = () => {
    if (typeof window === 'undefined') return false;
    try {
        const url = new URL(window.location.href);
        const pathname = url.pathname.replace(/\/+$/, '') || '/';
        return pathname === '/dashboard'
            || pathname === '/login'
            || isAppDemoLocation(url);
    } catch {
        return false;
    }
};

const moveToDashboardUrl = ({ replace = false } = {}) => {
    if (typeof window === 'undefined') return;
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete('landing');
        url.searchParams.delete('demo_request');
        const nextUrl = `/dashboard${url.search}`;
        const hostname = url.hostname.toLowerCase();
        if (isMarketingHostname(hostname)) {
            const targetUrl = new URL(nextUrl, 'https://app.commetriq.de');
            window.location[replace ? 'replace' : 'assign'](targetUrl.toString());
            return;
        }
        window.history[replace ? 'replaceState' : 'pushState']({}, document.title, nextUrl);
    } catch {
        // Ignore URL update failures.
    }
};

const moveToDemoUrl = () => {
    if (typeof window === 'undefined') return;
    window.location.assign(getAppDemoUrl());
};

const moveToLoginUrl = ({ replace = false } = {}) => {
    if (typeof window === 'undefined') return false;
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete('landing');
        url.searchParams.delete('demo_request');
        const hostname = url.hostname.toLowerCase();
        if (isMarketingHostname(hostname)) {
            const targetUrl = new URL(`/login${url.search}`, 'https://app.commetriq.de');
            window.location[replace ? 'replace' : 'assign'](targetUrl.toString());
            return true;
        }
        window.history[replace ? 'replaceState' : 'pushState']({}, document.title, `/login${url.search}`);
    } catch {
        // Ignore URL update failures.
    }
    return false;
};

const moveToLandingUrl = () => {
    if (typeof window === 'undefined') return false;
    try {
        const url = new URL(window.location.href);
        if (url.hostname.toLowerCase() === 'app.commetriq.de') {
            window.location.assign('https://commetriq.de/');
            return true;
        }
        window.history.pushState({}, document.title, '/');
    } catch {
        // Ignore URL update failures.
    }
    return false;
};

const requestBrowserPasswordSave = async ({ email, password } = {}) => {
    if (typeof window === 'undefined' || !window.isSecureContext || !email || !password) return;

    const credentialStore = window.navigator?.credentials;
    if (typeof window.PasswordCredential !== 'function' || typeof credentialStore?.store !== 'function') return;

    try {
        const credential = new window.PasswordCredential({
            id: String(email),
            name: String(email),
            password: String(password)
        });
        await credentialStore.store(credential);
    } catch {
        // Password managers that do not support this optional browser API keep their normal behavior.
    }
};
function DashboardApp() {
    const savedUiState = useMemo(() => readDashboardUiState(), []);
    const initialUserPreferences = useMemo(() => readUserPreferences(), []);
    const [landingPreviewRequested, setLandingPreviewRequested] = useState(readLandingPreviewFromUrl);
    const [dashboardRequested, setDashboardRequested] = useState(readDashboardRequestedFromUrl);
    const [legalPageKey, setLegalPageKey] = useState(() => getLegalPageKey(typeof window === 'undefined' ? '/' : window.location));
    const [supportPageRequested, setSupportPageRequested] = useState(() => typeof window !== 'undefined' && window.location.pathname.replace(/\/+$/, '') === '/support');
    const [authReady, setAuthReady] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [hasTenantAccess, setHasTenantAccess] = useState(false);
    const [availableTenants, setAvailableTenants] = useState([]);
    const [activeTenantId, setActiveTenantId] = useState('');
    const [tenantAccessLoading, setTenantAccessLoading] = useState(false);
    const [accessError, setAccessError] = useState('');
    const [authMode, setAuthMode] = useState(readInviteTokenFromUrl() ? 'signUp' : 'signIn');
    const [authNotice, setAuthNotice] = useState('');
    const [inviteToken, setInviteToken] = useState(readInviteTokenFromUrl);
    const [inviteStatus, setInviteStatus] = useState(inviteToken ? 'pending' : 'idle');
    const [inviteMessage, setInviteMessage] = useState(inviteToken ? 'Einladung erkannt. Bitte registrieren oder einloggen.' : '');
    const [showAuthScreen, setShowAuthScreen] = useState(Boolean(inviteToken));
    const inviteTokenRef = useRef(inviteToken);
    const consumedInviteTokenRef = useRef('');
    const authSubmitInFlightRef = useRef(false);
    const demoRouteAutoStartRef = useRef(false);

    useEffect(() => {
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId || !currentUser?.id) {
            dashboardVisitLoggedRef.current = false;
            return;
        }
        if (dashboardVisitLoggedRef.current) return;
        dashboardVisitLoggedRef.current = true;

        const logDashboardVisit = async () => {
            try {
                const { data, error } = await database.auth.getSession();
                if (error) throw error;
                const token = data?.session?.access_token;
                if (!token) return;
                const response = await fetch(`${LOCAL_API_URL}/api/dashboard-visits`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'X-Tenant-Id': activeTenantId
                    }
                });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
            } catch (error) {
                console.warn('Dashboard-Aufruf konnte nicht erfasst werden:', error);
            }
        };

        logDashboardVisit();
    }, [authReady, isAuthenticated, hasTenantAccess, activeTenantId, currentUser?.id]);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (legalPageKey || supportPageRequested || landingPreviewRequested || isSeoTopicPath(window.location.pathname) || isBlogPath(window.location.pathname)) return;
        document.title = landingPreviewRequested || (!dashboardRequested && !showAuthScreen)
            ? 'commetriq | E-Commerce Controlling für JTL-Händler'
            : 'commetriq';
    }, [legalPageKey, supportPageRequested, landingPreviewRequested, dashboardRequested, showAuthScreen, isAuthenticated]);

    const syncRouteIntent = useCallback(() => {
        setLandingPreviewRequested(readLandingPreviewFromUrl());
        setDashboardRequested(readDashboardRequestedFromUrl());
        setLegalPageKey(getLegalPageKey(typeof window === 'undefined' ? '/' : window.location));
        setSupportPageRequested(typeof window !== 'undefined' && window.location.pathname.replace(/\/+$/, '') === '/support');
    }, []);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [mfaFlow, setMfaFlow] = useState(null);
    const [mfaCode, setMfaCode] = useState('');
    const [trustMfaDevice, setTrustMfaDevice] = useState(true);
    const [loginError, setLoginError] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [demoLoadingStep, setDemoLoadingStep] = useState(-1);
    const [deadStockData, setDeadStockData] = useState([]);
    const [rawFacts, setRawFacts] = useState([]);
    const [prevRawFacts, setPrevRawFacts] = useState([]);
    const [rawProductSummary, setRawProductSummary] = useState([]);
    const [overviewKpis, setOverviewKpis] = useState(null);
    const [overviewTimeseries, setOverviewTimeseries] = useState([]);
    const [overviewPreviousTimeseries, setOverviewPreviousTimeseries] = useState([]);
    const [overviewPreviousTimeseriesStatus, setOverviewPreviousTimeseriesStatus] = useState('idle');
    const [overviewChannels, setOverviewChannels] = useState([]);
    const [overviewPreviousChannels, setOverviewPreviousChannels] = useState([]);
    const [overviewPreviousChannelsStatus, setOverviewPreviousChannelsStatus] = useState('idle');
    const [overviewStatus, setOverviewStatus] = useState([]);
    const [overviewLoading, setOverviewLoading] = useState(false);
    const [overviewError, setOverviewError] = useState(null);
    const [productSummaryLoading, setProductSummaryLoading] = useState(false);
    const rangeFetchSeqRef = useRef(0);
    const rangeCacheRef = useRef(new Map());
    const [overviewTopProducts, setOverviewTopProducts] = useState([]);
    const [overviewTopProductsLoading, setOverviewTopProductsLoading] = useState(false);
    const [overviewTrendSelection, setOverviewTrendSelection] = useState(null);
    const [overviewTrendProducts, setOverviewTrendProducts] = useState(null);
    const [overviewTrendPlatformData, setOverviewTrendPlatformData] = useState(null);
    const [overviewTrendDetailsLoading, setOverviewTrendDetailsLoading] = useState(false);
    const overviewTrendDetailsCacheRef = useRef(new Map());
    const overviewTrendDetailsSeqRef = useRef(0);
    const [rawPlzFacts, setRawPlzFacts] = useState([]);
    const [rawArticles, setRawArticles] = useState([]);
    const activeArticlePricingById = useMemo(() => new Map((rawArticles || []).map(article => [
        String(article.article_id ?? article.id ?? ''),
        getArticlePricing(article)
    ])), [rawArticles]);
    useEffect(() => {
        overviewTrendDetailsCacheRef.current.clear();
    }, [activeArticlePricingById]);
    const [rawPositions, setRawPositions] = useState([]);
    const [rawRetouren, setRawRetouren] = useState([]);
    const [rawRetourePositions, setRawRetourePositions] = useState([]);
    const [rawGrundRetoure, setRawGrundRetoure] = useState([]);
    const [rawAmazonRetouren, setRawAmazonRetouren] = useState([]);
    const [rawLagerbestand, setRawLagerbestand] = useState([]);
    const [rawArticleComponents, setRawArticleComponents] = useState([]);
    const [rawSupplierOrderPositions, setRawSupplierOrderPositions] = useState([]);
    const [rawPurchasePriceHistory, setRawPurchasePriceHistory] = useState([]);
    const [rawCustomers, setRawCustomers] = useState([]);
    const [customerAcquisition, setCustomerAcquisition] = useState([]);
    const [customerAcquisitionLoading, setCustomerAcquisitionLoading] = useState(false);
    const [catalogDataLoaded, setCatalogDataLoaded] = useState(false);
    const [catalogDataLoading, setCatalogDataLoading] = useState(false);
    const catalogFetchInFlightRef = useRef(false);
    const catalogLoadedForUserRef = useRef('');
    const [customersDataLoaded, setCustomersDataLoaded] = useState(false);
    const [customersDataLoading, setCustomersDataLoading] = useState(false);
    const [customersError, setCustomersError] = useState(null);
    const [customersTableError, setCustomersTableError] = useState(null);
    const [customersReloadSeq, setCustomersReloadSeq] = useState(0);
    const [customerSummary, setCustomerSummary] = useState(null);
    const [customerSummaryLoading, setCustomerSummaryLoading] = useState(false);
    const [customerSegmentFacetSummary, setCustomerSegmentFacetSummary] = useState(null);
    const [customerOriginFacetSummary, setCustomerOriginFacetSummary] = useState(null);
    const [customerFacetLoading, setCustomerFacetLoading] = useState(false);
    const [customerTotalCount, setCustomerTotalCount] = useState(0);
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [debouncedCustomerSearchTerm, setDebouncedCustomerSearchTerm] = useState('');
    const [customerPage, setCustomerPage] = useState(1);
    const [customerVisibleLimit, setCustomerVisibleLimit] = useState(10);
    const customersRequestKeyRef = useRef('');
    const customersSummaryKeyRef = useRef('');
    const customersFacetKeyRef = useRef('');
    const authAccessUserRef = useRef('');
    const authReadyRef = useRef(false);
    const [customerSegmentFilter, setCustomerSegmentFilter] = useState(savedUiState?.customerSegmentFilter || null);
    const [customerOriginFilter, setCustomerOriginFilter] = useState(savedUiState?.customerOriginFilter || null);
    const [customerGroupFilter, setCustomerGroupFilter] = useState(savedUiState?.customerGroupFilter || null);
    const [customerGroupMasterOptions, setCustomerGroupMasterOptions] = useState([]);
    const [customerGroupKnownOptions, setCustomerGroupKnownOptions] = useState([]);
    const [loadingFacts, setLoadingFacts] = useState(false);

    const applyArticleImageUrlUpdates = useCallback((updates = []) => {
        const imageBySku = new Map();

        (Array.isArray(updates) ? updates : []).forEach(update => {
            const sku = normalizeArticleSku(update.sku);
            const imageUrl = String(update.image_url || update.imageUrl || '').trim();
            if (sku && imageUrl) imageBySku.set(sku, imageUrl);
        });

        if (!imageBySku.size) return;

        setRawArticles(prev => prev.map(article => {
            const imageUrl = imageBySku.get(normalizeArticleSku(article.sku));
            return imageUrl ? { ...article, image_url: imageUrl, image_override_url: imageUrl } : article;
        }));
    }, []);
    const applyArticlePurchasePriceOverrideUpdates = useCallback((updates = []) => {
        const overrideByArticleId = new Map();
        (Array.isArray(updates) ? updates : []).forEach(update => {
            const articleId = String(update?.article_id ?? '').trim();
            const value = Number(update?.backup_purchase_price);
            if (articleId) overrideByArticleId.set(articleId, Number.isFinite(value) && value > 0 ? value : null);
        });
        if (!overrideByArticleId.size) return;
        setRawArticles(prev => prev.map(article => {
            const articleId = String(article?.article_id ?? '').trim();
            if (!overrideByArticleId.has(articleId)) return article;
            const override = overrideByArticleId.get(articleId);
            if (override === null) {
                const { purchase_price_override, ...withoutOverride } = article;
                return withoutOverride;
            }
            return { ...article, purchase_price_override: override };
        }));
    }, []);
    const [hasLoadedFacts, setHasLoadedFacts] = useState(false);
    const [returnsDataLoaded, setReturnsDataLoaded] = useState(false);
    const [returnsOverviewSummary, setReturnsOverviewSummary] = useState(null);
    const [overviewReturnsLoading, setOverviewReturnsLoading] = useState(false);
    const initialCurrentView = savedUiState?.currentView === 'tracking'
        ? 'warehouseOperations'
        : savedUiState?.currentView === 'amazonInsights'
            ? 'overview'
            : (savedUiState?.currentView || 'overview');
    const [currentView, setCurrentView] = useState(initialCurrentView);
    const [countryNames] = useState({});
    const [warehouseOperationsMode, setWarehouseOperationsMode] = useState(savedUiState?.warehouseOperationsMode || 'operative');
    const [rawArticlePerformance, setRawArticlePerformance] = useState([]);
    const [articlePerformanceLoading, setArticlePerformanceLoading] = useState(false);
    const [mapGermanyStates, setMapGermanyStates] = useState([]);
    const [mapGermanyStatesLoading, setMapGermanyStatesLoading] = useState(false);
    const [mapEuropeCountries, setMapEuropeCountries] = useState([]);
    const [mapEuropeCountriesLoading, setMapEuropeCountriesLoading] = useState(false);
    const [mapScope, setMapScope] = useState(savedUiState?.mapScope || 'europe');
    const [activeBundesland, setActiveBundesland] = useState(savedUiState?.activeBundesland || null);
    const [mapPlzDetail, setMapPlzDetail] = useState([]);
    const [mapPlzDetailLoading, setMapPlzDetailLoading] = useState(false);
    const toLocalISO = useMemo(() => {
        return (d) => {
            const tz = d.getTimezoneOffset() * 60000;
            return new Date(d.getTime() - tz).toISOString().split('T')[0];
        };
    }, []);

    const initialStartDate = useMemo(() => {
        const d = new Date();
        return toLocalISO(new Date(d.getFullYear(), d.getMonth(), 1));
    }, [toLocalISO]);

    const initialEndDate = useMemo(() => toLocalISO(new Date()), [toLocalISO]);
    const [startDate, setStartDate] = useState(savedUiState?.startDate || initialStartDate);
    const [endDate, setEndDate] = useState(savedUiState?.endDate || initialEndDate);
    const [appliedStartDate, setAppliedStartDate] = useState(savedUiState?.appliedStartDate || savedUiState?.startDate || initialStartDate);
    const [appliedEndDate, setAppliedEndDate] = useState(savedUiState?.appliedEndDate || savedUiState?.endDate || initialEndDate);
    const activeRangeKeyRef = useRef(getDateRangeCacheKey(initialStartDate, initialEndDate, savedUiState?.activeTenantId || ''));
    const [dateRangeReady, setDateRangeReady] = useState(false);
    const [defaultDateRange, setDefaultDateRange] = useState({ start: initialStartDate, end: initialEndDate });
    const [dataAnchorDate, setDataAnchorDate] = useState(initialEndDate);
    const [datePreset, setDatePreset] = useState(savedUiState?.datePreset || 'thisMonth');
    const [selectedPlatform, setSelectedPlatform] = useState(savedUiState?.selectedPlatform || null);
    const [drilldownPlatform, setDrilldownPlatform] = useState(savedUiState?.drilldownPlatform || null);
    const [selectedCountry, setSelectedCountry] = useState(savedUiState?.selectedCountry || null);
    const [orderStatusFilter, setOrderStatusFilter] = useState(savedUiState?.orderStatusFilter || 'all');
    const [chartGrouping, setChartGrouping] = useState('auto');
    const [userPreferences, setUserPreferences] = useState(initialUserPreferences);
    const [isDarkMode, setIsDarkMode] = useState(initialUserPreferences.theme !== 'light');
    const [currentPage, setCurrentPage] = useState(1);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const articlePerformanceCacheRef = useRef(new Map());
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [pageFilterCount, setPageFilterCount] = useState(0);
    const [pageFilterLabels, setPageFilterLabels] = useState([]);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [notificationTimeTick, setNotificationTimeTick] = useState(0);
    const [readNotificationKeys, setReadNotificationKeys] = useState(readNotificationReadKeys);
    const [notificationLifecycleRegistry, setNotificationLifecycleRegistry] = useState(readNotificationLifecycle);
    const [notificationStateHydratedScope, setNotificationStateHydratedScope] = useState('');
    const notificationStatePersistQueueRef = useRef(Promise.resolve());
    const [notificationFocus, setNotificationFocus] = useState(null);
    const [profileSettingsSection, setProfileSettingsSection] = useState('profile');
    const [integrationReturn, setIntegrationReturn] = useState(null);
    const [pushedNotificationKeys, setPushedNotificationKeys] = useState(readNotificationPushedKeys);
    const [warningSettings, setWarningSettings] = useState(readWarningSettings);
    const [dashboardRefreshInfo, setDashboardRefreshInfo] = useState(null);
    const [dashboardRefreshLoading, setDashboardRefreshLoading] = useState(false);
    const [dataRefreshRevision, setDataRefreshRevision] = useState('');
    const filterPanelRef = useRef(null);
    const pageFilterResetRef = useRef(null);
    const userMenuRef = useRef(null);
    const notificationsRef = useRef(null);
    const dashboardVisitLoggedRef = useRef(false);
    const handlePageFilterStateChange = useCallback((state = {}) => {
        const nextCount = Number(state.count || 0);
        const nextLabels = Array.isArray(state.labels) ? state.labels.filter(Boolean).map(String) : [];
        setPageFilterCount(Number.isFinite(nextCount) && nextCount > 0 ? nextCount : 0);
        setPageFilterLabels(current => (
            current.length === nextLabels.length && current.every((label, index) => label === nextLabels[index])
                ? current
                : nextLabels
        ));
        pageFilterResetRef.current = typeof state.reset === 'function' ? state.reset : null;
    }, []);
    useEffect(() => {
        setPageFilterCount(0);
        setPageFilterLabels([]);
        pageFilterResetRef.current = null;
    }, [currentView]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const url = new URL(window.location.href);
        const integration = String(url.searchParams.get('integration') || '').trim();
        const status = String(url.searchParams.get('status') || '').trim();
        const billingReturn = String(url.searchParams.get('billing') || '').trim();
        if (billingReturn) {
            setCurrentView('profile');
            setProfileSettingsSection('billing');
            url.searchParams.delete('billing');
            window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
            return;
        }
        if (!integration || !status) return;

        if (integration === 'google_analytics') setIntegrationReturn({ provider: integration, status });
        setCurrentView('profile');
        setProfileSettingsSection('integrations');
        url.searchParams.delete('integration');
        url.searchParams.delete('status');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }, []);

    const activeTenant = useMemo(() => (
        availableTenants.find(tenant => tenant.id === activeTenantId) || null
    ), [availableTenants, activeTenantId]);
    const activeTenantName = activeTenant?.name || 'Shop';
    const activeTenantLogoUrl = activeTenant?.logo_url || '';
    const activeTenantRole = normalizeTenantRole(activeTenant?.role);
    const isActiveDemoTenant = Boolean(activeTenant?.is_demo);
    const canWriteActiveTenant = !isActiveDemoTenant && WRITABLE_TENANT_ROLES.has(activeTenantRole);
    const hasResolvedTenant = !hasTenantAccess || Boolean(activeTenantId);
    const updateUserPreferences = useCallback((updates) => {
        setUserPreferences(prev => {
            const next = writeUserPreferences({ ...prev, ...updates }, currentUser?.id || '');
            if (Object.prototype.hasOwnProperty.call(updates, 'theme')) {
                setIsDarkMode(next.theme !== 'light');
            }
            return next;
        });
    }, [currentUser?.id]);
    const handleThemeChange = useCallback((theme) => {
        updateUserPreferences({ theme: theme === 'light' ? 'light' : 'dark' });
    }, [updateUserPreferences]);
    const handleThemeToggle = useCallback(() => {
        handleThemeChange(isDarkMode ? 'light' : 'dark');
    }, [handleThemeChange, isDarkMode]);

    const itemsPerPage = 10;
    const customerPageSize = customerVisibleLimit;

    useEffect(() => {
        if (!currentUser?.id) return;
        const next = readUserPreferences(currentUser.id);
        setUserPreferences(next);
        setIsDarkMode(next.theme !== 'light');
    }, [currentUser?.id]);

    useEffect(() => {
        if (!isFilterPanelOpen || PAGE_FILTER_PANEL_VIEWS.has(currentView) || GLOBAL_SALES_FILTER_VIEWS.has(currentView)) return;
        const handleClick = (event) => {
            if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) {
                setIsFilterPanelOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isFilterPanelOpen, currentView]);

    useEffect(() => {
        setIsFilterPanelOpen(false);
    }, [warehouseOperationsMode]);

    useEffect(() => {
        if (!isUserMenuOpen) return;
        const handleClick = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isUserMenuOpen]);

    useEffect(() => {
        if (!isNotificationsOpen) return;
        const handleClick = (event) => {
            if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
                setIsNotificationsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isNotificationsOpen]);

    useEffect(() => {
        let cancelled = false;

        const refreshWarningSettings = () => setWarningSettings(readWarningSettings());
        const refreshFromServer = async () => {
            if (!currentUser?.id || !activeTenantId) {
                refreshWarningSettings();
                return;
            }

            try {
                const { data, error } = await database.auth.getSession();
                if (error) throw error;
                const token = data?.session?.access_token;
                if (!token) throw new Error('No active session');

                const response = await fetch(`${REPORTS_API_URL}/api/warning-alert-settings`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'X-Tenant-Id': activeTenantId
                    }
                });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(payload.error || 'Warnregeln konnten nicht geladen werden.');

                const nextSettings = writeWarningSettings(payload.settings || DEFAULT_WARNING_SETTINGS);
                if (!cancelled) setWarningSettings(nextSettings);
            } catch (err) {
                console.warn('Server warning settings unavailable, using local fallback:', err);
                if (!cancelled) refreshWarningSettings();
            }
        };

        const handleWarningSettingsChanged = (event) => {
            setWarningSettings(event.detail || readWarningSettings());
        };

        refreshFromServer();
        window.addEventListener('focus', refreshFromServer);
        window.addEventListener('storage', refreshWarningSettings);
        window.addEventListener(WARNING_SETTINGS_UPDATED_EVENT, handleWarningSettingsChanged);
        return () => {
            cancelled = true;
            window.removeEventListener('focus', refreshFromServer);
            window.removeEventListener('storage', refreshWarningSettings);
            window.removeEventListener(WARNING_SETTINGS_UPDATED_EVENT, handleWarningSettingsChanged);
        };
    }, [activeTenantId, currentUser?.id, currentView, database]);

    useEffect(() => {
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) {
            setDashboardRefreshInfo(null);
            setDashboardRefreshLoading(false);
            setDataRefreshRevision('');
            return undefined;
        }

        let cancelled = false;
        let initialLoad = true;
        const revisionStorageKey = DASHBOARD_REFRESH_REVISION_PREFIX + activeTenantId;

        const loadRefreshInfo = async () => {
            if (initialLoad) setDashboardRefreshLoading(true);
            try {
                const info = await fetchRpcSingle('dashboard_last_refresh_for_tenant', {
                    p_tenant_id: activeTenantId
                }, { context: 'Dashboard header' });
                if (cancelled) return;

                const nextRevision = String(info?.last_refresh_at || '');
                let previousRevision = '';
                try {
                    previousRevision = window.sessionStorage.getItem(revisionStorageKey) || '';
                } catch {
                    previousRevision = '';
                }

                if (nextRevision && previousRevision && nextRevision !== previousRevision) {
                    rangeCacheRef.current.clear();
                    articlePerformanceCacheRef.current.clear();
                    clearSessionRangeCache();
                    customersRequestKeyRef.current = '';
                    customersSummaryKeyRef.current = '';
                    customersFacetKeyRef.current = '';
                    setCatalogDataLoaded(false);
                    setCustomersDataLoaded(false);
                    setReturnsDataLoaded(false);
                    setDataRefreshRevision(nextRevision);
                }
                if (nextRevision) {
                    try {
                        window.sessionStorage.setItem(revisionStorageKey, nextRevision);
                    } catch {
                        // Cache revision persistence is optional.
                    }
                }
                setDashboardRefreshInfo(info || null);
            } catch (err) {
                console.warn('Dashboard refresh stamp could not be loaded:', err);
                if (!cancelled && initialLoad) setDashboardRefreshInfo(null);
            } finally {
                if (!cancelled && initialLoad) setDashboardRefreshLoading(false);
                initialLoad = false;
            }
        };

        loadRefreshInfo();
        const intervalId = window.setInterval(loadRefreshInfo, 60 * 1000);
        window.addEventListener('focus', loadRefreshInfo);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            window.removeEventListener('focus', loadRefreshInfo);
        };
    }, [activeTenantId, authReady, isAuthenticated, hasTenantAccess]);

    const resetCustomersRequestState = useCallback(() => {
        customersRequestKeyRef.current = '';
        customersSummaryKeyRef.current = '';
        customersFacetKeyRef.current = '';
        setCustomersError(null);
        setCustomersTableError(null);
    }, []);

    const retryCustomersLoad = useCallback(() => {
        resetCustomersRequestState();
        setCustomersDataLoaded(false);
        setCustomerPage(1);
        setCustomerVisibleLimit(10);
        setCustomersReloadSeq(seq => seq + 1);
    }, [resetCustomersRequestState]);

    useEffect(() => {
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) {
            setCustomerGroupMasterOptions([]);
            setCustomerGroupKnownOptions([]);
            return;
        }

        let cancelled = false;

        const loadCustomerGroups = async () => {
            const { data, error } = await database
                .from('kunden_gruppe')
                .select('cName')
                .eq('tenant_id', activeTenantId)
                .order('cName', { ascending: true });

            if (!cancelled) {
                setCustomerGroupMasterOptions(error
                    ? []
                    : Array.from(new Set((data || [])
                        .map(row => String(row.cName || '').trim())
                        .filter(Boolean)))
                );
            }
        };

        loadCustomerGroups();

        return () => {
            cancelled = true;
        };
    }, [authReady, isAuthenticated, hasTenantAccess, activeTenantId]);

    useEffect(() => {
        const nextOptions = normalizeCustomerGroupOptions([
            ...customerGroupMasterOptions,
            ...[customerSummary, customerOriginFacetSummary, customerSegmentFacetSummary]
                .map(unwrapDashboardCustomerSummary)
                .flatMap(summary => Array.isArray(summary?.customer_groups) ? summary.customer_groups : [])
                .map(group => group?.name),
            ...[...rawFacts, ...prevRawFacts].map(row => row.customer_group),
            ...rawCustomers.map(customer => customer?.KundenGruppe || customer?.customer_group),
            customerGroupFilter
        ]);

        if (!nextOptions.length) return;

        setCustomerGroupKnownOptions(previousOptions => {
            const mergedOptions = normalizeCustomerGroupOptions([
                ...previousOptions,
                ...nextOptions
            ]);
            return mergedOptions.join('|') === previousOptions.join('|')
                ? previousOptions
                : mergedOptions;
        });
    }, [
        customerGroupMasterOptions,
        customerSummary,
        customerOriginFacetSummary,
        customerSegmentFacetSummary,
        rawFacts,
        prevRawFacts,
        rawCustomers,
        customerGroupFilter
    ]);

    const resetTenantScopedState = useCallback(() => {
        setRawFacts([]);
        setPrevRawFacts([]);
        setRawProductSummary([]);
        setOverviewKpis(null);
        setOverviewTimeseries([]);
        setOverviewChannels([]);
        setOverviewPreviousChannels([]);
        setOverviewPreviousChannelsStatus('idle');
        setOverviewStatus([]);
        setOverviewTopProducts([]);
        setOverviewError(null);
        setOverviewLoading(false);
        setOverviewTopProductsLoading(false);
        setProductSummaryLoading(false);
        setRawPlzFacts([]);
        setRawArticles([]);
        setRawPositions([]);
        setRawRetouren([]);
        setRawRetourePositions([]);
        setRawGrundRetoure([]);
        setRawAmazonRetouren([]);
        setRawLagerbestand([]);
        setRawArticleComponents([]);
        setRawSupplierOrderPositions([]);
        setRawPurchasePriceHistory([]);
        setRawCustomers([]);
        setCustomerAcquisition([]);
        setCustomerSummary(null);
        setCustomerSummaryLoading(false);
        setCustomerSegmentFacetSummary(null);
        setCustomerOriginFacetSummary(null);
        setCustomerFacetLoading(false);
        setCustomerTotalCount(0);
        setCustomerGroupKnownOptions([]);
        setCustomerAcquisitionLoading(false);
        setCatalogDataLoaded(false);
        catalogLoadedForUserRef.current = '';
        setCustomersDataLoaded(false);
        setCustomersDataLoading(false);
        setReturnsDataLoaded(false);
        setReturnsOverviewSummary(null);
        setOverviewReturnsLoading(false);
        setRawArticlePerformance([]);
        setMapGermanyStates([]);
        setMapEuropeCountries([]);
        setMapPlzDetail([]);
        setHasLoadedFacts(false);
        setLoadingFacts(false);
        setDateRangeReady(false);
        rangeCacheRef.current.clear();
        articlePerformanceCacheRef.current.clear();
        clearSessionRangeCache();
        resetCustomersRequestState();
    }, [resetCustomersRequestState]);

    useEffect(() => {
        inviteTokenRef.current = inviteToken;
    }, [inviteToken]);

    useEffect(() => {
        // Keep the captured token in state, but remove it from the address bar
        // immediately so legacy query links cannot leak it via Referer headers.
        if (inviteToken) clearInviteTokenFromUrl();
    }, [inviteToken]);

    useEffect(() => {
        normalizeLegacyLandingUrl();
        syncRouteIntent();
        window.addEventListener('popstate', syncRouteIntent);
        return () => window.removeEventListener('popstate', syncRouteIntent);
    }, [syncRouteIntent]);

    useEffect(() => {
        if (!authReady || !isAuthenticated || typeof window === 'undefined') return;
        const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
        if (pathname !== '/login') return;

        moveToDashboardUrl({ replace: true });
        setLandingPreviewRequested(false);
        setDashboardRequested(true);
    }, [authReady, isAuthenticated]);

    const loadUserAccess = useCallback(async (user, token = inviteTokenRef.current) => {
        if (!user) {
            setIsAdmin(false);
            setHasTenantAccess(false);
            setAvailableTenants([]);
            setActiveTenantId('');
            setTenantAccessLoading(false);
            setAccessError('');
            return;
        }

        setAccessError('');
        setAuthNotice('');
        setTenantAccessLoading(true);

        const effectiveInviteToken = token && token !== consumedInviteTokenRef.current ? token : '';

        if (effectiveInviteToken) {
            setInviteStatus('accepting');
            setInviteMessage('Einladung wird angenommen...');

            try {
                const { data: sessionData, error: sessionError } = await database.auth.getSession();
                if (sessionError) throw sessionError;
                const accessToken = sessionData?.session?.access_token;
                if (!accessToken) throw new Error('Bitte neu einloggen, um die Einladung anzunehmen.');

                const response = await fetch(`${REPORTS_API_URL}/api/invitations/accept-existing`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({ inviteToken: effectiveInviteToken })
                });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(payload.error || 'Einladung konnte nicht angenommen werden.');

                const tenantName = payload.tenantName;
                setInviteStatus('accepted');
                setInviteMessage(tenantName ? `Einladung für ${tenantName} angenommen.` : 'Einladung angenommen.');
                consumedInviteTokenRef.current = effectiveInviteToken;
                inviteTokenRef.current = '';
                setInviteToken('');
                clearInviteTokenFromUrl();
            } catch (err) {
                console.error('Invitation acceptance failed:', err);
                if (/bereits verwendet|already/i.test(String(err.message || ''))) {
                    setInviteStatus('accepted');
                    setInviteMessage('Einladung wurde bereits angenommen.');
                    consumedInviteTokenRef.current = effectiveInviteToken;
                    inviteTokenRef.current = '';
                    setInviteToken('');
                    clearInviteTokenFromUrl();
                } else {
                    setInviteStatus('error');
                    setInviteMessage(err.message || 'Einladung konnte nicht angenommen werden.');
                }
            }
        }

        const [
            { data: roleData },
            { data: tenantsData, error: tenantsError },
            { data: membershipsData, error: membershipsError }
        ] = await Promise.all([
            database
                .from('user_roles')
                .select('is_super_admin')
                .eq('user_id', user.id)
                .maybeSingle(),
            selectTenantsWithDemoFlag(database),
            selectTenantMembershipsWithRole(database, user.id)
        ]);

        if (tenantsError) {
            console.error('Tenant access check failed:', tenantsError);
            setTenantAccessLoading(false);
            throw tenantsError;
        }
        if (membershipsError) {
            console.error('Tenant role check failed:', membershipsError);
            setTenantAccessLoading(false);
            throw membershipsError;
        }

        const nextIsAdmin = !!roleData?.is_super_admin;
        const roleByTenantId = new Map((membershipsData || [])
            .filter(row => row?.tenant_id)
            .map(row => [row.tenant_id, normalizeTenantRole(row.role)]));
        const nextTenants = (tenantsData || [])
            .filter(tenant => tenant?.id)
            .filter(tenant => nextIsAdmin || roleByTenantId.has(tenant.id))
            .map(tenant => ({
                id: tenant.id,
                name: tenant.name || tenant.id,
                is_demo: Boolean(tenant.is_demo),
                logo_url: tenant.logo_url || '',
                role: nextIsAdmin ? 'owner' : normalizeTenantRole(roleByTenantId.get(tenant.id))
            }));
        const nextHasTenantAccess = nextTenants.length > 0;

        setIsAdmin(nextIsAdmin);
        setAvailableTenants(nextTenants);
        setHasTenantAccess(nextHasTenantAccess);
        setAccessError(nextHasTenantAccess ? '' : 'Kein Zugriff: Dieses Konto wurde noch keinem Shop zugeordnet.');
        setActiveTenantId(previousTenantId => {
            if (!nextTenants.length) return '';

            const storedTenantId = readStoredTenantId(user.id);
            const preferredTenantId = previousTenantId || storedTenantId || savedUiState?.activeTenantId || '';
            const nextTenantId = nextTenants.some(tenant => tenant.id === preferredTenantId)
                ? preferredTenantId
                : nextTenants[0].id;

            writeStoredTenantId(user.id, nextTenantId);
            return nextTenantId;
        });
        setTenantAccessLoading(false);
    }, [savedUiState?.activeTenantId]);

    const handleAuth = async (e) => {
        e.preventDefault();
        if (authSubmitInFlightRef.current) return;
        authSubmitInFlightRef.current = true;
        setIsLoggingIn(true);
        setLoginError('');
        setAuthNotice('');
        let credentials = null;
        try {
            credentials = {
                email: email.trim().toLowerCase(),
                password
            };

            if (authMode === 'signUp') {
                const tokenForSignup = inviteTokenRef.current || inviteToken;
                if (!tokenForSignup) {
                    throw new Error('Konten können nur über einen gültigen Einladungslink erstellt werden.');
                }

                const response = await fetch(`${REPORTS_API_URL}/api/invitations/accept`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: credentials.email,
                        password: credentials.password,
                        inviteToken: tokenForSignup
                    })
                });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(payload.error || 'Einladung konnte nicht angenommen werden.');
                }

                consumedInviteTokenRef.current = tokenForSignup;
                inviteTokenRef.current = '';
                setInviteToken('');
                clearInviteTokenFromUrl();
                moveToLoginUrl({ replace: true });
                setLandingPreviewRequested(false);
                setDashboardRequested(true);
                setShowAuthScreen(true);
                setAuthMode('signIn');
                setMfaFlow(null);
                setMfaCode('');
                setPassword('');
                setInviteStatus('accepted');
                setInviteMessage(payload.tenantName ? `Einladung für ${payload.tenantName} angenommen.` : 'Einladung angenommen.');
                setAuthNotice('Konto erstellt. Bitte melde dich jetzt mit deiner E-Mail und deinem Passwort an.');
                return;
            }

            const signInResult = await database.auth.signInWithPassword(credentials);
            const { error } = signInResult;

            if (error) {
                console.error('[LOCAL_DATABASE ERROR]', error);
                throw new Error(error.message || JSON.stringify(error));
            }
            await requestBrowserPasswordSave(credentials);
            setPassword('');
            if (signInResult.data?.mfa) {
                setMfaFlow(signInResult.data.mfa);
                setMfaCode('');
                setPassword('');
            }
        } catch (err) {
            console.error('Auth Error:', err);
            const message = err.message || 'Invalid email or password.';
            const canRecoverConsumedInvite = authMode === 'signUp'
                && credentials?.email
                && /bereits verwendet|already|nicht gefunden|not found/i.test(message);

            if (canRecoverConsumedInvite) {
                consumedInviteTokenRef.current = inviteTokenRef.current || inviteToken;
                inviteTokenRef.current = '';
                setInviteToken('');
                clearInviteTokenFromUrl();
                moveToLoginUrl({ replace: true });
                setLandingPreviewRequested(false);
                setDashboardRequested(true);
                setShowAuthScreen(true);
                setAuthMode('signIn');
                setMfaFlow(null);
                setMfaCode('');
                setPassword('');
                setInviteStatus('accepted');
                setInviteMessage('Einladung angenommen.');
                setAuthNotice('Falls dein Konto bereits erstellt wurde, melde dich jetzt mit E-Mail und Passwort an.');
                setLoginError('');
                return;
            }

            setLoginError(message);
        } finally {
            authSubmitInFlightRef.current = false;
            setIsLoggingIn(false);
        }
    };

    const handleMfaVerify = async (event) => {
        event.preventDefault();
        if (authSubmitInFlightRef.current || !mfaFlow?.challengeToken) return;
        const code = mfaCode.replace(/\D/g, '');
        if (!/^\d{6}$/.test(code)) {
            setLoginError('Bitte gib den sechsstelligen Code aus deiner E-Mail ein.');
            return;
        }

        authSubmitInFlightRef.current = true;
        setIsLoggingIn(true);
        setLoginError('');
        try {
            const { error } = await database.auth.mfa.verify({
                challengeToken: mfaFlow.challengeToken,
                code,
                trustDevice: trustMfaDevice
            });
            if (error) throw error;
            setMfaFlow(null);
            setMfaCode('');
            setPassword('');
            setAuthNotice(trustMfaDevice ? 'E-Mail-Code bestätigt. Dieser Browser wird 30 Tage erkannt.' : 'E-Mail-Code erfolgreich bestätigt.');
        } catch (error) {
            setLoginError(error.message || 'Der E-Mail-Code konnte nicht bestätigt werden.');
        } finally {
            authSubmitInFlightRef.current = false;
            setIsLoggingIn(false);
        }
    };

    const handleMfaCancel = () => {
        setMfaFlow(null);
        setMfaCode('');
        setTrustMfaDevice(true);
        setPassword('');
        setLoginError('');
        setAuthNotice('');
    };

    const handleDemoOpen = useCallback(async () => {
        if (authSubmitInFlightRef.current) return;
        if (typeof window !== 'undefined' && isMarketingHostname(window.location.hostname)) {
            moveToDemoUrl();
            return;
        }

        authSubmitInFlightRef.current = true;
        setIsLoggingIn(true);
        setLoginError('');
        setAuthNotice('');
        setDemoLoadingStep(0);

        try {
            const demoSessionPromise = database.auth.signInWithDemo();
            const loadingSequencePromise = new Promise((resolve) => {
                window.setTimeout(() => setDemoLoadingStep(1), 1000);
                window.setTimeout(() => setDemoLoadingStep(2), 2000);
                window.setTimeout(resolve, 3000);
            });
            const [demoResult] = await Promise.all([
                demoSessionPromise.then(
                    (result) => ({ result }),
                    (requestError) => ({ requestError })
                ),
                loadingSequencePromise
            ]);
            if (demoResult.requestError) throw demoResult.requestError;

            const { error } = demoResult.result;
            if (error) throw new Error(error.message || 'Demo-Session konnte nicht gestartet werden.');

            if (!isAppDemoLocation(window.location)) moveToDashboardUrl();
            setLandingPreviewRequested(false);
            setDashboardRequested(true);
            setShowAuthScreen(false);
            setCurrentView('overview');
        } catch (error) {
            console.error('Demo auth error:', error);
            setLoginError(error.message || 'Demo-Session konnte nicht gestartet werden.');
            setDashboardRequested(true);
            setShowAuthScreen(true);
        } finally {
            setDemoLoadingStep(-1);
            authSubmitInFlightRef.current = false;
            setIsLoggingIn(false);
        }
    }, []);

    useEffect(() => {
        if (!authReady || isAuthenticated || demoRouteAutoStartRef.current) return;
        if (typeof window === 'undefined' || !isAppDemoLocation(window.location)) return;

        demoRouteAutoStartRef.current = true;
        handleDemoOpen();
    }, [authReady, handleDemoOpen, isAuthenticated]);

    const handleBackToLanding = useCallback(() => {
        if (moveToLandingUrl()) return;
        setLandingPreviewRequested(true);
        setDashboardRequested(false);
        setShowAuthScreen(false);
        setLoginError('');
        setAuthNotice('');
        setMfaFlow(null);
        setMfaCode('');
    }, []);

    const handleSignOut = async () => {
        setIsUserMenuOpen(false);
        setMfaFlow(null);
        setMfaCode('');
        setPassword('');
        await database.auth.signOut();
    };

    const handleActiveTenantChange = useCallback((tenantId) => {
        if (!tenantId || tenantId === activeTenantId) return;

        setActiveTenantId(tenantId);
        if (currentUser?.id) writeStoredTenantId(currentUser.id, tenantId);
        resetTenantScopedState();
        setCurrentPage(1);
        setCustomerPage(1);
    }, [activeTenantId, currentUser?.id, resetTenantScopedState]);

    const handleTenantLogoUpdated = useCallback((tenantId, logoUrl) => {
        if (!tenantId) return;
        setAvailableTenants(previousTenants => previousTenants.map(tenant => (
            tenant.id === tenantId ? { ...tenant, logo_url: logoUrl || '' } : tenant
        )));
    }, []);

    const handleTenantNameUpdated = useCallback((tenantId, name) => {
        if (!tenantId || !name) return;
        setAvailableTenants(previousTenants => previousTenants.map(tenant => (
            tenant.id === tenantId ? { ...tenant, name } : tenant
        )));
    }, []);

    const handleViewChange = useCallback((nextView) => {
        if (!nextView || nextView === currentView) return;
        // Do not let a slow Profit/RPC request keep consuming the local API
        // while the user is already navigating to another page.
        database.cancelPendingRequests?.();
        setIsFilterPanelOpen(false);
        setIsNotificationsOpen(false);
        setIsUserMenuOpen(false);
        setCurrentView(nextView);
    }, [currentView]);

    useEffect(() => {
        writeDashboardUiState({
            activeTenantId,
            currentView,
            startDate,
            endDate,
            appliedStartDate,
            appliedEndDate,
            selectedPlatform,
            drilldownPlatform,
            selectedCountry,
            orderStatusFilter,
            customerSegmentFilter,
            customerOriginFilter,
            customerGroupFilter,
            warehouseOperationsMode,
            datePreset,
            chartGrouping,
            mapScope,
            activeBundesland
        });
    }, [
        activeTenantId,
        currentView,
        startDate,
        endDate,
        appliedStartDate,
        appliedEndDate,
        selectedPlatform,
        drilldownPlatform,
        selectedCountry,
        orderStatusFilter,
        customerSegmentFilter,
        customerOriginFilter,
        customerGroupFilter,
        warehouseOperationsMode,
        datePreset,
        chartGrouping,
        mapScope,
        activeBundesland
    ]);

    useEffect(() => {
        authReadyRef.current = authReady;
    }, [authReady]);


    useEffect(() => {
        let mounted = true;

        const applySession = async (session, event = '') => {
            if (!mounted) return;

            const userId = session?.user?.id || '';
            const isSameAuthenticatedUser = userId && authAccessUserRef.current === userId;

            if (event === 'TOKEN_REFRESHED' && isSameAuthenticatedUser) {
                setIsAuthenticated(true);
                return;
            }

            if (event === 'SIGNED_IN' && isSameAuthenticatedUser && authReadyRef.current) {
                setIsAuthenticated(true);
                return;
            }

            setAuthReady(false);
            setIsAuthenticated(!!session);

            if (!session?.user) {
                authAccessUserRef.current = '';
                setCurrentUser(null);
                setIsAdmin(false);
                setHasTenantAccess(false);
                setAvailableTenants([]);
                setActiveTenantId('');
                setTenantAccessLoading(false);
                setAccessError('');
                resetTenantScopedState();
                if (mounted) setAuthReady(true);
                return;
            }

            try {
                if (authAccessUserRef.current && authAccessUserRef.current !== session.user.id) {
                    resetTenantScopedState();
                }
                setCurrentUser(session.user);
                await loadUserAccess(session.user);
                authAccessUserRef.current = session.user.id;
            } catch (err) {
                console.error('Auth access check failed:', err);
                authAccessUserRef.current = '';
                if (mounted) {
                    setIsAdmin(false);
                    setHasTenantAccess(false);
                    setAvailableTenants([]);
                    setActiveTenantId('');
                    setTenantAccessLoading(false);
                    setAccessError(err.message || 'Zugriff konnte nicht geprüft werden.');
                }
            } finally {
                if (mounted) setAuthReady(true);
            }
        };

        database.auth.getSession().then(({ data: { session } }) => {
            applySession(session, 'INITIAL_SESSION');
        });

        const { data: { subscription } } = database.auth.onAuthStateChange((event, session) => {
            applySession(session, event);
        });

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, [loadUserAccess, resetTenantScopedState]);

    useEffect(() => {
        if (!authReady || !isAuthenticated) return;
        if (currentView === 'admin' && !isAdmin) {
            setCurrentView('overview');
        }
        if (currentView === 'tracking') {
            setCurrentView('warehouseOperations');
        }
        if (currentView === 'amazonInsights') {
            setCurrentView('overview');
        }
        if (!hasTenantAccess && currentView !== 'access_denied') {
            setCurrentView('access_denied');
        }
        if (hasTenantAccess && currentView === 'access_denied') {
            setCurrentView('overview');
        }
    }, [authReady, isAuthenticated, hasTenantAccess, isAdmin, currentView]);

    useEffect(() => {
        if (currentView !== 'comparison') return;
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) return;
        if (!appliedStartDate || !appliedEndDate) return;

        let cancelled = false;

        const loadDeadStock = async () => {
            const startBoundary = toDayStartIso(appliedStartDate);
            const endBoundary = toNextDayStartIso(appliedEndDate);

            if (!startBoundary || !endBoundary) return;

            try {
                const rows = await fetchRpcRows('dashboard_dead_stock', {
                    p_tenant_id: activeTenantId,
                    p_start_date: startBoundary,
                    p_end_date: endBoundary,
                    p_min_days_without_sale: 60,
                    p_limit: 10
                });

                if (!cancelled) {
                    setDeadStockData(rows || []);
                }
            } catch (err) {
                console.error('Failed to load dead stock:', err);
                if (!cancelled) setDeadStockData([]);
            }
        };

        loadDeadStock();

        return () => {
            cancelled = true;
        };
    }, [
        currentView,
        authReady,
        isAuthenticated,
        hasTenantAccess,
        activeTenantId,
        appliedStartDate,
        appliedEndDate
    ]);

    useEffect(() => {
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) return;

        let cancelled = false;
        setDateRangeReady(false);

        const loadLatestOrderMonth = async () => {
            try {
                const { data, error } = await database
                    .from('verkauf_auftragsverwaltung')
                    .select('dErstellt')
                    .eq('tenant_id', activeTenantId)
                    .not('dErstellt', 'is', null)
                    .order('dErstellt', { ascending: false })
                    .limit(1)
                    .maybeSingle();

                if (error || !data?.dErstellt || cancelled) return;

                const latestDate = String(data.dErstellt).slice(0, 10);
                const latest = new Date(`${latestDate}T00:00:00`);
                const latestRange = {
                    start: toLocalISO(new Date(latest.getFullYear(), latest.getMonth(), 1)),
                    end: latestDate
                };

                

                setDefaultDateRange(latestRange);
                setDataAnchorDate(latestDate);
                const normalizedLatestRange = normalizeDateRange(latestRange.start, latestRange.end);
                const savedPreset = savedUiState?.datePreset;
                const refreshedPresetRange = savedPreset && savedPreset !== "custom"
                    ? getPresetRange(savedPreset, latestDate)
                    : null;

                if (refreshedPresetRange) {
                    const normalizedPresetRange = normalizeDateRange(refreshedPresetRange.start, refreshedPresetRange.end);
                    setStartDate(normalizedPresetRange.start);
                    setEndDate(normalizedPresetRange.end);
                    setDatePreset(savedPreset);
                } else if (!savedUiState?.startDate || !savedUiState?.endDate) {
                    setStartDate(normalizedLatestRange.start);
                    setEndDate(normalizedLatestRange.end);
                    setDatePreset("thisMonth");
                }

            
            } catch (err) {
                console.warn('Could not detect latest LOCAL_DATABASE order date:', err);
            } finally {
                if (!cancelled) setDateRangeReady(true);
            }
        };

        loadLatestOrderMonth();

        return () => {
            cancelled = true;
        };
    }, [toLocalISO, authReady, isAuthenticated, hasTenantAccess, activeTenantId]);


    useEffect(() => {
        const needsCatalogData =
            currentView === 'overview' ||
            currentView === 'kpiWidgets' ||
            currentView === 'articles' ||
            currentView === 'warehouse';
        if (!needsCatalogData) return;
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) return;
        const catalogUserKey = `${authAccessUserRef.current || 'authenticated'}|${activeTenantId}`;
        if (catalogDataLoaded && catalogLoadedForUserRef.current === catalogUserKey) return;
        if (catalogFetchInFlightRef.current) return;

        const fetchCatalogData = async () => {
            catalogFetchInFlightRef.current = true;
            setCatalogDataLoading(true);

            try {
                const articlesList = await fetchAllLOCAL_DATABASERows(() =>
                    database.from('articles').select(ARTICLE_SELECT).eq('tenant_id', activeTenantId)
                );
                const [articleImageOverrides, articlePurchasePriceOverrides] = await Promise.all([
                    fetchAllLOCAL_DATABASERows(() =>
                        database.from('article_image_overrides').select('sku,image_url').eq('tenant_id', activeTenantId)
                    ).catch(err => {
                        console.warn('Artikelbild-Overrides konnten nicht geladen werden:', err);
                        return [];
                    }),
                    fetchAllLOCAL_DATABASERows(() =>
                        database.from('article_purchase_price_overrides').select(ARTICLE_PURCHASE_PRICE_OVERRIDE_SELECT).eq('tenant_id', activeTenantId)
                    ).catch(err => {
                        console.warn('EK-Korrekturen konnten nicht geladen werden:', err);
                        return [];
                    })
                ]);

                setRawArticles(mergeArticlePurchasePriceOverrides(
                    mergeArticleImageOverrides(articlesList || [], articleImageOverrides),
                    articlePurchasePriceOverrides
                ));
                setCatalogDataLoaded(true);
                catalogLoadedForUserRef.current = catalogUserKey;

                fetchAllLOCAL_DATABASERows(() =>
                    database.from('lagerbestand').select(LAGERBESTAND_SELECT).eq('tenant_id', activeTenantId)
                )
                    .then(lagerbestandList => {
                        setRawLagerbestand(lagerbestandList || []);
                    })
                    .catch(lagerErr => {
                        console.error('Lagerbestand konnte nicht geladen werden:', lagerErr);
                        setRawLagerbestand([]);
                    });

                fetchAllLOCAL_DATABASERows(() =>
                    database.from('article_components').select(ARTICLE_COMPONENT_SELECT).eq('tenant_id', activeTenantId)
                )
                    .then(articleComponentsList => {
                        setRawArticleComponents(articleComponentsList || []);
                    })
                    .catch(componentsErr => {
                        console.warn('Stücklisten konnten nicht geladen werden:', componentsErr);
                        setRawArticleComponents([]);
                    });

                fetchAllLOCAL_DATABASERows(() =>
                    database.from('supplier_order_positions').select(SUPPLIER_ORDER_POSITION_SELECT).eq('tenant_id', activeTenantId)
                )
                    .then(supplierOrderPositionsList => {
                        setRawSupplierOrderPositions(supplierOrderPositionsList || []);
                    })
                    .catch(supplierErr => {
                        console.warn('Lieferantenbestellungen konnten nicht geladen werden:', supplierErr);
                        setRawSupplierOrderPositions([]);
                    });

                fetchAllLOCAL_DATABASERows(() =>
                    database
                        .from('article_purchase_price_history')
                        .select(ARTICLE_PURCHASE_PRICE_HISTORY_SELECT)
                        .eq('tenant_id', activeTenantId)
                        .order('event_date', { ascending: false })
                )
                    .then(purchasePriceHistoryList => {
                        setRawPurchasePriceHistory(purchasePriceHistoryList || []);
                    })
                    .catch(priceHistoryErr => {
                        console.warn('EK-Historie konnte nicht geladen werden:', priceHistoryErr);
                        setRawPurchasePriceHistory([]);
                    });
            } catch (err) {
                console.error('Artikel konnten nicht geladen werden:', err);

                setRawArticles([]);
                setRawLagerbestand([]);
                setRawArticleComponents([]);
                setRawSupplierOrderPositions([]);
                setRawPurchasePriceHistory([]);
                setCatalogDataLoaded(true);
                catalogLoadedForUserRef.current = catalogUserKey;
            } finally {
                catalogFetchInFlightRef.current = false;
                setCatalogDataLoading(false);
            }
        };

        fetchCatalogData();
    }, [currentView, catalogDataLoaded, authReady, isAuthenticated, hasTenantAccess, activeTenantId]);

    useEffect(() => {
        const needsArticlePerformance =
            currentView === 'articles' ||
            currentView === 'warehouse';

        if (!needsArticlePerformance) return;
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) return;
        if (!appliedStartDate || !appliedEndDate) return;

        let cancelled = false;

        const fetchArticlePerformance = async () => {
            const startBoundary = toDayStartIso(appliedStartDate);
            const endBoundary = toNextDayStartIso(appliedEndDate);
            const previousRange = getPreviousDateRange(appliedStartDate, appliedEndDate, dataAnchorDate);
            const previousStartBoundary = toDayStartIso(previousRange.prevStart);
            const previousEndBoundary = toNextDayStartIso(previousRange.prevEnd);
            const performanceUserKey = `${authAccessUserRef.current || 'authenticated'}|${activeTenantId}`;
            const cacheKey = `articles-performance-v4|${performanceUserKey}|${startBoundary}|${endBoundary}|${previousStartBoundary}|${previousEndBoundary}`;

            const cached = articlePerformanceCacheRef.current.get(cacheKey);
            if (cached && isFreshRangeCacheEntry(cached)) {
                setRawArticlePerformance(cached.data || []);
                setArticlePerformanceLoading(false);
                return;
            }
            if (cached) articlePerformanceCacheRef.current.delete(cacheKey);

            setRawArticlePerformance([]);
            setArticlePerformanceLoading(true);

            try {
                const currentRows = await fetchRpcRows('dashboard_articles_performance_daily', {
                    p_tenant_id: activeTenantId,
                    p_start_date: startBoundary,
                    p_end_date: endBoundary
                });
                if (cancelled) return;

                const currentRowsWithoutComparison = (currentRows || []).map(row => ({
                    ...row,
                    previous_qty: 0,
                    previous_revenue: 0,
                    previous_profit: 0,
                    has_previous_performance: false
                }));
                setRawArticlePerformance(currentRowsWithoutComparison);
                setArticlePerformanceLoading(false);

                let previousRows = [];
                try {
                    previousRows = await fetchRpcRows('dashboard_articles_performance_daily', {
                        p_tenant_id: activeTenantId,
                        p_start_date: previousStartBoundary,
                        p_end_date: previousEndBoundary
                    });
                } catch (previousError) {
                    console.warn('Vorperiodenwerte fuer Artikel konnten nicht geladen werden:', previousError);
                }
                const previousByArticle = new Map(
                    (previousRows || []).map(row => [String(row.article_id), row])
                );
                const currentArticleIds = new Set(
                    (currentRows || []).map(row => String(row.article_id))
                );
                const rows = (currentRows || []).map(row => {
                    const previous = previousByArticle.get(String(row.article_id));
                    return {
                        ...row,
                        previous_qty: Number(previous?.total_qty ?? previous?.qty ?? 0),
                        previous_revenue: Number(previous?.total_revenue ?? previous?.revenue ?? 0),
                        previous_profit: Number(previous?.total_profit ?? previous?.profit ?? previous?.gross_profit ?? 0),
                        has_previous_performance: Boolean(previous)
                    };
                });
                (previousRows || []).forEach(previous => {
                    if (currentArticleIds.has(String(previous.article_id))) return;
                    rows.push({
                        article_id: previous.article_id,
                        total_qty: 0,
                        total_revenue: 0,
                        total_profit: 0,
                        trend: [],
                        previous_qty: Number(previous.total_qty ?? previous.qty ?? 0),
                        previous_revenue: Number(previous.total_revenue ?? previous.revenue ?? 0),
                        previous_profit: Number(previous.total_profit ?? previous.profit ?? previous.gross_profit ?? 0),
                        has_previous_performance: true
                    });
                });

                if (!cancelled) {
                    articlePerformanceCacheRef.current.set(cacheKey, { data: rows || [], cachedAt: Date.now() });
                    setRawArticlePerformance(rows || []);
                }
            } catch (err) {
                console.error('Failed to load article performance:', err);
                if (!cancelled) setRawArticlePerformance([]);
            } finally {
                if (!cancelled) setArticlePerformanceLoading(false);
            }
        };

        fetchArticlePerformance();

        return () => {
            cancelled = true;
        };
    }, [currentView, appliedStartDate, appliedEndDate, dataAnchorDate, authReady, isAuthenticated, hasTenantAccess, activeTenantId]);

    
    

    
    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedCustomerSearchTerm(customerSearchTerm.trim());
        }, 300);

        return () => window.clearTimeout(timer);
    }, [customerSearchTerm]);

    useEffect(() => {
        setCustomerPage(1);
        setCustomerVisibleLimit(10);
        resetCustomersRequestState();
    }, [
        appliedStartDate,
        appliedEndDate,
        debouncedCustomerSearchTerm,
        selectedPlatform,
        drilldownPlatform,
        selectedCountry,
        activeTenantId,
        customerSegmentFilter,
        customerOriginFilter,
        customerGroupFilter,
        resetCustomersRequestState
    ]);

    useEffect(() => {
        if (currentView !== 'map') return;
        if (mapScope !== 'germany' && mapScope !== 'detail') return;
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) return;
        if (!startDate || !endDate) return;

        let cancelled = false;

        const loadGermanyStates = async () => {
            const normalizedRange = normalizeDateRange(startDate, endDate);
            const startBoundary = toDayStartIso(normalizedRange.start);
            const endBoundary = toNextDayStartIso(normalizedRange.end);

            if (!startBoundary || !endBoundary) return;

            setMapGermanyStatesLoading(true);

            try {
                const rows = await fetchRpcRows('dashboard_map_germany_states', {
                    p_tenant_id: activeTenantId,
                    p_start_date: startBoundary,
                    p_end_date: endBoundary,
                    p_platform_group: drilldownPlatform || null,
                    p_platform: selectedPlatform || null,
                    p_order_status: orderStatusFilter
                });

                if (cancelled) return;

                setMapGermanyStates(rows || []);
            } catch (err) {
                console.error('Failed to load Germany map states:', err);
                if (!cancelled) setMapGermanyStates([]);
            } finally {
                if (!cancelled) setMapGermanyStatesLoading(false);
            }
        };

        loadGermanyStates();

        return () => {
            cancelled = true;
        };
    }, [
        currentView,
        mapScope,
        authReady,
        isAuthenticated,
        hasTenantAccess,
        activeTenantId,
        startDate,
        endDate,
        drilldownPlatform,
        selectedPlatform,
        orderStatusFilter
    ]);

    useEffect(() => {
        if (currentView !== 'map') return;
        if (mapScope !== 'europe') return;
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) return;
        if (!startDate || !endDate) return;

        let cancelled = false;

        const loadEuropeCountries = async () => {
            const normalizedRange = normalizeDateRange(startDate, endDate);
            const startBoundary = toDayStartIso(normalizedRange.start);
            const endBoundary = toNextDayStartIso(normalizedRange.end);

            if (!startBoundary || !endBoundary) return;

            setMapEuropeCountriesLoading(true);

            try {
                const rows = await fetchRpcRows('dashboard_map_europe_countries', {
                    p_tenant_id: activeTenantId,
                    p_start_date: startBoundary,
                    p_end_date: endBoundary,
                    p_platform_group: drilldownPlatform || null,
                    p_platform: selectedPlatform || null,
                    p_order_status: orderStatusFilter
                });

                if (!cancelled) {
                    setMapEuropeCountries(rows || []);
                }
            } catch (err) {
                console.error('Failed to load Europe map countries:', err);
                if (!cancelled) setMapEuropeCountries([]);
            } finally {
                if (!cancelled) setMapEuropeCountriesLoading(false);
            }
        };

        loadEuropeCountries();

        return () => {
            cancelled = true;
        };
    }, [
        currentView,
        mapScope,
        authReady,
        isAuthenticated,
        hasTenantAccess,
        activeTenantId,
        startDate,
        endDate,
        drilldownPlatform,
        selectedPlatform,
        orderStatusFilter
    ]);


    useEffect(() => {
        if (currentView !== 'map') return;
        if (mapScope !== 'detail') return;
        if (!activeBundesland) return;
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) return;
        if (!startDate || !endDate) return;

        let cancelled = false;

        const loadPlzDetail = async () => {
            const normalizedRange = normalizeDateRange(startDate, endDate);
            const startBoundary = toDayStartIso(normalizedRange.start);
            const endBoundary = toNextDayStartIso(normalizedRange.end);

            if (!startBoundary || !endBoundary) return;

            setMapPlzDetailLoading(true);

            try {
                const rows = await fetchRpcRows('dashboard_map_plz_detail', {
                    p_tenant_id: activeTenantId,
                    p_start_date: startBoundary,
                    p_end_date: endBoundary,
                    p_bundesland: activeBundesland,
                    p_platform_group: drilldownPlatform || null,
                    p_platform: selectedPlatform || null,
                    p_order_status: orderStatusFilter
                });

                if (cancelled) return;

                setMapPlzDetail(rows || []);
            } catch (err) {
                console.error('Failed to load PLZ detail map:', err);
                if (!cancelled) setMapPlzDetail([]);
            } finally {
                if (!cancelled) setMapPlzDetailLoading(false);
            }
        };

        loadPlzDetail();

        return () => {
            cancelled = true;
        };
    }, [
        currentView,
        mapScope,
        activeBundesland,
        authReady,
        isAuthenticated,
        hasTenantAccess,
        activeTenantId,
        startDate,
        endDate,
        drilldownPlatform,
        selectedPlatform,
        orderStatusFilter
    ]);

    useEffect(() => {
        const needsCustomerSummary = currentView === 'customers';
        if (!needsCustomerSummary) return;
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) {
            customersRequestKeyRef.current = '';
            setCustomersDataLoading(false);
            return;
        }
        if (!appliedStartDate || !appliedEndDate) return;

        const rangeStart = String(appliedStartDate).slice(0, 10);
        const rangeEndExclusive = toNextDayStartIso(appliedEndDate);
        const topLimit = Math.min(Math.max(Number(customerVisibleLimit || 10), 10), 20);

        const requestKey = [
            activeTenantId,
            rangeStart,
            rangeEndExclusive,
            debouncedCustomerSearchTerm || '',
            topLimit,
            customerSegmentFilter || '',
            customerOriginFilter || '',
            customerGroupFilter || '',
            selectedPlatform || '',
            drilldownPlatform || '',
            selectedCountry || '',
            orderStatusFilter,
            customersReloadSeq
        ].join('|');

        if (customersRequestKeyRef.current === requestKey) return;

        let cancelled = false;

        const fetchCustomersPage = async () => {
            customersRequestKeyRef.current = requestKey;
            setCustomersTableError(null);
            setCustomersDataLoading(true);

            const fetchKundeSichtFallback = async () => {
                const selectColumns = 'kKunde,Anzeigename,cKundenNr,cLand,cPLZ,AnzahlBestellungen,Umsatz,AvgWarenkorb,cHerkunft,KundeSeit,LetzteBestellung,KundenGruppe';
                const searchValue = String(debouncedCustomerSearchTerm || '').trim().replace(/[(),]/g, ' ');
                let query = database
                    .from('kunde_sicht')
                    .select(selectColumns)
                    .eq('tenant_id', activeTenantId)
                    .order('Umsatz', { ascending: false, nullsFirst: false })
                    .limit(Math.max(topLimit * 3, 30));

                if (selectedCountry) query = query.eq('cLand', selectedCountry);
                if (customerOriginFilter) query = query.eq('cHerkunft', customerOriginFilter);
                if (customerGroupFilter) query = query.eq('KundenGruppe', customerGroupFilter);
                if (searchValue) {
                    query = query.or(`Anzeigename.ilike.%${searchValue}%,cKundenNr.ilike.%${searchValue}%`);
                }

                let { data, error } = await query;

                if (error && searchValue) {
                    let retryQuery = database
                        .from('kunde_sicht')
                        .select(selectColumns)
                        .eq('tenant_id', activeTenantId)
                        .order('Umsatz', { ascending: false, nullsFirst: false })
                        .limit(Math.max(topLimit * 3, 30));
                    if (selectedCountry) retryQuery = retryQuery.eq('cLand', selectedCountry);
                    if (customerOriginFilter) retryQuery = retryQuery.eq('cHerkunft', customerOriginFilter);
                    if (customerGroupFilter) retryQuery = retryQuery.eq('KundenGruppe', customerGroupFilter);
                    const retry = await retryQuery;
                    data = retry.data;
                    error = retry.error;
                }

                if (error) throw error;

                const segmentFiltered = (data || []).filter(row => {
                    if (!customerSegmentFilter) return true;
                    const orders = Number(row.AnzahlBestellungen || 0);
                    const segment =
                        orders >= 5 ? 'VIP'
                            : orders >= 3 ? 'Loyal'
                                : orders === 2 ? 'Repeat'
                                    : 'New';
                    return segment === customerSegmentFilter;
                });

                return segmentFiltered.slice(0, topLimit);
            };

            try {
                const canUseCustomerSnapshot =
                    !debouncedCustomerSearchTerm &&
                    !customerSegmentFilter &&
                    !customerOriginFilter &&
                    !customerGroupFilter &&
                    !(selectedPlatform && drilldownPlatform);

                if (canUseCustomerSnapshot) {
                    const snapshot = await withTimeout(
                        fetchRpcSingle('dashboard_customers_snapshot_cached_v2', {
                            p_tenant_id: activeTenantId,
                            p_start_date: rangeStart,
                            p_end_date: rangeEndExclusive,
                            p_order_status: orderStatusFilter,
                            p_platform: selectedPlatform || null,
                            p_platform_group: drilldownPlatform || null,
                            p_country: selectedCountry || null
                        }),
                        2500,
                        'Kunden-Snapshot'
                    );

                    if (cancelled) return;

                    const topCustomers = Array.isArray(snapshot?.top_customers) ? snapshot.top_customers : [];
                    const snapshotCoversRequestedLimit = topCustomers.length >= topLimit;

                    if (!snapshot || !Array.isArray(snapshot.top_customers)) {
                        customersSummaryKeyRef.current = '';
                    } else if (snapshotCoversRequestedLimit || topCustomers.length > 0) {
                        const pageRows = topCustomers.slice(0, topLimit);
                        const nextSummary = {
                            kpis: snapshot?.kpis || {},
                            segments: Array.isArray(snapshot?.segments) ? snapshot.segments : [],
                            origins: Array.isArray(snapshot?.origins) ? snapshot.origins : [],
                            customer_groups: Array.isArray(snapshot?.customer_groups) ? snapshot.customer_groups : [],
                            acquisition: Array.isArray(snapshot?.acquisition) ? snapshot.acquisition : [],
                            aov_by_customer_type: Array.isArray(snapshot?.aov_by_customer_type) ? snapshot.aov_by_customer_type : []
                        };

                        setRawCustomers(pageRows);
                        setCustomerTotalCount(pageRows.length);
                        setCustomerSummary(nextSummary);
                        setCustomerAcquisition(nextSummary.acquisition);
                        setCustomersError(null);
                        setCustomerSummaryLoading(false);
                        setCustomersTableError(null);
                        setCustomersDataLoaded(true);

                        const summaryKey = [
                            activeTenantId,
                            rangeStart,
                            rangeEndExclusive,
                            '',
                            '',
                            '',
                            '',
                            selectedPlatform || '',
                            drilldownPlatform || '',
                            selectedCountry || '',
                            orderStatusFilter,
                            customersReloadSeq
                        ].join('|');
                        customersSummaryKeyRef.current = summaryKey;
                        return;
                    } else {
                        customersSummaryKeyRef.current = '';
                    }
                }

                const topCustomersParams = {
                    p_tenant_id: activeTenantId,
                    p_start_date: rangeStart,
                    p_end_date: rangeEndExclusive,
                    p_search: debouncedCustomerSearchTerm || '',
                    p_limit: topLimit,
                    p_segment: customerSegmentFilter || null,
                    p_origin: customerOriginFilter || null,
                    p_platform: selectedPlatform || null,
                    p_platform_group: drilldownPlatform || null,
                    p_country: selectedCountry || null,
                    p_order_status: orderStatusFilter
                };
                if (customerGroupFilter) {
                    topCustomersParams.p_customer_group = customerGroupFilter;
                }

                let rows;
                try {
                    rows = await withTimeout(
                        fetchRpcRows('dashboard_customers_top_light', topCustomersParams),
                        6500,
                        'Kundentabelle'
                    );
                } catch (topLightErr) {
                    const missingTopLightRpc = isMissingRpcSignatureError(topLightErr);

                    if (!missingTopLightRpc) {
                        rows = await fetchKundeSichtFallback();
                    } else {
                        const fallbackTopCustomersParams = { ...topCustomersParams };
                        if (customerGroupFilter) {
                            delete fallbackTopCustomersParams.p_customer_group;
                        }

                        try {
                            rows = await withTimeout(
                                fetchRpcRows('dashboard_customers_page_light', {
                                    ...fallbackTopCustomersParams,
                                    p_offset: 0
                                }),
                                6500,
                                'Kundentabelle'
                            );
                        } catch {
                            rows = await fetchKundeSichtFallback();
                        }

                        if (customerGroupFilter) {
                            setCustomerGroupFilter(null);
                        }
                    }
                }

                if (cancelled) return;

                setRawCustomers(rows || []);
                setCustomerTotalCount(Number(rows?.length || 0));
                setCustomersTableError(null);
                setCustomersDataLoaded(true);
            } catch (err) {
                console.error('Failed to load customers page:', err);

                if (!cancelled) {
                    try {
                        const fallbackRows = await fetchKundeSichtFallback();
                        if (cancelled) return;
                        setRawCustomers(fallbackRows || []);
                        setCustomerTotalCount(Number(fallbackRows?.length || 0));
                        setCustomersTableError(null);
                        setCustomersDataLoaded(true);
                    } catch (fallbackErr) {
                        console.error('Failed to load customers fallback:', fallbackErr);
                        setCustomersDataLoaded(true);
                        setCustomersTableError(fallbackErr.message || err.message || 'Kundentabelle konnte nicht geladen werden.');
                        customersRequestKeyRef.current = '';
                    }
                }
            } finally {
                if (!cancelled) setCustomersDataLoading(false);
            }
        };

        fetchCustomersPage();

        return () => {
            cancelled = true;
        };
    }, [
        currentView,
        authReady,
        isAuthenticated,
        hasTenantAccess,
        activeTenantId,
        appliedStartDate,
        appliedEndDate,
        debouncedCustomerSearchTerm,
        customerVisibleLimit,
        customerSegmentFilter,
        customerOriginFilter,
        customerGroupFilter,
        selectedPlatform,
        drilldownPlatform,
        selectedCountry,
        orderStatusFilter,
        customersReloadSeq
    ]);

    useEffect(() => {
        const needsCustomerSummary = currentView === 'customers' || currentView === 'overview' || currentView === 'kpiWidgets';
        if (!needsCustomerSummary) return;
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) {
            customersSummaryKeyRef.current = '';
            setCustomerSummaryLoading(false);
            return;
        }
        if (!appliedStartDate || !appliedEndDate) return;

        const rangeStart = String(appliedStartDate).slice(0, 10);
        const rangeEndExclusive = toNextDayStartIso(appliedEndDate);
        const requestKey = [
            activeTenantId,
            rangeStart,
            rangeEndExclusive,
            debouncedCustomerSearchTerm || '',
            customerSegmentFilter || '',
            customerOriginFilter || '',
            customerGroupFilter || '',
            selectedPlatform || '',
            drilldownPlatform || '',
            selectedCountry || '',
            orderStatusFilter,
            customersReloadSeq
        ].join('|');

        if (customersSummaryKeyRef.current === requestKey) return;

        let cancelled = false;

        const fetchCustomerSummary = async () => {
            customersSummaryKeyRef.current = requestKey;
            setCustomersError(null);
            setCustomerSummaryLoading(true);

            try {
                const summaryParams = {
                    p_tenant_id: activeTenantId,
                    p_start_date: rangeStart,
                    p_end_date: rangeEndExclusive,
                    p_search: debouncedCustomerSearchTerm || '',
                    p_segment: customerSegmentFilter || null,
                    p_origin: customerOriginFilter || null,
                    p_platform: selectedPlatform || null,
                    p_platform_group: drilldownPlatform || null,
                    p_country: selectedCountry || null,
                    p_order_status: orderStatusFilter
                };
                if (customerGroupFilter) {
                    summaryParams.p_customer_group = customerGroupFilter;
                }

                let data;
                try {
                    data = await fetchRpcSingle('dashboard_customers_summary_light', summaryParams);
                } catch (summaryErr) {
                    if (!customerGroupFilter || !isMissingRpcSignatureError(summaryErr)) {
                        throw summaryErr;
                    }

                    const fallbackSummaryParams = { ...summaryParams };
                    delete fallbackSummaryParams.p_customer_group;
                    data = await fetchRpcSingle('dashboard_customers_summary_light', fallbackSummaryParams);
                    setCustomerGroupFilter(null);
                }

                if (!cancelled) {
                    setCustomerSummary(data || null);
                    setCustomerAcquisition(Array.isArray(data?.acquisition) ? data.acquisition : []);
                    setCustomersError(null);
                }
            } catch (err) {
                console.error('Failed to load customer summary:', err);

                if (!cancelled) {
                    setCustomersError(err.message || 'Kundenübersicht konnte nicht geladen werden.');
                    customersSummaryKeyRef.current = '';
                }
            } finally {
                if (!cancelled) setCustomerSummaryLoading(false);
            }
        };

        fetchCustomerSummary();

        return () => {
            cancelled = true;
        };
    }, [
        currentView,
        authReady,
        isAuthenticated,
        hasTenantAccess,
        activeTenantId,
        appliedStartDate,
        appliedEndDate,
        debouncedCustomerSearchTerm,
        customerSegmentFilter,
        customerOriginFilter,
        customerGroupFilter,
        selectedPlatform,
        drilldownPlatform,
        selectedCountry,
        orderStatusFilter,
        customersReloadSeq
    ]);

    useEffect(() => {
        if (currentView !== 'customers') return;
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) {
            customersFacetKeyRef.current = '';
            setCustomerFacetLoading(false);
            return;
        }
        if (!appliedStartDate || !appliedEndDate) return;

        const needsSegmentFacet = Boolean(customerOriginFilter || customerGroupFilter);
        const needsOriginFacet = Boolean(customerSegmentFilter || customerGroupFilter);

        if (!needsSegmentFacet && !needsOriginFacet) {
            customersFacetKeyRef.current = '';
            setCustomerSegmentFacetSummary(null);
            setCustomerOriginFacetSummary(null);
            setCustomerFacetLoading(false);
            return;
        }

        const rangeStart = String(appliedStartDate).slice(0, 10);
        const rangeEndExclusive = toNextDayStartIso(appliedEndDate);
        const requestKey = [
            activeTenantId,
            rangeStart,
            rangeEndExclusive,
            debouncedCustomerSearchTerm || '',
            customerSegmentFilter || '',
            customerOriginFilter || '',
            customerGroupFilter || '',
            selectedPlatform || '',
            drilldownPlatform || '',
            selectedCountry || '',
            orderStatusFilter,
            customersReloadSeq
        ].join('|');

        if (customersFacetKeyRef.current === requestKey) return;

        let cancelled = false;

        const fetchCustomerFacets = async () => {
            customersFacetKeyRef.current = requestKey;
            setCustomerFacetLoading(true);

            const baseParams = {
                p_tenant_id: activeTenantId,
                p_start_date: rangeStart,
                p_end_date: rangeEndExclusive,
                p_search: debouncedCustomerSearchTerm || '',
                p_platform: selectedPlatform || null,
                p_platform_group: drilldownPlatform || null,
                p_country: selectedCountry || null,
                p_order_status: orderStatusFilter
            };

            try {
                const segmentFacetParams = {
                        ...baseParams,
                        p_segment: null,
                        p_origin: customerOriginFilter || null
                    };
                const originFacetParams = {
                        ...baseParams,
                        p_segment: customerSegmentFilter || null,
                        p_origin: null
                    };
                if (customerGroupFilter) {
                    segmentFacetParams.p_customer_group = customerGroupFilter;
                    originFacetParams.p_customer_group = customerGroupFilter;
                }

                const [segmentFacet, originFacet] = await Promise.all([
                    needsSegmentFacet ? fetchRpcSingle('dashboard_customers_summary_fast', segmentFacetParams) : Promise.resolve(null),
                    needsOriginFacet ? fetchRpcSingle('dashboard_customers_summary_fast', originFacetParams) : Promise.resolve(null)
                ]);

                if (!cancelled) {
                    setCustomerSegmentFacetSummary(needsSegmentFacet ? (segmentFacet || null) : null);
                    setCustomerOriginFacetSummary(needsOriginFacet ? (originFacet || null) : null);
                }
            } catch (err) {
                console.error('Failed to load customer chart facets:', err);

                if (!cancelled) {
                    if (customerGroupFilter && isMissingRpcSignatureError(err)) {
                        setCustomerGroupFilter(null);
                    }
                    customersFacetKeyRef.current = '';
                }
            } finally {
                if (!cancelled) setCustomerFacetLoading(false);
            }
        };

        const timer = window.setTimeout(() => {
            fetchCustomerFacets();
        }, 200);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [
        currentView,
        authReady,
        isAuthenticated,
        hasTenantAccess,
        activeTenantId,
        appliedStartDate,
        appliedEndDate,
        debouncedCustomerSearchTerm,
        customerSegmentFilter,
        customerOriginFilter,
        customerGroupFilter,
        selectedPlatform,
        drilldownPlatform,
        selectedCountry,
        orderStatusFilter,
        customersReloadSeq
    ]);

    useEffect(() => {
        if (currentView !== 'customers') return;
        if (!appliedStartDate || !appliedEndDate) return;
        setCustomerAcquisitionLoading(false);
    }, [
        currentView,
        appliedStartDate,
        appliedEndDate,
        debouncedCustomerSearchTerm,
        customerSegmentFilter,
        customerOriginFilter,
        customerGroupFilter,
        selectedPlatform,
        drilldownPlatform,
        selectedCountry
    ]);

    useEffect(() => {
        if (currentView !== 'returns' && currentView !== 'overview') return;
        setReturnsDataLoaded(true);
    }, [currentView]);

    useEffect(() => {
        if (currentView !== 'overview' && currentView !== 'kpiWidgets') return;
        if (!isAuthenticated || !hasTenantAccess || !activeTenantId) return;
        if (!appliedStartDate || !appliedEndDate) return;

        let cancelled = false;

        const fetchOverviewReturns = async () => {
            const startBoundary = toDayStartIso(appliedStartDate);
            const endBoundary = toNextDayStartIso(appliedEndDate);
            const granularity = resolveChartGrouping(appliedStartDate, appliedEndDate, chartGrouping);
            const baseParams = {
                p_tenant_id: activeTenantId,
                p_start_date: startBoundary,
                p_end_date: endBoundary,
                p_platform: selectedPlatform || null,
                p_platform_group: drilldownPlatform || null,
                p_country: selectedCountry || null,
                p_order_status: orderStatusFilter,
                p_reason: null,
                p_condition: null,
                p_return_platform: null,
                p_granularity: granularity
            };
            const shouldLoadChannelReturnRates = !selectedPlatform && !drilldownPlatform;
            const returnRateChannelGroups = ['Shop', 'Amazon', 'eBay', 'Other'];

            try {
                setOverviewReturnsLoading(true);
                const [data, channelSummaries] = await Promise.all([
                    fetchRpcSingle('dashboard_returns_summary_light', baseParams),
                    shouldLoadChannelReturnRates
                        ? Promise.all(returnRateChannelGroups.map(channelGroup => (
                            fetchRpcSingle('dashboard_returns_summary_light', {
                                ...baseParams,
                                p_platform_group: channelGroup
                            })
                                .then(summary => ({ channelGroup, summary }))
                                .catch(err => {
                                    console.warn(`Failed to load ${channelGroup} return alert summary:`, err);
                                    return null;
                                })
                        )))
                        : Promise.resolve([])
                ]);

                if (!cancelled) {
                    const nextSummary = data || null;

                    if (nextSummary) {
                        nextSummary.channelReturnSummaries = (channelSummaries || [])
                            .filter(Boolean)
                            .map(({ channelGroup, summary }) => ({
                                channel: channelGroup,
                                totalReturns: Number(summary?.totalReturns ?? summary?.kpis?.totalReturns ?? 0),
                                returnRate: Number(summary?.returnRate ?? summary?.kpis?.returnRate ?? 0),
                                soldOrders: Number(summary?.soldOrders ?? summary?.kpis?.soldOrders ?? 0),
                                topReason: summary?.topReason || '-'
                            }))
                            .filter(row => row.totalReturns > 0 || row.soldOrders > 0);
                    }

                    setReturnsOverviewSummary(nextSummary);
                }
            } catch (err) {
                console.error('Failed to load overview returns summary:', err);
                if (!cancelled) setReturnsOverviewSummary(null);
            } finally {
                if (!cancelled) setOverviewReturnsLoading(false);
            }
        };

        fetchOverviewReturns();

        return () => {
            cancelled = true;
        };
    }, [
        currentView,
        isAuthenticated,
        hasTenantAccess,
        activeTenantId,
        appliedStartDate,
        appliedEndDate,
        selectedPlatform,
        drilldownPlatform,
        selectedCountry,
        orderStatusFilter,
        chartGrouping
    ]);

    useEffect(() => {
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) return;

        const loadRangePayload = async (requestedStartDate, requestedEndDate, options = {}) => {
            const normalizedRange = normalizeDateRange(requestedStartDate, requestedEndDate);
            const normalizedStartDate = normalizedRange.start;
            const normalizedEndDate = normalizedRange.end;
            const cacheKey = getDateRangeCacheKey(normalizedStartDate, normalizedEndDate, activeTenantId);
            const cachedRange = getFreshMemoryRangeCache(rangeCacheRef.current, cacheKey) || readSessionRangeCache(cacheKey);
            const needsProducts = options.includeProducts !== false;
            const needsPlz = options.includePlz === true;
            const rpcTimingOptions = { context: 'Sales Analysis' };

            const canUseCachedRange =
                cachedRange &&
                cachedRange.salesAnalysisFacts === true &&
                (!needsProducts || cachedRange.productSummaryLoaded) &&
                (!needsPlz || cachedRange.plzFactsLoaded);

            if (canUseCachedRange) {
                rangeCacheRef.current.set(cacheKey, cachedRange);
                return cachedRange;
            }

            const start = parseISODateOnly(normalizedStartDate);
            const end = parseISODateOnly(normalizedEndDate);
            if (!start || !end) throw new Error('Invalid date range selected.');

            const previousRange = getPreviousDateRange(normalizedStartDate, normalizedEndDate, dataAnchorDate);
            const prevStartStr = previousRange.prevStart;
            const prevEndStr = previousRange.prevEnd;

            const startBoundary = toDayStartIso(normalizedStartDate);
            const endBoundary = toNextDayStartIso(normalizedEndDate);
            const prevStartBoundary = toDayStartIso(prevStartStr);
            const prevEndBoundary = toNextDayStartIso(prevEndStr);

            if (!startBoundary || !endBoundary || !prevStartBoundary || !prevEndBoundary) {
                throw new Error('Could not normalize date boundaries.');
            }

            const hasCachedFacts =
                Array.isArray(cachedRange?.facts) &&
                Array.isArray(cachedRange?.previousFacts) &&
                cachedRange.salesAnalysisFacts === true;
            const factsPromise = hasCachedFacts
                ? Promise.resolve({ facts: cachedRange.facts, previousFacts: cachedRange.previousFacts })
                : Promise.all([
                    fetchRpcRowsPaged('dashboard_sales_analysis_facts', {
                        p_tenant_id: activeTenantId,
                        p_start_date: String(startBoundary).slice(0, 10),
                        p_end_date: String(endBoundary).slice(0, 10),
                        p_granularity: 'day'
                    }, { ...rpcTimingOptions, label: 'current period' }),
                    fetchRpcRowsPaged('dashboard_sales_analysis_facts', {
                        p_tenant_id: activeTenantId,
                        p_start_date: String(prevStartBoundary).slice(0, 10),
                        p_end_date: String(prevEndBoundary).slice(0, 10),
                        p_granularity: 'day'
                    }, { ...rpcTimingOptions, label: 'previous period' })
                ]).then(([summaryRows, prevSummaryRows]) => ({
                    facts: normalizeSummaryFacts(summaryRows),
                    previousFacts: normalizeSummaryFacts(prevSummaryRows)
                }));

            const productSummaryPromise = !needsProducts
                ? Promise.resolve({
                    rows: cachedRange?.productSummary || [],
                    loaded: Boolean(cachedRange?.productSummaryLoaded)
                })
                : cachedRange?.productSummaryLoaded
                    ? Promise.resolve({
                        rows: cachedRange.productSummary || [],
                        loaded: true
                    })
                    : (async () => {
                        try {
                            const rows = await fetchRpcRows('dashboard_top_products_daily', {
                                p_tenant_id: activeTenantId,
                                p_start_date: startBoundary,
                                p_end_date: endBoundary,
                                p_limit: 50
                            }, rpcTimingOptions);

                            if (rows.length > 0) {
                                return { rows, loaded: true };
                            }

                            console.warn('Top products daily returned no rows, trying product summary fallback.');
                            const fallbackRows = await fetchRpcRows('dashboard_product_summary', {
                                p_tenant_id: activeTenantId,
                                p_start_date: startBoundary,
                                p_end_date: endBoundary
                            }, rpcTimingOptions);

                            return {
                                rows: fallbackRows,
                                loaded: true
                            };
                        } catch (err) {
                            console.warn('Top products failed, dashboard continues:', err);
                            return {
                                rows: cachedRange?.productSummary || [],
                                loaded: Boolean(cachedRange?.productSummaryLoaded)
                            };
                        }
                    })();

            const plzSummaryPromise = !needsPlz
                ? Promise.resolve(cachedRange?.plzFacts || [])
                : cachedRange?.plzFactsLoaded
                    ? Promise.resolve(cachedRange.plzFacts || [])
                    : fetchRpcRowsPaged('dashboard_plz_summary', {
                        p_tenant_id: activeTenantId,
                        p_start_date: startBoundary,
                        p_end_date: endBoundary
                    }).catch(err => {
                        console.warn('PLZ summary failed, Germany map continues without PLZ data:', err);
                        return [];
                    });

            const [{ facts, previousFacts }, productSummaryResult, plzSummaryRows] = await Promise.all([
                factsPromise,
                productSummaryPromise,
                plzSummaryPromise
            ]);

            const productSummaryRows = productSummaryResult.rows || [];
            if (
                needsProducts &&
                productSummaryResult.loaded &&
                productSummaryRows.length === 0 &&
                facts.some(row => Number(row.revenue || 0) !== 0 || Number(row.order_count || 0) > 0)
            ) {
                console.warn('Top products returned no rows while order summary has data for this range.');
            }

            const cachePayload = {
                facts,
                previousFacts,
                productSummary: productSummaryRows || [],
                productSummaryLoaded: Boolean(productSummaryResult.loaded),
                positionsList: [],
                plzFacts: plzSummaryRows || [],
                plzFactsLoaded: Boolean(needsPlz || cachedRange?.plzFactsLoaded),
                salesAnalysisFacts: true,
                cachedAt: Date.now()
            };

            rangeCacheRef.current.set(cacheKey, cachePayload);
            writeSessionRangeCache(cacheKey, cachePayload);

            return cachePayload;
        };

        const applyRangePayload = (requestedStartDate, requestedEndDate, payload) => {
            const normalizedRange = normalizeDateRange(requestedStartDate, requestedEndDate);
            const rangeKey = getDateRangeCacheKey(normalizedRange.start, normalizedRange.end, activeTenantId);
            const latestPayload = getFreshMemoryRangeCache(rangeCacheRef.current, rangeKey) || payload;

            setRawFacts(latestPayload.facts || []);
            setPrevRawFacts(latestPayload.previousFacts || []);
            setRawProductSummary(latestPayload.productSummary || []);
            setProductSummaryLoading(false);
            setRawPositions(latestPayload.positionsList || []);
            setRawPlzFacts(latestPayload.plzFacts || []);
            setAppliedStartDate(normalizedRange.start);
            setAppliedEndDate(normalizedRange.end);
            activeRangeKeyRef.current = rangeKey;
            setHasLoadedFacts(true);
            setLoadingFacts(false);
        };

        const fetchFacts = async () => {
            if (!dateRangeReady) {
                
                return;
            }

            const fetchSeq = ++rangeFetchSeqRef.current;
            const normalizedRange = normalizeDateRange(startDate, endDate);

            const requestedStartDate = normalizedRange.start;
            const requestedEndDate = normalizedRange.end;
            const requestedRangeKey = getDateRangeCacheKey(requestedStartDate, requestedEndDate, activeTenantId);

            const needsPlz = false;
            activeRangeKeyRef.current = requestedRangeKey;

            // Every page except Profit uses its own focused RPCs. Avoid the old
            // full-period order load on navigation to independent pages.
            if (currentView !== 'comparison') {
                setAppliedStartDate(requestedStartDate);
                setAppliedEndDate(requestedEndDate);
                setHasLoadedFacts(true);
                setLoadingFacts(false);
                return;
            }

            const cachedRange = getFreshMemoryRangeCache(rangeCacheRef.current, requestedRangeKey) || readSessionRangeCache(requestedRangeKey);

            const startObj = parseISODateOnly(requestedStartDate);
            const endObj = parseISODateOnly(requestedEndDate);

            const daysInRange = startObj && endObj
                ? Math.ceil((endObj.getTime() - startObj.getTime()) / MS_PER_DAY) + 1
                : 0;

            // Profit loads its product comparison below, including the previous
            // period. Do not fetch the same product summary a second time.
            const includeProducts = currentView === 'overview' && daysInRange <= 365;

            if (cachedRange && (!includeProducts || cachedRange.productSummaryLoaded) && (!needsPlz || cachedRange.plzFactsLoaded)) {
                rangeCacheRef.current.set(requestedRangeKey, cachedRange);
                applyRangePayload(requestedStartDate, requestedEndDate, cachedRange);
                return;
            }

            setLoadingFacts(true);
            setProductSummaryLoading(includeProducts && !cachedRange?.productSummaryLoaded);

            if (includeProducts && !cachedRange?.productSummaryLoaded) {
                setRawProductSummary([]);
            }

            if (!includeProducts) {
                setRawProductSummary([]);
            }

            try {
                const payload = await loadRangePayload(requestedStartDate, requestedEndDate, {
                    includePlz: needsPlz,
                    includeProducts
                });

                if (fetchSeq !== rangeFetchSeqRef.current) return;
                applyRangePayload(requestedStartDate, requestedEndDate, payload);
            } catch (err) {
                if (fetchSeq !== rangeFetchSeqRef.current) return;
                console.error('Failed to load daily dashboard data:', err);
                setRawFacts([]);
                setPrevRawFacts([]);
                setRawProductSummary([]);
                setProductSummaryLoading(false);
                setRawPlzFacts([]);
            } finally {
                if (fetchSeq === rangeFetchSeqRef.current) setLoadingFacts(false);
            }
        };

        fetchFacts();
    }, [startDate, endDate, dateRangeReady, toLocalISO, currentView, mapScope, authReady, isAuthenticated, hasTenantAccess, activeTenantId, dataAnchorDate, dataRefreshRevision]);

    useEffect(() => {
        if (currentView !== 'overview' && currentView !== 'comparison' && currentView !== 'kpiWidgets') return;
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId) return;
        if (!dateRangeReady) return;

        const normalizedRange = normalizeDateRange(startDate, endDate);
        const requestedStartDate = normalizedRange.start;
        const requestedEndDate = normalizedRange.end;
        const startObj = parseISODateOnly(requestedStartDate);
        const endObj = parseISODateOnly(requestedEndDate);

        if (!startObj || !endObj) return;

        const startBoundary = toDayStartIso(requestedStartDate);
        const endBoundary = toNextDayStartIso(requestedEndDate);
        const previousRange = getPreviousDateRange(requestedStartDate, requestedEndDate, dataAnchorDate);
        const prevStartBoundary = toDayStartIso(previousRange.prevStart);
        const prevEndBoundary = toNextDayStartIso(previousRange.prevEnd);

        if (!startBoundary || !endBoundary || !prevStartBoundary || !prevEndBoundary) return;

        let cancelled = false;
        setOverviewPreviousChannels([]);
        setOverviewPreviousChannelsStatus('loading');
        if (currentView === 'overview') {
            setOverviewPreviousTimeseries([]);
            setOverviewPreviousTimeseriesStatus('loading');
        }
        const baseParams = {
            p_tenant_id: activeTenantId,
            p_start_date: startBoundary,
            p_end_date: endBoundary,
            p_platform_group: drilldownPlatform || null,
            p_platform: selectedPlatform || null,
            p_country: selectedCountry || null,
            p_order_status: orderStatusFilter
        };

        const fallbackKpisFromTimeseries = (rows) => {
            const revenue = rows.reduce((sum, row) => sum + Number(row.revenue || 0), 0);
            const profit = rows.reduce((sum, row) => sum + Number(row.profit || 0), 0);
            const count = rows.reduce((sum, row) => sum + Number(row.order_count || 0), 0);
            return {
                revenue,
                profit,
                order_count: count,
                aov: count > 0 ? revenue / count : 0,
                previous_revenue: 0,
                previous_profit: 0,
                previous_order_count: 0,
                previous_aov: 0,
                revenue_growth: 0,
                profit_growth: 0,
                order_growth: 0,
                aov_growth: 0
            };
        };

        const loadOverview = async () => {
            const isProfitView = currentView === 'comparison';
            if (!isProfitView) {
                setOverviewLoading(true);
                setOverviewKpis(null);
            } else {
                setOverviewTopProducts([]);
            }
            setOverviewTopProductsLoading(true);
            setOverviewError(null);
            const rpcTimingOptions = { context: 'Overview' };

            const debugRpc = async (name, params, options) => {
                const started = performance.now();

                try {
                    

                    const rows = await fetchRpcRows(name, params, options);

                    

                    return { name, ok: true, rows: rows || [] };
                } catch (err) {
                    console.error(`[RPC FAIL] ${name}`, {
                        ms: Math.round(performance.now() - started),
                        message: err?.message,
                        error: err
                    });

                    return { name, ok: false, rows: [], error: err };
                }
            };

            const normalizeOverviewProductRows = (rows) => (rows || []).map(row => {
                const totalRevenue = Number(row.total_revenue ?? row.revenue ?? 0);
                const totalQty = Number(row.total_qty ?? row.qty ?? 0);
                const articleId = row.article_id ?? row.id;
                const catalogPricing = activeArticlePricingById.get(String(articleId ?? ''));
                const fallbackPricing = getArticlePricing(row);
                const activePurchasePrice = Number(catalogPricing?.purchasePrice || fallbackPricing.purchasePrice || 0);
                const reportedProfit = Number(row.total_profit ?? row.profit);
                const totalProfit = Number.isFinite(reportedProfit)
                    ? reportedProfit
                    : activePurchasePrice > 0 ? totalRevenue - (totalQty * activePurchasePrice) : 0;
                const marginPercent = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
                const previousQty = Number(row.previous_qty ?? row.previousQty ?? 0);
                const previousRevenue = Number(row.previous_revenue ?? row.previousRevenue ?? 0);

                return {
                    ...row,
                    article_id: articleId,
                    article_name: row.article_name || row.name || `Artikel ${articleId}`,
                    sku: row.sku || '',
                    total_qty: totalQty,
                    total_revenue: totalRevenue,
                    total_profit: totalProfit,
                    margin_percent: Number.isFinite(marginPercent) ? marginPercent : 0,
                    previous_qty: previousQty,
                    previous_revenue: previousRevenue,
                    previous_profit: Number.isFinite(Number(row.previous_profit ?? row.previousProfit))
                        ? Number(row.previous_profit ?? row.previousProfit)
                        : activePurchasePrice > 0 ? previousRevenue - (previousQty * activePurchasePrice) : 0,
                    main_channel: row.main_channel || row.platform || row.platform_group || 'Other',
                    stock: Number(row.stock ?? row.stock_level ?? 0),
                    purchase_price: activePurchasePrice
                };
            }).filter(row => row.article_id);

            const aggregateProductComparisonRows = (rows) => {
                const productMap = new Map();
                (rows || []).forEach(row => {
                    const key = row.article_id ?? row.id;
                    if (key === undefined || key === null) return;
                    const current = productMap.get(String(key)) || {
                        total_qty: 0, total_revenue: 0, total_profit: 0,
                        previous_qty: 0, previous_revenue: 0, previous_profit: 0
                    };
                    productMap.set(String(key), {
                        ...current, ...row, article_id: key,
                        total_qty: Number(current.total_qty || 0) + Number(row.total_qty || 0),
                        total_revenue: Number(current.total_revenue || 0) + Number(row.total_revenue || 0),
                        total_profit: Number(current.total_profit || 0) + Number(row.total_profit || 0),
                        previous_qty: Number(current.previous_qty || 0) + Number(row.previous_qty || 0),
                        previous_revenue: Number(current.previous_revenue || 0) + Number(row.previous_revenue || 0),
                        previous_profit: Number(current.previous_profit || 0) + Number(row.previous_profit || 0)
                    });
                });
                return normalizeOverviewProductRows(Array.from(productMap.values())).sort((a, b) => b.total_profit - a.total_profit);
            };

            if (isProfitView) {
                try {
                    let productRows = [];
                    const hasGlobalProductScope = Boolean(drilldownPlatform || selectedPlatform || selectedCountry);

                    if (!hasGlobalProductScope) {
                        const comparisonRows = await fetchRpcRowsPaged('dashboard_profit_product_comparison', {
                            p_tenant_id: activeTenantId,
                            p_start_date: String(startBoundary).slice(0, 10),
                            p_end_date: String(endBoundary).slice(0, 10),
                            p_prev_start_date: String(prevStartBoundary).slice(0, 10),
                            p_prev_end_date: String(prevEndBoundary).slice(0, 10)
                        }, {
                            ...rpcTimingOptions,
                            context: 'Profit',
                            label: 'product comparison'
                        });
                        productRows = aggregateProductComparisonRows(comparisonRows);
                    } else {
                        const [currentRows, previousRows] = await Promise.all([
                            fetchRpcRows('dashboard_overview_top_products', {
                                ...baseParams,
                                p_limit: 50
                            }, { ...rpcTimingOptions, context: 'Profit', label: 'current top products' }),
                            fetchRpcRows('dashboard_overview_top_products', {
                                ...baseParams,
                                p_start_date: prevStartBoundary,
                                p_end_date: prevEndBoundary,
                                p_limit: 50
                            }, { ...rpcTimingOptions, context: 'Profit', label: 'previous top products' })
                        ]);
                        const previousByArticle = new Map(
                            normalizeOverviewProductRows(previousRows).map(row => [String(row.article_id), row])
                        );
                        productRows = normalizeOverviewProductRows(currentRows).map(row => {
                            const previous = previousByArticle.get(String(row.article_id));
                            return {
                                ...row,
                                previous_qty: Number(previous?.total_qty || 0),
                                previous_revenue: Number(previous?.total_revenue || 0),
                                previous_profit: Number(previous?.total_profit || 0)
                            };
                        });
                    }

                    if (!cancelled) setOverviewTopProducts(productRows);
                } catch (err) {
                    if (!cancelled) {
                        console.warn('Profit product comparison failed; loading a smaller product summary.', err);
                        try {
                            const fallbackRows = await fetchRpcRows('dashboard_overview_top_products', {
                                ...baseParams,
                                p_limit: 50
                            }, { ...rpcTimingOptions, context: 'Profit', label: 'product fallback' });
                            if (!cancelled) setOverviewTopProducts(normalizeOverviewProductRows(fallbackRows));
                        } catch (fallbackError) {
                            console.warn('Profit product fallback failed:', fallbackError);
                            if (!cancelled) setOverviewTopProducts([]);
                        }
                    }
                } finally {
                    if (!cancelled) setOverviewTopProductsLoading(false);
                }
                return;
            }


            const loadTopProductsFallback = async () => {
                const canUseUnfilteredFallback =
                    !drilldownPlatform &&
                    !selectedPlatform &&
                    !selectedCountry;

                if (!canUseUnfilteredFallback) return [];

                try {
                    const fallbackParams = {
                        p_tenant_id: activeTenantId,
                        p_start_date: String(startBoundary).slice(0, 10),
                        p_end_date: String(endBoundary).slice(0, 10),
                        p_limit: 50
                    };
                    const dailyRows = await fetchRpcRows('dashboard_top_products_daily', fallbackParams, {
                        ...rpcTimingOptions,
                        label: 'top products fallback'
                    });

                    if (dailyRows?.length) {
                        return normalizeOverviewProductRows(dailyRows);
                    }

                    const summaryRows = await fetchRpcRows('dashboard_product_summary', {
                        p_tenant_id: activeTenantId,
                        p_start_date: fallbackParams.p_start_date,
                        p_end_date: fallbackParams.p_end_date
                    }, {
                        ...rpcTimingOptions,
                        label: 'product summary fallback'
                    });

                    return normalizeOverviewProductRows(summaryRows)
                        .sort((a, b) => Number(b.total_profit || 0) - Number(a.total_profit || 0))
                        .slice(0, 50);
                } catch (err) {
                    console.warn('Overview top products fallback failed:', err);
                    return [];
                }
            };

            try {
                

                const effectiveGranularity = resolveChartGrouping(
                    requestedStartDate,
                    requestedEndDate,
                    chartGrouping
                );

                if (currentView === 'overview') {
                    fetchRpcRows('dashboard_overview_timeseries', {
                        ...baseParams,
                        p_start_date: prevStartBoundary,
                        p_end_date: prevEndBoundary,
                        p_granularity: effectiveGranularity
                    }, { ...rpcTimingOptions, label: 'previous trend comparison' })
                        .then(rows => {
                            if (cancelled) return;
                            setOverviewPreviousTimeseries(rows || []);
                            setOverviewPreviousTimeseriesStatus(rows?.length ? 'ready' : 'empty');
                        })
                        .catch(error => {
                            if (cancelled) return;
                            console.warn('Previous trend comparison could not be loaded:', error);
                            setOverviewPreviousTimeseries([]);
                            setOverviewPreviousTimeseriesStatus('error');
                        });
                }

                const [results, previousChannelResult] = await Promise.all([
                    Promise.all([
                        debugRpc('dashboard_overview_kpis', {
                            ...baseParams,
                            p_prev_start_date: prevStartBoundary,
                            p_prev_end_date: prevEndBoundary
                        }, rpcTimingOptions),

                        debugRpc('dashboard_overview_timeseries', {
                            ...baseParams,
                            p_granularity: effectiveGranularity
                        }, rpcTimingOptions),

                        debugRpc('dashboard_overview_channels', baseParams, rpcTimingOptions),

                        debugRpc('dashboard_overview_status', baseParams, rpcTimingOptions),

                        debugRpc('dashboard_overview_top_products', {
                            ...baseParams,
                            p_limit: 50
                        }, rpcTimingOptions)
                    ]),
                    fetchRpcRows('dashboard_overview_channels', {
                        ...baseParams,
                        p_start_date: prevStartBoundary,
                        p_end_date: prevEndBoundary
                    }, { ...rpcTimingOptions, label: 'previous channel comparison' })
                        .then(rows => ({ rows, ok: true }))
                        .catch(error => ({ rows: [], ok: false, error }))
                ]);

                if (cancelled) return;

                setOverviewPreviousChannels(previousChannelResult.rows || []);
                setOverviewPreviousChannelsStatus(previousChannelResult.ok ? 'ready' : 'error');
                if (!previousChannelResult.ok) {
                    console.warn('Previous channel comparison could not be loaded:', previousChannelResult.error);
                }

                const resultMap = Object.fromEntries(results.map(r => [r.name, r]));

                const kpiRows = resultMap.dashboard_overview_kpis?.rows || [];
                const timeseriesRows = resultMap.dashboard_overview_timeseries?.rows || [];
                const channelRows = resultMap.dashboard_overview_channels?.rows || [];
                const statusRows = resultMap.dashboard_overview_status?.rows || [];
                let productRows = resultMap.dashboard_overview_top_products?.rows || [];
                if (!drilldownPlatform && !selectedPlatform && !selectedCountry) {
                    try {
                        const comparisonRows = await fetchRpcRows('dashboard_profit_product_comparison', {
                            p_tenant_id: activeTenantId,
                            p_start_date: String(startBoundary).slice(0, 10),
                            p_end_date: String(endBoundary).slice(0, 10),
                            p_prev_start_date: String(prevStartBoundary).slice(0, 10),
                            p_prev_end_date: String(prevEndBoundary).slice(0, 10)
                        }, {
                            ...rpcTimingOptions,
                            label: 'product comparison'
                        });
                        if (comparisonRows.length) productRows = aggregateProductComparisonRows(comparisonRows);
                    } catch (err) {
                        console.warn('Product comparison RPC unavailable, using previous overview products:', err);
                        try {
                            const previousRows = await fetchRpcRows('dashboard_overview_top_products', {
                                ...baseParams,
                                p_start_date: prevStartBoundary,
                                p_end_date: prevEndBoundary,
                                p_limit: 50
                            }, { ...rpcTimingOptions, label: 'previous top products' });
                            const previousByArticle = new Map(normalizeOverviewProductRows(previousRows).map(row => [String(row.article_id), row]));
                            productRows = normalizeOverviewProductRows(productRows).map(row => {
                                const previous = previousByArticle.get(String(row.article_id));
                                return {
                                    ...row,
                                    previous_qty: Number(previous?.total_qty || 0),
                                    previous_revenue: Number(previous?.total_revenue || 0),
                                    previous_profit: Number(previous?.total_profit || 0)
                                };
                            });
                        } catch (fallbackError) {
                            console.warn('Previous top products could not be loaded:', fallbackError);
                        }
                }
                }

                if (!productRows.length) {
                    productRows = await loadTopProductsFallback();
                }

                const nextOverviewKpis = kpiRows[0] || fallbackKpisFromTimeseries(timeseriesRows || []);

                setOverviewKpis(nextOverviewKpis);

                setOverviewTimeseries(timeseriesRows || []);
                setOverviewChannels(channelRows || []);
                setOverviewStatus(statusRows || []);
                setOverviewTopProducts(productRows || []);

                const failedResults = results.filter(r => !r.ok);

                if (failedResults.length) {
                    setOverviewError(
                        'Einige Overview-Daten konnten nicht geladen werden: ' +
                        failedResults.map(r => r.name).join(', ')
                    );
                } else {
                    setOverviewError(null);
                }
            } catch (err) {
                if (!cancelled) {
                    setOverviewPreviousChannels([]);
                    setOverviewPreviousChannelsStatus('error');
                    if (currentView === 'overview') {
                        setOverviewPreviousTimeseries([]);
                        setOverviewPreviousTimeseriesStatus('error');
                    }
                    // Provide more helpful error message for large periods
                    const isLargePeriod = (appliedEndDate && appliedStartDate) && 
                        ((new Date(appliedEndDate) - new Date(appliedStartDate)) / (1000 * 60 * 60 * 24) > 180);
                    
                    let errorMessage = err.message || 'Overview konnte nicht geladen werden.';
                    if (isLargePeriod && errorMessage.includes('timeout')) {
                        errorMessage = 'Der Zeitraum ist zu groß. Bitte reduzieren Sie den Zeitraum auf maximal 6 Monate für schnelleres Laden. ' + 
                                      'Hinweis: Wir arbeiten an Verbesserungen der Performance für große Zeiträume.';
                    } else if (isLargePeriod) {
                        errorMessage = 'Fehler beim Laden für diesen großen Zeitraum. ' + 
                                      'Bitte versuchen Sie einen kleineren Zeitraum oder prüfen Sie Ihre Internetverbindung. ' +
                                      'Ursprünglicher Fehler: ' + errorMessage;
                    }
                    
                    setOverviewError(errorMessage);
                }
            } finally {
                if (!cancelled) {
                    setOverviewLoading(false);
                    setOverviewTopProductsLoading(false);
                }
            }
        };

        loadOverview();

        return () => {
            cancelled = true;
        };
    }, [
        currentView,
        dateRangeReady,
        startDate,
        endDate,
        chartGrouping,
        selectedPlatform,
        drilldownPlatform,
        selectedCountry,
        orderStatusFilter,
        toLocalISO,
        authReady,
        isAuthenticated,
        hasTenantAccess,
        activeTenantId,
        dataAnchorDate,
        dataRefreshRevision,
        activeArticlePricingById
    ]);

    const handlePlatformClick = (platformName) => {
        if (!drilldownPlatform && isPlatformGroupDrilldown(platformName)) {
            setDrilldownPlatform(platformName);
            setSelectedPlatform(null);
        } else {
            setSelectedPlatform(prev => (prev === platformName ? null : platformName));
        }
        setSelectedCountry(null);
        setCurrentPage(1);
    };

    useEffect(() => {
        if (!drilldownPlatform && selectedPlatform && isPlatformGroupDrilldown(selectedPlatform)) {
            setDrilldownPlatform(selectedPlatform);
            setSelectedPlatform(null);
        }
    }, [drilldownPlatform, selectedPlatform]);

    const handleCountryClick = (geoId) => {
        if (mapScope === 'germany') {
            setActiveBundesland(geoId);
            setMapScope('detail');
            setSelectedCountry(null);
        } else {
            setSelectedCountry(prev => (prev === geoId ? null : geoId));
        }
        setCurrentPage(1);
    };

    const handleBackToGermany = () => {
        setMapScope('germany');
        setActiveBundesland(null);
        setSelectedCountry(null);
    };

    const clearAllFilters = () => {
        const normalizedDefaultRange = normalizeDateRange(defaultDateRange.start, defaultDateRange.end);
        setSelectedPlatform(null);
        setDrilldownPlatform(null);
        setSelectedCountry(null);
        setCustomerSegmentFilter(null);
        setCustomerOriginFilter(null);
        setCustomerGroupFilter(null);
        setOrderStatusFilter('all');
        setStartDate(normalizedDefaultRange.start);
        setEndDate(normalizedDefaultRange.end);
        setDatePreset(getPresetKeyForRange(normalizedDefaultRange.start, normalizedDefaultRange.end, dataAnchorDate));
        setChartGrouping('auto');
        setCurrentPage(1);
        setCustomerPage(1);
        setCustomerVisibleLimit(10);
        resetCustomersRequestState();
    };

    const clearGlobalDashboardFilters = () => {
        setSelectedPlatform(null);
        setDrilldownPlatform(null);
        setSelectedCountry(null);
        setOrderStatusFilter('all');
        setCurrentPage(1);
    };

    const clearHeaderFilters = () => {
        clearGlobalDashboardFilters();
        pageFilterResetRef.current?.();
        if (currentView !== 'customers') return;
        setCustomerSegmentFilter(null);
        setCustomerOriginFilter(null);
        setCustomerGroupFilter(null);
        setCustomerPage(1);
        setCustomerVisibleLimit(10);
        resetCustomersRequestState();
    };

    const handleChartClick = (chartState) => {
        if (!chartState || !chartState.activePayload || !chartState.activePayload.length) return;
        const effectiveGrouping = resolveChartGrouping(appliedStartDate, appliedEndDate, chartGrouping);
        if (effectiveGrouping === 'year') {
            setChartGrouping('month');
            setCurrentPage(1);
        } else if (effectiveGrouping === 'month') {
            setChartGrouping('day');
            setCurrentPage(1);
        }
    };

    const filteredFacts = useMemo(() => {
        const rangeStart = appliedStartDate ? String(appliedStartDate).slice(0, 10) : null;
        const rangeEnd = appliedEndDate ? String(appliedEndDate).slice(0, 10) : null;
        return applyFactFilters(rawFacts, {
            rangeStart,
            rangeEnd,
            drilldownPlatform,
            selectedPlatform,
            selectedCountry,
            orderStatusFilter
        });
    }, [rawFacts, appliedStartDate, appliedEndDate, selectedPlatform, drilldownPlatform, selectedCountry, orderStatusFilter]);

    const topProducts = useMemo(() => {
        if (!rawProductSummary.length) return [];

        const productMap = new Map();

        rawProductSummary.forEach(row => {
            const id = row.article_id ?? row.id;
            if (!id) return;

            const articleName = String(row.article_name || row.name || '').trim();
            if (!articleName || /^Artikel\s+\d+$/i.test(articleName)) return;

            const key = String(id);
            const existing = productMap.get(key) || {
                id,
                article_name: articleName,
                stock: Number(row.stock || 0),
                purchase_price: Number(row.purchase_price || 0),
                total_qty: 0,
                total_revenue: 0,
                total_profit: 0,
                profit: 0,
                main_channel: 'Other',
                channels: {}
            };

            const totalQty = Number(row.total_qty || row.qty || 0);
            const totalRevenue = Number(row.total_revenue || row.revenue || 0);
            const totalProfit = Number(row.total_profit ?? row.profit ?? row.gross_profit ?? 0);
            const channel = row.main_channel || row.platform || row.platform_group || 'Other';

            existing.total_qty += totalQty;
            existing.total_revenue += totalRevenue;
            existing.total_profit += totalProfit;
            existing.profit = existing.total_profit;
            existing.stock = Number.isFinite(Number(row.stock)) ? Number(row.stock) : existing.stock;
            existing.purchase_price = Number.isFinite(Number(row.purchase_price)) ? Number(row.purchase_price) : existing.purchase_price;
            existing.channels[channel] = (existing.channels[channel] || 0) + totalQty;

            productMap.set(key, existing);
        });

        return Array.from(productMap.values())
            .map(product => {
                const mainChannel = Object.entries(product.channels)
                    .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))[0]?.[0] || 'Other';
                const pricing = activeArticlePricingById.get(String(product.id ?? '')) || getArticlePricing(product);
                const purchasePrice = Number(pricing.purchasePrice || 0);
                const totalProfit = purchasePrice > 0
                    ? Number(product.total_revenue || 0) - (Number(product.total_qty || 0) * purchasePrice)
                    : 0;
                return {
                    ...product,
                    main_channel: mainChannel,
                    purchase_price: purchasePrice,
                    total_profit: totalProfit,
                    profit: totalProfit
                };
            })
            .filter(product => product.total_qty > 0 || product.total_revenue !== 0 || product.total_profit !== 0)
            .sort((a, b) => Number(b.total_profit || 0) - Number(a.total_profit || 0))
            .slice(0, 50);
    }, [rawProductSummary, activeArticlePricingById]);

    const salesAnalysisSnapshot = null;

    const overviewSnapshot = useMemo(() => {
        const kpi = { revenue: 0, profit: 0, count: 0, aov: 0 };
        const statusCounts = {};
        const sourceCounts = {};
        const platformMap = {};
        const countryMap = {};

        filteredFacts.forEach(row => {
            const revenue = Number(row.revenue || 0);
            const profit = Number(row.profit || 0);
            const orders = Number(row.order_count || 0);
            const platformKey = drilldownPlatform ? row.platform : row.platform_group;
            const sourceKey = row.platform_group || 'Other';

            kpi.revenue += revenue;
            kpi.profit += profit;
            kpi.count += orders;

            const statusLabel = row.status_label || 'Unbekannt';
            statusCounts[statusLabel] = (statusCounts[statusLabel] || 0) + orders;
            sourceCounts[sourceKey] = (sourceCounts[sourceKey] || 0) + orders;

            if (!platformMap[platformKey]) platformMap[platformKey] = { name: platformKey, revenue: 0, profit: 0, orders: 0 };
            platformMap[platformKey].revenue += revenue;
            platformMap[platformKey].profit += profit;
            platformMap[platformKey].orders += orders;

            if (!countryMap[row.country]) countryMap[row.country] = { name: row.country, revenue: 0, profit: 0, count: 0 };
            countryMap[row.country].revenue += revenue;
            countryMap[row.country].profit += profit;
            countryMap[row.country].count += orders;
        });

        kpi.aov = kpi.count > 0 ? kpi.revenue / kpi.count : 0;
        const platformTotal = Object.values(platformMap).reduce((s, r) => s + r.revenue, 0);

        return {
            kpi,
            statusDistribution: Object.entries(statusCounts).map(([name, value]) => ({ name, value })),
            sourceDistribution: Object.entries(sourceCounts)
                .map(([name, value]) => ({ name, value }))
                .sort((a, b) => b.value - a.value),
            platformDataAll: Object.values(platformMap).map(item => ({
                name: item.name,
                value: Number(item.revenue.toFixed(2)),
                profit: Number(item.profit.toFixed(2)),
                profitMargin: item.revenue > 0 ? (item.profit / item.revenue) * 100 : 0,
                orders: item.orders,
                percentage: platformTotal > 0 ? ((item.revenue / platformTotal) * 100).toFixed(1) : 0
            })).sort((a, b) => b.value - a.value),
            countrySalesData: Object.values(countryMap).map(item => ({
                ...item,
                profitMargin: item.revenue > 0 ? (item.profit / item.revenue) * 100 : 0
            })).sort((a, b) => b.revenue - a.revenue)
        };
    }, [filteredFacts, drilldownPlatform]);

    const kpiData = useMemo(() => {
        if (!overviewKpis) {
            return overviewSnapshot.kpi;
        }

        const count = Number(overviewKpis.order_count ?? 0);
        const revenue = Number(overviewKpis.revenue ?? 0);

        const mapped = {
            revenue,
            profit: Number(overviewKpis.profit ?? 0),
            count,
            aov: Number(overviewKpis.aov ?? (count > 0 ? revenue / count : 0))
        };

        return mapped;
    }, [overviewKpis, overviewSnapshot.kpi]);

    const statusDistribution = useMemo(() => {
        if (!overviewStatus.length) return overviewSnapshot.statusDistribution;
        return overviewStatus.map(row => ({
            name: row.status_label || getOrderStatusLabel(row),
            value: Number(row.order_count || 0)
        }));
    }, [overviewStatus, overviewSnapshot.statusDistribution]);

    const sourceDistribution = useMemo(() => {
        if (!overviewChannels.length) return overviewSnapshot.sourceDistribution;
        return overviewChannels
            .map(row => ({
                name: row.platform_group || row.platform || 'Other',
                value: Number(row.order_count || 0)
            }))
            .sort((a, b) => b.value - a.value);
    }, [overviewChannels, overviewSnapshot.sourceDistribution]);

    const platformDataAll = useMemo(() => {
        if (!overviewChannels.length) return overviewSnapshot.platformDataAll;
        return overviewChannels
            .map(row => ({
                name: row.platform || row.platform_group || 'Other',
                value: Number(row.revenue || 0),
                profit: Number(row.profit || 0),
                profitMargin: Number(row.revenue || 0) > 0 ? (Number(row.profit || 0) / Number(row.revenue || 0)) * 100 : 0,
                orders: Number(row.order_count || 0),
                percentage: Number(row.share_percent || 0).toFixed(1)
            }))
            .sort((a, b) => b.value - a.value);
    }, [overviewChannels, overviewSnapshot.platformDataAll]);

    const comparisonPlatformDataAll = useMemo(() => {
        if (currentView !== 'comparison') {
            if (overviewPreviousChannelsStatus !== 'ready') return [];
            return (overviewPreviousChannels || []).map(row => ({
                name: row.platform || row.platform_group || 'Other',
                value: Number(row.revenue || 0)
            })).sort((a, b) => b.value - a.value);
        }
        const previousFacts = applyFactFilters(prevRawFacts, {
            drilldownPlatform,
            selectedPlatform,
            selectedCountry
        });
        const platformMap = new Map();

        previousFacts.forEach(row => {
            const name = drilldownPlatform
                ? row.platform || row.platform_group || 'Other'
                : row.platform_group || row.platform || 'Other';
            const current = platformMap.get(name) || { name, value: 0 };
            current.value += Number(row.revenue || 0);
            platformMap.set(name, current);
        });

        return Array.from(platformMap.values()).sort((a, b) => b.value - a.value);
    }, [currentView, overviewPreviousChannels, overviewPreviousChannelsStatus, prevRawFacts, drilldownPlatform, selectedPlatform, selectedCountry]);

    const countrySalesData = mapEuropeCountries.length ? mapEuropeCountries : overviewSnapshot.countrySalesData;



    const prevKpiData = useMemo(() => {
        const prevFiltered = applyFactFilters(prevRawFacts, {
            drilldownPlatform,
            selectedPlatform,
            selectedCountry
        });
        const revenue = prevFiltered.reduce((s, r) => s + Number(r.revenue), 0);
        const profit = prevFiltered.reduce((s, r) => s + Number(r.profit), 0);
        const count = prevFiltered.reduce((s, r) => s + Number(r.order_count || 0), 0);
        return { revenue, profit, count, aov: count > 0 ? revenue / count : 0 };
    }, [prevRawFacts, drilldownPlatform, selectedPlatform, selectedCountry]);

    const kpiGrowth = useMemo(() => {
        const getGrowth = (current, previous) => previous > 0 ? ((current - previous) / previous) * 100 : (current > 0 ? 100 : 0);

        if (overviewKpis) {
            return {
                revenue: getGrowth(
                    Number(overviewKpis.revenue || 0),
                    Number(overviewKpis.previous_revenue || 0)
                ),
                profit: getGrowth(
                    Number(overviewKpis.profit || 0),
                    Number(overviewKpis.previous_profit || 0)
                ),
                orders: getGrowth(
                    Number(overviewKpis.order_count || 0),
                    Number(overviewKpis.previous_order_count || 0)
                ),
                aov: getGrowth(
                    Number(overviewKpis.aov || 0),
                    Number(overviewKpis.previous_aov || 0)
                )
            };
        }

        return {
            revenue: getGrowth(kpiData.revenue, prevKpiData.revenue),
            profit: getGrowth(kpiData.profit, prevKpiData.profit),
            orders: getGrowth(kpiData.count, prevKpiData.count),
            aov: getGrowth(kpiData.aov, prevKpiData.aov)
        };
    }, [overviewKpis, kpiData, prevKpiData]);

    const comparisonKpiData = useMemo(() => {
        if (overviewKpis) {
            const revenue = Number(overviewKpis.previous_revenue || 0);
            const profit = Number(overviewKpis.previous_profit || 0);
            const count = Number(overviewKpis.previous_order_count || 0);
            return {
                revenue,
                profit,
                count,
                aov: Number(overviewKpis.previous_aov || 0),
                marginPct: revenue > 0 ? (profit / revenue) * 100 : 0
            };
        }

        return {
            ...prevKpiData,
            marginPct: prevKpiData.revenue > 0 ? (prevKpiData.profit / prevKpiData.revenue) * 100 : 0
        };
    }, [overviewKpis, prevKpiData]);



    const overviewChartData = useMemo(() => {
        if (overviewTimeseries.length) {
            return overviewTimeseries.map((row, index) => ({
                name: row.period_label || String(row.period_start || ''),
                periodStart: row.period_start || null,
                sortIndex: index,
                revenue: Number(row.revenue || 0),
                profit: Number(row.profit || 0),
                orders: Number(row.order_count || 0)
            }));
        }

        const chartEndDate = clampEndDateToToday(appliedEndDate);

        return buildOverviewChartData(
            filteredFacts,
            appliedStartDate,
            chartEndDate,
            resolveChartGrouping(appliedStartDate, chartEndDate, chartGrouping)
        );
    }, [overviewTimeseries, filteredFacts, appliedStartDate, appliedEndDate, chartGrouping]);

    const overviewChartGranularity = useMemo(() => (
        resolveChartGrouping(appliedStartDate, clampEndDateToToday(appliedEndDate), chartGrouping)
    ), [appliedStartDate, appliedEndDate, chartGrouping]);

    const rpcOverviewData = useMemo(() => {
        return {
            kpiData,
            kpiGrowth,
            comparisonKpiData,
            chartData: overviewChartData,
            platformDataAll,
            comparisonPlatformDataAll,
            sourceDistribution,
            statusDistribution
        };
    }, [
        kpiData,
        kpiGrowth,
        comparisonKpiData,
        overviewChartData,
        platformDataAll,
        comparisonPlatformDataAll,
        sourceDistribution,
        statusDistribution
    ]);

    const overviewReturnsSummary = useMemo(() => {
        const kpis = returnsOverviewSummary?.kpis || {};
        const overTime = Array.isArray(returnsOverviewSummary?.over_time)
            ? returnsOverviewSummary.over_time
            : Array.isArray(returnsOverviewSummary?.overTime)
                ? returnsOverviewSummary.overTime
                : [];

        return {
            totalReturns: Number(returnsOverviewSummary?.totalReturns ?? kpis.totalReturns ?? 0),
            returnRate: Number(returnsOverviewSummary?.returnRate ?? kpis.returnRate ?? 0),
            topReason: returnsOverviewSummary?.topReason || '-',
            topCategory: returnsOverviewSummary?.topCategory || '-',
            channelReturnSummaries: Array.isArray(returnsOverviewSummary?.channelReturnSummaries)
                ? returnsOverviewSummary.channelReturnSummaries
                : [],
            overTime,
            topArticles: Array.isArray(returnsOverviewSummary?.top_articles)
                ? returnsOverviewSummary.top_articles
                : [],
            categories: Array.isArray(returnsOverviewSummary?.categories)
                ? returnsOverviewSummary.categories
                : [],
            granularity: resolveChartGrouping(appliedStartDate, appliedEndDate, chartGrouping)
        };
    }, [returnsOverviewSummary, appliedStartDate, appliedEndDate, chartGrouping]);

    const rpcTopProducts = useMemo(() => {
        return (overviewTopProducts || []).map(row => {
            const totalRevenue = Number(row.total_revenue ?? row.revenue ?? 0);
            const totalProfit = Number(row.total_profit ?? row.profit ?? 0);
            const marginPercent = Number(row.margin_percent ?? (totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0));

            return {
                id: row.article_id,
                article_id: row.article_id,
                article_name: row.article_name || `Artikel ${row.article_id}`,
                name: row.article_name || `Artikel ${row.article_id}`,
                sku: row.sku || '',
                category: row.category || '',
                product_group: row.product_group || '',
                purchase_price: Number(row.purchase_price || 0),
                total_qty: Number(row.total_qty ?? row.qty ?? 0),
                total_revenue: totalRevenue,
                total_profit: totalProfit,
                profit: totalProfit,
                previous_qty: Number(row.previous_qty ?? row.previousQty ?? 0),
                previous_revenue: Number(row.previous_revenue ?? row.previousRevenue ?? 0),
                previous_profit: Number(row.previous_profit ?? row.previousProfit ?? 0),
                profit_margin: Number.isFinite(marginPercent) ? marginPercent : 0,
                main_channel: row.main_channel || 'Other',
                stock: Number(row.stock || 0),
                channels: {
                    [row.main_channel || 'Other']: Number(row.total_qty ?? row.qty ?? 0)
                }
            };
        });
    }, [overviewTopProducts]);

    const overviewDisplayTopProducts = useMemo(() => {
        return rpcTopProducts.length ? rpcTopProducts : topProducts;
    }, [rpcTopProducts, topProducts]);

    const handleOverviewTrendBucketChange = useCallback((selection) => {
        setOverviewTrendSelection(selection);
        if (!selection) {
            setOverviewTrendProducts(null);
            setOverviewTrendPlatformData(null);
            setOverviewTrendDetailsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!overviewTrendSelection?.startKey || !overviewTrendSelection?.endKey || !activeTenantId) return;

        const startBoundary = toDayStartIso(overviewTrendSelection.startKey);
        const endBoundary = toDayStartIso(overviewTrendSelection.endKey);
        if (!startBoundary || !endBoundary) return;

        const cacheKey = [
            activeTenantId,
            startBoundary,
            endBoundary,
            drilldownPlatform || '',
            selectedPlatform || '',
            selectedCountry || '',
            orderStatusFilter
        ].join('|');

        const cached = overviewTrendDetailsCacheRef.current.get(cacheKey);
        if (cached) {
            setOverviewTrendProducts(cached.products);
            setOverviewTrendPlatformData(cached.platformData);
            setOverviewTrendDetailsLoading(false);
            return;
        }

        let cancelled = false;
        const seq = ++overviewTrendDetailsSeqRef.current;
        setOverviewTrendDetailsLoading(true);
        setOverviewTrendProducts(null);
        setOverviewTrendPlatformData(null);

        const baseParams = {
            p_tenant_id: activeTenantId,
            p_start_date: startBoundary,
            p_end_date: endBoundary,
            p_platform_group: drilldownPlatform || null,
            p_platform: selectedPlatform || null,
            p_country: selectedCountry || null,
            p_order_status: orderStatusFilter
        };

        Promise.all([
            fetchRpcRows('dashboard_overview_channels', baseParams, {
                context: 'Overview cross filter',
                label: overviewTrendSelection.label
            }),
            fetchRpcRows('dashboard_overview_top_products', {
                ...baseParams,
                p_limit: 50
            }, {
                context: 'Overview cross filter',
                label: overviewTrendSelection.label
            })
        ]).then(([channelRows, productRows]) => {
            if (cancelled || seq !== overviewTrendDetailsSeqRef.current) return;

            const channelTotal = (channelRows || []).reduce((sum, row) => sum + Number(row.revenue || 0), 0);
            const platformData = (channelRows || [])
                .map(row => {
                    const revenue = Number(row.revenue || 0);
                    const profit = Number(row.profit || 0);
                    return {
                        name: row.platform || row.platform_group || 'Other',
                        value: revenue,
                        profit,
                        profitMargin: revenue > 0 ? (profit / revenue) * 100 : 0,
                        orders: Number(row.order_count || 0),
                        percentage: channelTotal > 0 ? (revenue / channelTotal) * 100 : Number(row.share_percent || 0)
                    };
                })
                .filter(row => row.value > 0)
                .sort((a, b) => b.value - a.value);

            const products = (productRows || []).map(row => {
                const totalRevenue = Number(row.total_revenue ?? row.revenue ?? 0);
                const totalQty = Number(row.total_qty ?? row.qty ?? 0);
                const pricing = activeArticlePricingById.get(String(row.article_id ?? '')) || getArticlePricing(row);
                const purchasePrice = Number(pricing.purchasePrice || 0);
                const totalProfit = purchasePrice > 0 ? totalRevenue - (totalQty * purchasePrice) : 0;
                const marginPercent = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
                return {
                    id: row.article_id,
                    article_id: row.article_id,
                    article_name: row.article_name || `Artikel ${row.article_id}`,
                    name: row.article_name || `Artikel ${row.article_id}`,
                    sku: row.sku || '',
                    total_qty: totalQty,
                    total_revenue: totalRevenue,
                    total_profit: totalProfit,
                    profit: totalProfit,
                    profit_margin: Number.isFinite(marginPercent) ? marginPercent : 0,
                    purchase_price: purchasePrice,
                    main_channel: row.main_channel || 'Other',
                    stock: Number(row.stock || 0),
                    channels: {
                        [row.main_channel || 'Other']: totalQty
                    }
                };
            });

            const nextDetails = { platformData, products };
            overviewTrendDetailsCacheRef.current.set(cacheKey, nextDetails);
            setOverviewTrendPlatformData(platformData);
            setOverviewTrendProducts(products);
            setOverviewTrendDetailsLoading(false);
        }).catch(err => {
            if (cancelled || seq !== overviewTrendDetailsSeqRef.current) return;
            console.warn('Overview cross-filter details failed:', err);
            setOverviewTrendPlatformData([]);
            setOverviewTrendProducts([]);
            setOverviewTrendDetailsLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [
        activeTenantId,
        drilldownPlatform,
        selectedPlatform,
        selectedCountry,
        orderStatusFilter,
        overviewTrendSelection?.startKey,
        overviewTrendSelection?.endKey,
        overviewTrendSelection?.label,
        activeArticlePricingById
    ]);

    const notificationAlerts = useMemo(() => {
        const settings = {
            ...DEFAULT_WARNING_SETTINGS,
            ...warningSettings
        };
        const excludedMarketplaces = new Set(
            normalizeNotificationList(settings.excludedMarketplaces)
                .map(normalizeNotificationKey)
        );
        const excludedProducts = new Set(
            normalizeNotificationList(settings.excludedProducts)
                .map(normalizeNotificationKey)
        );

        const alerts = [];
        const articleRows = (rawArticles || [])
            .filter(article => !isNotificationProductExcluded(article, excludedProducts))
            .map(article => {
            const stockValue = article?.stock_level ?? article?.available_stock ?? article?.stock ?? article?.fBestand ?? article?.quantity;
            const hasStock = hasValue(stockValue);
            const stock = hasStock ? Number(stockValue) : null;
            const pricing = getArticlePricing(article);

            return {
                id: String(article?.article_id ?? article?.id ?? article?.kArtikel ?? article?.sku ?? '').trim(),
                name: getNotificationArticleName(article),
                hasStock,
                stock: Number.isFinite(stock) ? stock : null,
                pricing
            };
        })
            .filter(article => article.id);

        const lowStockThreshold = Number(settings.lowStockUnits || DEFAULT_WARNING_SETTINGS.lowStockUnits);
        const topProductThreshold = Number(settings.topProductStockUnits || DEFAULT_WARNING_SETTINGS.topProductStockUnits);
        const returnRateThreshold = Number(settings.returnRatePct ?? DEFAULT_WARNING_SETTINGS.returnRatePct);
        const channelMarginThreshold = Number(settings.channelMarginPct ?? DEFAULT_WARNING_SETTINGS.channelMarginPct);
        const configuredWarningSeverity = ['critical', 'important'].includes(settings.escalationLevel) ? settings.escalationLevel : 'warning';

        const addArticleAlert = ({ id, severity, area, title, detail, rows, signalFilter }) => {
            const articleIds = normalizeNotificationArticleIds(rows.map(article => article.id));
            if (!articleIds.length) return;
            alerts.push({ id, severity, area, title, detail, countsArticles: true, signalValue: articleIds.length, worseWhen: 'higher', realertDelta: 1, primaryKey: articleIds[0], target: { view: 'articles', signalFilter, articleIds } });
        };
        const outOfStock = articleRows.filter(article => article.hasStock && article.stock <= 0);
        if (outOfStock.length) addArticleAlert({ id: 'notification-out-of-stock', severity: 'critical', area: 'Lager', title: `${outOfStock.length.toLocaleString('de-DE')} Artikel im Katalog ausverkauft`, detail: `${outOfStock[0]?.name || 'Ein Artikel'} hat keinen Bestand mehr.`, rows: outOfStock, signalFilter: 'out_of_stock' });
        const negativeMargin = articleRows.filter(article => article.pricing.isNegativeMargin);
        if (negativeMargin.length) addArticleAlert({ id: 'notification-negative-margin', severity: 'critical', area: 'Profit', title: `${negativeMargin.length.toLocaleString('de-DE')} Artikel mit negativer Marge`, detail: `${negativeMargin[0]?.name || 'Ein Artikel'} liegt mit dem Netto-VK unter dem Einkaufspreis.`, rows: negativeMargin, signalFilter: 'negative_margin' });
        const topProductsLowStock = (overviewDisplayTopProducts || [])
            .filter(product => !isNotificationMarketplaceExcluded(product, excludedMarketplaces))
            .filter(product => !isNotificationProductExcluded(product, excludedProducts))
            .map(product => ({ id: String(product?.article_id ?? product?.id ?? product?.kArtikel ?? product?.sku ?? '').trim(), name: getNotificationArticleName(product), stock: safeNumber(product?.stock, product?.stock_level, product?.fBestand), qty: safeNumber(product?.total_qty, product?.qty, product?.sold_qty), revenue: safeNumber(product?.total_revenue, product?.revenue) }))
            .filter(product => product.id && product.qty > 0 && product.stock > 0 && product.stock < topProductThreshold)
            .sort((a, b) => b.revenue - a.revenue);
        if (topProductsLowStock.length) addArticleAlert({ id: 'notification-top-products-low-stock', severity: configuredWarningSeverity, area: 'Produkte', title: `${topProductsLowStock.length.toLocaleString('de-DE')} Topseller bald ausverkauft`, detail: `${topProductsLowStock[0]?.name || 'Ein Topseller'} liegt unter ${topProductThreshold.toLocaleString('de-DE')} Stueck.`, rows: topProductsLowStock, signalFilter: 'low_stock' });
        const returnRate = safeNumber(overviewReturnsSummary?.returnRate);
        const totalReturns = safeNumber(overviewReturnsSummary?.totalReturns);
        if (returnRate >= returnRateThreshold && totalReturns > 0) alerts.push({ id: 'notification-return-rate', severity: configuredWarningSeverity, area: 'Retouren', title: `Retourenquote bei ${returnRate.toFixed(1)}%`, detail: overviewReturnsSummary?.topReason && overviewReturnsSummary.topReason !== '-' ? `Haeufigster Grund: ${overviewReturnsSummary.topReason}.` : `${totalReturns.toLocaleString('de-DE')} Retouren im aktuellen Zeitraum.`, signalValue: returnRate, worseWhen: 'higher', realertDelta: 1, primaryKey: String(overviewReturnsSummary?.topReason || ''), target: { view: 'returns', clearGlobalFilters: true } });
        else {
            const channelReturnAlert = (overviewReturnsSummary?.channelReturnSummaries || []).map(channel => ({ name: String(channel?.channel || 'Kanal'), returnRate: safeNumber(channel?.returnRate), totalReturns: safeNumber(channel?.totalReturns), soldOrders: safeNumber(channel?.soldOrders), topReason: channel?.topReason || '-' })).filter(channel => channel.totalReturns > 0 && channel.returnRate >= returnRateThreshold).sort((a, b) => b.returnRate - a.returnRate)[0];
            if (channelReturnAlert) alerts.push({ id: `notification-return-rate-channel-${normalizeNotificationKey(channelReturnAlert.name)}`, severity: configuredWarningSeverity, area: 'Retouren', title: `${channelReturnAlert.name} Retourenquote bei ${channelReturnAlert.returnRate.toFixed(1)}%`, detail: channelReturnAlert.topReason && channelReturnAlert.topReason !== '-' ? `Haeufigster Grund: ${channelReturnAlert.topReason}.` : `${channelReturnAlert.totalReturns.toLocaleString('de-DE')} Retouren bei ${channelReturnAlert.soldOrders.toLocaleString('de-DE')} Bestellungen.`, signalValue: channelReturnAlert.returnRate, worseWhen: 'higher', realertDelta: 1, primaryKey: channelReturnAlert.name, target: { view: 'returns', platform: channelReturnAlert.name } });
        }
        const lowMarginChannel = (platformDataAll || []).filter(channel => !isNotificationMarketplaceExcluded(channel, excludedMarketplaces)).map(channel => {
            const revenue = safeNumber(channel?.value, channel?.revenue); const profit = safeNumber(channel?.profit, channel?.total_profit); const marginPct = hasValue(channel?.profitMargin) ? Number(channel.profitMargin) : revenue > 0 ? (profit / revenue) * 100 : null;
            return { name: String(channel?.name || 'Kanal'), revenue, profit, marginPct };
        }).filter(channel => channel.revenue > 0 && Number.isFinite(channel.marginPct) && channel.marginPct < channelMarginThreshold).sort((a, b) => a.marginPct - b.marginPct)[0];
        if (lowMarginChannel) alerts.push({ id: 'notification-low-margin-channel', severity: configuredWarningSeverity, area: 'Kanaele', title: `${lowMarginChannel.name} mit schwacher Marge`, detail: `${lowMarginChannel.marginPct.toFixed(1)}% Marge bei ${formatNotificationCurrency(lowMarginChannel.revenue)} Umsatz.`, signalValue: lowMarginChannel.marginPct, worseWhen: 'lower', realertDelta: 1, primaryKey: lowMarginChannel.name, target: { view: 'comparison', platform: lowMarginChannel.name } });
        const lowStock = articleRows.filter(article => article.hasStock && article.stock > 0 && article.stock < lowStockThreshold);
        if (lowStock.length) addArticleAlert({ id: 'notification-low-stock', severity: configuredWarningSeverity, area: 'Lager', title: `${lowStock.length.toLocaleString('de-DE')} Artikel kritisch im Bestand`, detail: `${lowStock[0]?.name || 'Ein Artikel'} liegt unter ${lowStockThreshold.toLocaleString('de-DE')} Stueck.`, rows: lowStock, signalFilter: 'low_stock' });
        const consistencyErrors = validateNotificationAlertTargets(alerts);
        if (consistencyErrors.length) console.error('Notification target consistency failed:', consistencyErrors);
        return alerts.slice(0, 8);
    }, [rawArticles, overviewDisplayTopProducts, overviewReturnsSummary, platformDataAll, warningSettings]);

    const notificationScopeKey = useMemo(() => [
        currentUser?.id || 'anonymous',
        activeTenantId || 'no-tenant'
    ].join('|'), [
        currentUser?.id,
        activeTenantId
    ]);
    const notificationEvaluationComplete = Boolean(
        currentView === 'overview'
        && catalogDataLoaded
        && hasLoadedFacts
        && returnsOverviewSummary
        && !catalogDataLoading
        && !loadingFacts
        && !overviewLoading
        && !overviewReturnsLoading
    );

    useEffect(() => {
        if (!authReady || !isAuthenticated || !hasTenantAccess || !activeTenantId || !currentUser?.id) {
            setNotificationStateHydratedScope('');
            return undefined;
        }

        let cancelled = false;
        setNotificationStateHydratedScope('');

        const hydrateNotificationState = async () => {
            const prefix = `${notificationScopeKey}|`;
            const legacyLifecycle = Object.fromEntries(
                Object.entries(readNotificationLifecycle()).filter(([key]) => key.startsWith(prefix))
            );
            const legacyReadKeys = readNotificationReadKeys().filter(key => key.startsWith(prefix));
            const legacyPushedKeys = readNotificationPushedKeys().filter(key => key.startsWith(prefix));
            const { data, error } = database.notifications?.getState
                ? await database.notifications.getState(activeTenantId)
                : { data: null, error: { message: 'Benachrichtigungs-API fehlt.' } };
            if (cancelled) return;

            if (error) {
                console.warn('Benachrichtigungsstatus konnte nicht aus der Datenbank geladen werden:', error.message || error);
            }
            const serverState = data?.state || {};
            const hasServerState = Boolean(data?.exists);
            setNotificationLifecycleRegistry(hasServerState ? (serverState.lifecycle || {}) : legacyLifecycle);
            setReadNotificationKeys(hasServerState && Array.isArray(serverState.readKeys) ? serverState.readKeys : legacyReadKeys);
            setPushedNotificationKeys(hasServerState && Array.isArray(serverState.pushedKeys) ? serverState.pushedKeys : legacyPushedKeys);
            setNotificationStateHydratedScope(notificationScopeKey);
        };

        hydrateNotificationState();
        return () => {
            cancelled = true;
        };
    }, [
        activeTenantId,
        authReady,
        currentUser?.id,
        hasTenantAccess,
        isAuthenticated,
        notificationScopeKey
    ]);

    useEffect(() => {
        if (notificationStateHydratedScope !== notificationScopeKey || !activeTenantId) return;
        const stateSnapshot = {
            lifecycle: notificationLifecycleRegistry,
            readKeys: readNotificationKeys,
            pushedKeys: pushedNotificationKeys
        };
        notificationStatePersistQueueRef.current = notificationStatePersistQueueRef.current
            .catch(() => undefined)
            .then(async () => {
                if (!database.notifications?.saveState) return;
                const { error } = await database.notifications.saveState(activeTenantId, stateSnapshot);
                if (error) console.warn('Benachrichtigungsstatus konnte nicht gespeichert werden:', error.message || error);
            });
    }, [
        activeTenantId,
        notificationLifecycleRegistry,
        notificationScopeKey,
        notificationStateHydratedScope,
        pushedNotificationKeys,
        readNotificationKeys
    ]);

    useEffect(() => {
        if (notificationStateHydratedScope !== notificationScopeKey) return;
        setNotificationLifecycleRegistry(previousRegistry => {
            const nextRegistry = reconcileNotificationLifecycle({
                registry: previousRegistry,
                scopeKey: notificationScopeKey,
                alerts: notificationAlerts,
                allowResolutions: notificationEvaluationComplete
            });
            return JSON.stringify(nextRegistry) === JSON.stringify(previousRegistry) ? previousRegistry : nextRegistry;
        });
    }, [notificationAlerts, notificationEvaluationComplete, notificationScopeKey, notificationStateHydratedScope]);
    const routedNotificationAlerts = useMemo(() => {
        const categories = userPreferences?.notificationCategories || NOTIFICATION_CATEGORY_DEFAULTS;
        return notificationAlerts.filter(alert => categories[getNotificationPreferenceCategory(alert)] !== false);
    }, [notificationAlerts, userPreferences?.notificationCategories]);

    const visibleNotificationAlerts = useMemo(() => {
        if (warningSettings?.boardNotificationsEnabled === false) return [];
        return routedNotificationAlerts;
    }, [routedNotificationAlerts, warningSettings?.boardNotificationsEnabled]);

    const addNotificationReadState = useCallback((alerts) => {
        const readSet = new Set(readNotificationKeys);
        return alerts.map(alert => {
            const lifecycleRecord = notificationLifecycleRegistry[getAlertLifecycleKey(notificationScopeKey, alert.id)];
            const readKey = getAlertInstanceKey(notificationScopeKey, alert, notificationLifecycleRegistry);
            return {
                ...alert,
                readKey,
                isRead: readSet.has(readKey),
                isRecurring: Number(lifecycleRecord?.episode || 1) > 1 || Number(lifecycleRecord?.revision || 1) > 1
            };
        }).sort((a, b) => (
            Number(a.isRead) - Number(b.isRead)
            || getNotificationSeverityRank(b.severity) - getNotificationSeverityRank(a.severity)
            || String(a.title || '').localeCompare(String(b.title || ''), 'de-DE')
        ));
    }, [notificationLifecycleRegistry, notificationScopeKey, readNotificationKeys]);

    const notificationsWithReadState = useMemo(
        () => addNotificationReadState(visibleNotificationAlerts),
        [addNotificationReadState, visibleNotificationAlerts]
    );

    const browserNotificationsWithReadState = useMemo(
        () => addNotificationReadState(routedNotificationAlerts),
        [addNotificationReadState, routedNotificationAlerts]
    );
    const unreadNotificationCount = notificationsWithReadState.filter(alert => !alert.isRead).length;
    const hasUnreadPanelNotifications = unreadNotificationCount > 0;

    const markNotificationRead = useCallback((readKey) => {
        if (!readKey) return;
        setReadNotificationKeys(prev => {
            if (prev.includes(readKey)) return prev;
            const next = [...prev, readKey].slice(-300);
            return next;
        });
    }, []);

    const markAllNotificationsRead = useCallback(() => {
        const unreadKeys = notificationsWithReadState
            .filter(alert => !alert.isRead)
            .map(alert => alert.readKey);

        if (!unreadKeys.length) return;

        setReadNotificationKeys(prev => {
            const next = [...new Set([...prev, ...unreadKeys])].slice(-300);
            return next;
        });
    }, [notificationsWithReadState]);

    const openNotificationSettings = useCallback(() => {
        setIsNotificationsOpen(false);
        setProfileSettingsSection('notifications');
        setCurrentView('profile');
    }, []);

    const openNotification = useCallback((alert) => {
        const target = alert?.target;
        if (!target?.view) return;

        if (target.clearGlobalFilters) {
            setSelectedPlatform(null);
            setDrilldownPlatform(null);
            setSelectedCountry(null);
            setOrderStatusFilter('all');
        } else if (target.platform) {
            const platformName = String(target.platform).trim();
            setSelectedCountry(null);
            if (isPlatformGroupDrilldown(platformName)) {
                setDrilldownPlatform(platformName);
                setSelectedPlatform(null);
            } else {
                setSelectedPlatform(platformName);
                setDrilldownPlatform(null);
            }
        }

        markNotificationRead(alert.readKey);
        setIsNotificationsOpen(false);
        setNotificationFocus({ token: Date.now(), target });
        setCurrentView(target.view);
    }, [markNotificationRead]);
    const browserAlertsEnabled = Boolean(userPreferences?.browserNotificationsEnabled);

    useEffect(() => {
        if (!browserAlertsEnabled) return undefined;
        if (userPreferences.notificationDeliveryMode !== 'window') return undefined;
        const intervalId = window.setInterval(() => setNotificationTimeTick(Date.now()), 60000);
        return () => window.clearInterval(intervalId);
    }, [browserAlertsEnabled, userPreferences?.notificationDeliveryMode]);

    useEffect(() => {
        if (!browserAlertsEnabled) return;
        if (userPreferences.notificationDeliveryMode === 'muted') return;
        if (userPreferences.notificationDeliveryMode === 'window' && !isInsideNotificationWindow(userPreferences)) return;
        if (typeof window === 'undefined' || !('Notification' in window)) return;
        if (window.Notification.permission !== 'granted') return;

        const pushedSet = new Set(pushedNotificationKeys);
        const nextAlerts = browserNotificationsWithReadState
            .filter(alert => !alert.isRead && !pushedSet.has(alert.readKey))
            .slice(0, 3);

        if (!nextAlerts.length) return;

        nextAlerts.forEach(alert => {
            try {
                const interruptive = ['critical', 'important'].includes(alert.severity);
                const notification = new window.Notification(alert.title, {
                    body: alert.detail || alert.area || 'Neue Dashboard-Warnung',
                    tag: alert.readKey,
                    renotify: interruptive,
                    requireInteraction: alert.severity === 'critical',
                    silent: !interruptive
                });
                notification.onclick = () => {
                    window.focus();
                    openNotification(alert);
                    notification.close();
                };
            } catch (err) {
                console.warn('Browser notification failed:', err);
            }
        });

        setPushedNotificationKeys(prev => {
            const next = [...new Set([...prev, ...nextAlerts.map(alert => alert.readKey)])].slice(-300);
            return next;
        });
    }, [browserAlertsEnabled, browserNotificationsWithReadState, notificationTimeTick, openNotification, pushedNotificationKeys, userPreferences]);

    const germanySalesData = useMemo(() => {
        const map = {};
        rawPlzFacts.filter(row => {
            if (appliedStartDate && row.order_date < appliedStartDate) return false;
            if (appliedEndDate && row.order_date > appliedEndDate) return false;
            if (drilldownPlatform && row.platform_group !== drilldownPlatform) return false;
            if (selectedPlatform && row.platform !== selectedPlatform) return false;
            return true;
        }).forEach(row => {
            const bundesland = getBundeslandByPLZ(row.plz_prefix);
            if (bundesland !== 'Unknown') {
                if (!map[bundesland]) map[bundesland] = { name: bundesland, revenue: 0, profit: 0, count: 0 };
                map[bundesland].revenue += Number(row.revenue);
                map[bundesland].profit += Number(row.profit);
                map[bundesland].count += row.order_count;
            }
        });
        return Object.values(map).map(item => ({
            ...item,
            profitMargin: item.revenue > 0 ? (item.profit / item.revenue) * 100 : 0
        })).sort((a, b) => b.revenue - a.revenue);
    }, [rawPlzFacts, appliedStartDate, appliedEndDate, drilldownPlatform, selectedPlatform]);

    const plzDetailData = useMemo(() => {
        if (!activeBundesland) return [];
        const map = {};
        rawPlzFacts.filter(row => {
            if (appliedStartDate && row.order_date < appliedStartDate) return false;
            if (appliedEndDate && row.order_date > appliedEndDate) return false;
            if (drilldownPlatform && row.platform_group !== drilldownPlatform) return false;
            if (selectedPlatform && row.platform !== selectedPlatform) return false;
            return true;
        }).forEach(row => {
            const bundesland = getBundeslandByPLZ(row.plz_prefix);
            if (bundesland === activeBundesland) {
                const key = row.plz_prefix;
                if (!map[key]) map[key] = { name: key, revenue: 0, profit: 0, count: 0 };
                map[key].revenue += Number(row.revenue);
                map[key].profit += Number(row.profit);
                map[key].count += row.order_count;
            }
        });
        return Object.values(map).map(item => ({
            ...item,
            profitMargin: item.revenue > 0 ? (item.profit / item.revenue) * 100 : 0
        })).sort((a, b) => b.revenue - a.revenue);
    }, [rawPlzFacts, activeBundesland, appliedStartDate, appliedEndDate, drilldownPlatform, selectedPlatform]);

    const displayData = [];
    const pageCount = 0;
    const setPage = (page) => { if (page >= 1 && page <= pageCount) setCurrentPage(page); };

    const textClass = isDarkMode ? 'text-[#FFFFFF]' : 'text-[#0f172a]';
    const bgMain = isDarkMode ? 'bg-[#070C12]' : 'bg-[#eef2f7]';
    const bgCard = isDarkMode ? 'bg-[#0D151F]' : 'bg-white';
    const borderCard = isDarkMode ? 'border-[#404040]' : 'border-[#cbd5e1]';

    if (legalPageKey) {
        return <LegalPage pageKey={legalPageKey} />;
    }

    if (supportPageRequested) {
        return <SupportPage />;
    }

    if (typeof window !== 'undefined' && isBlogPath(window.location.pathname)) {
        return <BlogPage path={window.location.pathname} />;
    }

    if (typeof window !== 'undefined' && isSeoTopicPath(window.location.pathname)) {
        return (
            <SeoTopicPage
                path={window.location.pathname}
                onLogin={() => {
                    if (moveToLoginUrl()) return;
                    setDashboardRequested(true);
                    setShowAuthScreen(true);
                }}
                onDemoOpen={handleDemoOpen}
                onLeadRequest={() => {
                    if (typeof window !== 'undefined') window.location.assign('/demo');
                }}
            />
        );
    }

    if (demoLoadingStep >= 0) {
        return <DemoLoadingScreen currentStep={demoLoadingStep} isDarkMode={isDarkMode} />;
    }

    // Invitation links use the root URL with a hash token. The root path is
    // also treated as the landing preview, so let the invitation flow take
    // precedence or invitees would be stuck on the public landing page.
    if (landingPreviewRequested && !inviteToken) {
        return (
            <LandingPage
                onDemoOpen={handleDemoOpen}
                onAuthIntent={(intent = 'signIn') => {
                    if (moveToLoginUrl()) return;
                    setLandingPreviewRequested(false);
                    setDashboardRequested(true);

                    if (isAuthenticated) {
                        setShowAuthScreen(false);
                        return;
                    }

                    setAuthMode(inviteToken && intent === 'signUp' ? 'signUp' : 'signIn');
                    setShowAuthScreen(true);
                }}
            />
        );
    }

    if (!authReady) {
        return (
            <div className={`${bgMain} ${isDarkMode ? 'dark' : ''}`}>
                <LoadingSkeleton isDarkMode={isDarkMode} variant="screen" />
            </div>
        );
    }

    if (!isAuthenticated) {
        if (!dashboardRequested && !showAuthScreen && !inviteToken) {
            return (
                <LandingPage
                    onDemoOpen={handleDemoOpen}
                    onAuthIntent={(intent = 'signIn') => {
                        if (moveToLoginUrl()) return;
                        setLandingPreviewRequested(false);
                        setDashboardRequested(true);
                        setAuthMode(inviteToken && intent === 'signUp' ? 'signUp' : 'signIn');
                        setShowAuthScreen(true);
                    }}
                />
            );
        }

        return (
            <Auth
                authClient={database.auth}
                email={email} setEmail={setEmail}
                password={password} setPassword={setPassword}
                handleAuth={handleAuth}
                mfaFlow={mfaFlow}
                mfaCode={mfaCode}
                setMfaCode={setMfaCode}
                trustMfaDevice={trustMfaDevice}
                setTrustMfaDevice={setTrustMfaDevice}
                handleMfaVerify={handleMfaVerify}
                onMfaCancel={handleMfaCancel}
                loginError={loginError}
                authNotice={authNotice}
                isLoggingIn={isLoggingIn}
                isDarkMode={isDarkMode}
                authMode={authMode}
                setAuthMode={setAuthMode}
                inviteToken={inviteToken}
                inviteMessage={inviteMessage}
                onBackToLanding={!inviteToken ? handleBackToLanding : null}
            />
        );
    }

    if (tenantAccessLoading || !hasResolvedTenant) {
        return (
            <div className={`${bgMain} ${isDarkMode ? 'dark' : ''}`}>
                <LoadingSkeleton isDarkMode={isDarkMode} variant="screen" />
            </div>
        );
    }

    if (!hasTenantAccess) {
        return (
            <div className={`min-h-screen flex items-center justify-center p-6 ${bgMain} ${isDarkMode ? 'dark' : ''}`}>
                <div className={`w-full max-w-md rounded-xl border p-6 shadow-lg ${bgCard} ${borderCard}`}>
                    <h1 className={`text-xl font-semibold ${textClass}`}>Kein Dashboard-Zugriff</h1>
                    <p className={`mt-3 text-sm ${isDarkMode ? 'text-[#a0a0a0]' : 'text-[#475569]'}`}>
                        {inviteStatus === 'accepting' ? 'Einladung wird geprüft...' : (accessError || 'Dieses Konto ist keinem Shop zugeordnet.')}
                    </p>
                    {inviteMessage && (
                        <div className={`mt-4 rounded-lg border px-3 py-2 text-sm ${inviteStatus === 'error' ? 'border-red-500/20 bg-red-500/10 text-red-400' : 'border-emerald-500/20 bg-emerald-500/10 text-emerald-400'}`}>
                            {inviteMessage}
                        </div>
                    )}
                    <button
                        onClick={handleSignOut}
                        className="mt-5 rounded-lg bg-[#E55C1A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#d14f10]"
                    >
                        Mit anderem Konto einloggen
                    </button>
                </div>
            </div>
        );
    }

    const viewTitle = {
        overview: 'Dashboard',
        map: 'Regionen',
        customers: 'Kunden',
        articles: 'Produkte',
        returns: 'Retouren',
        comparison: 'Gewinnanalyse',
        costs: 'Kosten',
        admin: 'Admin-Center',
        profile: 'Profil',
        warehouse: 'Lager & Bestand',
        warehouseOperations: warehouseOperationsMode === 'billing' ? 'Versand · Abrechnungen' : 'Versand Performance',
        kpiWidgets: 'KPI Widgets',
        reports: 'Reports'
    }[currentView] || 'Dashboard';

    const headerPlatformOptions = Array.from(new Set([
        ...[...rawFacts, ...prevRawFacts, ...rawPlzFacts].map(row => row.platform),
        ...overviewChannels.map(row => row.platform),
        ...platformDataAll.map(row => row.name),
        ...overviewDisplayTopProducts.map(row => row.main_channel),
        selectedPlatform
    ]
        .map(value => String(value || '').trim())
        .filter(value => value && value !== 'Unknown')
    )).sort((a, b) => a.localeCompare(b, 'de'));
    const headerCountryOptions = Array.from(new Set([
        ...[...rawFacts, ...prevRawFacts, ...rawPlzFacts].map(row => row.country),
        ...countrySalesData.map(row => row.country || row.name || row.code || row.country_code),
        ...rawCustomers.map(customer => customer?.cLand || customer?.country),
        selectedCountry
    ]
        .map(value => String(value || '').trim())
        .filter(value => value && value !== 'Unknown')
    )).sort((a, b) => a.localeCompare(b, 'de'));
    const customerOriginOptions = Array.from(new Set([
        ...[customerSummary, customerOriginFacetSummary, customerSegmentFacetSummary]
            .map(unwrapDashboardCustomerSummary)
            .flatMap(summary => Array.isArray(summary?.origins) ? summary.origins : [])
            .map(origin => origin?.name),
        ...rawCustomers.map(customer => customer?.cHerkunft || customer?.origin),
        customerOriginFilter
    ].filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b), 'de'));
    const customerGroupOptions = normalizeCustomerGroupOptions([
        ...customerGroupKnownOptions,
        ...customerGroupMasterOptions,
        customerGroupFilter
    ]);
    const showCustomerHeaderFilters = currentView === 'customers';
    const showGlobalSalesFilters = GLOBAL_SALES_FILTER_VIEWS.has(currentView);
    const showPageFilterPanel = PAGE_FILTER_PANEL_VIEWS.has(currentView)
        && !(currentView === 'warehouseOperations' && warehouseOperationsMode === 'billing');
    const globalHeaderFilterCount = [
        showGlobalSalesFilters && Boolean(drilldownPlatform),
        showGlobalSalesFilters && Boolean(selectedPlatform),
        showGlobalSalesFilters && Boolean(selectedCountry),
        showGlobalSalesFilters && orderStatusFilter !== 'all',
        showCustomerHeaderFilters && !showPageFilterPanel && Boolean(customerSegmentFilter) && customerSegmentFilter !== 'all',
        showCustomerHeaderFilters && !showPageFilterPanel && Boolean(customerOriginFilter),
        showCustomerHeaderFilters && !showPageFilterPanel && Boolean(customerGroupFilter)
    ].filter(Boolean).length;
    const globalFilterCount = globalHeaderFilterCount + (showPageFilterPanel ? pageFilterCount : 0);
    const activeFilterSummary = [
        showGlobalSalesFilters && drilldownPlatform && { key: 'channel', label: `Kanal: ${drilldownPlatform}` },
        showGlobalSalesFilters && selectedPlatform && { key: 'platform', label: `Plattform: ${selectedPlatform}` },
        showGlobalSalesFilters && selectedCountry && { key: 'country', label: `Land: ${selectedCountry}` },
        showGlobalSalesFilters && orderStatusFilter !== 'all' && {
            key: 'status',
            label: `Aufträge: ${orderStatusFilter === 'cancelled' ? 'Storniert' : 'Aktiv'}`
        },
        showCustomerHeaderFilters && !showPageFilterPanel && customerSegmentFilter && customerSegmentFilter !== 'all' && {
            key: 'segment',
            label: `Segment: ${CUSTOMER_SEGMENT_FILTER_OPTIONS.find(option => option.value === customerSegmentFilter)?.label || customerSegmentFilter}`
        },
        showCustomerHeaderFilters && !showPageFilterPanel && customerOriginFilter && { key: 'origin', label: `Herkunft: ${customerOriginFilter}` },
        showCustomerHeaderFilters && !showPageFilterPanel && customerGroupFilter && { key: 'group', label: `Kundengruppe: ${customerGroupFilter}` },
        ...(showPageFilterPanel ? pageFilterLabels.map((label, index) => ({ key: `page-${index}-${label}`, label })) : []),
        ...Array.from(
            { length: showPageFilterPanel ? Math.max(0, pageFilterCount - pageFilterLabels.length) : 0 },
            (_, index) => ({ key: `page-extra-${index}`, label: 'Zusätzliche Auswahl' })
        )
    ].filter(Boolean);
    const filterPanelClass = isDarkMode ? 'bg-[#292929] border-[#404040]' : 'bg-white border-[#cbd5e1]';
    const filterInputClass = isDarkMode
        ? 'bg-[#1A1A1A] border-[#404040] text-slate-100'
        : 'bg-white border-[#cbd5e1] text-slate-800';
    const filterSectionClass = isDarkMode ? 'border-[#404040]' : 'border-slate-200';
    const filterSectionTitleClass = `text-[10px] font-black uppercase tracking-wide ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`;
    const showGlobalDashboardControls = currentView !== 'admin'
        && currentView !== 'profile'
        && (showGlobalSalesFilters || showPageFilterPanel);
    const showHeaderDateRangePicker = currentView !== 'admin' && currentView !== 'profile' && currentView !== 'controllingReports' && currentView !== 'reports';
    const currentUserEmail = currentUser?.email || '';
    const currentUserName = currentUser?.user_metadata?.full_name || currentUser?.user_metadata?.name || currentUserEmail.split('@')[0] || 'Konto';
    const currentUserInitials = currentUserName
        .split(/[.\s_-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase())
        .join('') || 'U';
    const themeToggleLabel = isDarkMode ? 'Hellmodus aktivieren' : 'Dunkelmodus aktivieren';
    const dashboardRefreshLabel = dashboardRefreshLoading && !dashboardRefreshInfo?.last_refresh_at
        ? 'Aktualisierung...'
        : formatDashboardRefreshStamp(dashboardRefreshInfo?.last_refresh_at);
    const dashboardLastOrderDate = formatDashboardRefreshDate(dashboardRefreshInfo?.last_order_date);
    const dashboardRefreshTitle = dashboardLastOrderDate
        ? `Letzter Cache-Refresh. Daten bis ${dashboardLastOrderDate}.`
        : 'Letzter Cache-Refresh';
    const dashboardRefreshInlineLabel = dashboardRefreshLoading && !dashboardRefreshInfo?.last_refresh_at
        ? 'wird geladen'
        : formatDashboardRefreshInlineStamp(dashboardRefreshInfo?.last_refresh_at);

    return (
        <div className={`${isDarkMode ? 'dark' : ''} dashboard-ui fixed inset-0 flex overflow-hidden ${bgMain} font-sans ${textClass} transition-colors duration-200`}>
            <DashboardViewportLock />
            <Sidebar
                currentView={currentView}
                setCurrentView={handleViewChange}
                warehouseOperationsMode={warehouseOperationsMode}
                setWarehouseOperationsMode={setWarehouseOperationsMode}
                isSidebarOpen={isSidebarOpen}
                setIsSidebarOpen={setIsSidebarOpen}
                isDarkMode={isDarkMode}
                isAdmin={isAdmin}
                availableTenants={availableTenants}
                activeTenantId={activeTenantId}
                activeTenantName={activeTenantName}
                activeTenantRole={activeTenantRole}
                isActiveDemoTenant={isActiveDemoTenant}
                onTenantChange={handleActiveTenantChange}
            />

            <main className={`relative h-full min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain ${currentView === 'reports' || currentView === 'costs' || currentView === 'returns' || currentView === 'warehouse' ? 'xl:flex xl:flex-col' : ''}`}>
                <nav className={`${bgCard} border-b ${borderCard} px-4 py-3 lg:px-6 flex flex-col gap-3 sticky top-0 z-40 shrink-0 xl:flex-row xl:items-center ${currentView === 'warehouse' ? 'xl:shrink-0' : ''} ${isDarkMode ? 'shadow-sm' : 'shadow-[0_1px_0_rgba(15,23,42,0.04),0_10px_24px_rgba(15,23,42,0.05)]'}`}>
                    <div className="flex min-w-0 items-center justify-between gap-3 xl:flex-1">
                        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-1">
                            <h1 className={`min-w-0 truncate text-xl font-semibold ${textClass}`}>{viewTitle}</h1>
                            {showGlobalDashboardControls && (
                                <div
                                    className={`hidden items-center gap-1.5 text-[11px] font-normal leading-none sm:inline-flex ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}
                                    title={`${dashboardRefreshTitle}: ${dashboardRefreshLabel}`}
                                >
                                    <RefreshCw className={`h-3 w-3 text-[#E55C1A] ${dashboardRefreshLoading ? 'animate-spin' : ''}`} strokeWidth={2} />
                                    <span>Letzte Aktualisierung</span>
                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>{dashboardRefreshInlineLabel}</span>
                                </div>
                            )}
                        </div>
                        <div id="header-actions" className="flex min-w-0 shrink items-center gap-2"></div>
                        <button
                            onClick={handleThemeToggle}
                            className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors md:hidden ${isDarkMode ? 'bg-[#292929] border-[#404040]' : 'bg-white border-[#cbd5e1] hover:bg-[#f3f6fa]'}`}
                            title={themeToggleLabel}
                            aria-label={themeToggleLabel}
                        >
                            <MoonIcon size={20} style={{ color: isDarkMode ? '#E55C1A' : LIGHT_MODE_COLORS.ACCENT_PRIMARY }} />
                        </button>
                    </div>

                    <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 xl:ml-auto xl:flex-none xl:flex-nowrap">
                        {isActiveDemoTenant && (
                            <button
                                type="button"
                                onClick={handleBackToLanding}
                                className="inline-flex h-9 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-[#E55C1A] bg-[#E55C1A] px-3.5 text-xs font-semibold text-white shadow-sm transition-colors hover:border-[#cf4f13] hover:bg-[#cf4f13] active:border-[#b9430f] active:bg-[#b9430f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E55C1A] focus-visible:ring-offset-2"
                                title="Zur Startseite"
                                aria-label="Zur Startseite"
                            >
                                <Home className="h-4 w-4 shrink-0" strokeWidth={2} />
                                <span>Zur Startseite</span>
                            </button>
                        )}
                        <HeaderFilterPanel
                            show={showGlobalDashboardControls}
                            drawer={showGlobalDashboardControls}
                            filterPanelRef={filterPanelRef}
                            isFilterPanelOpen={isFilterPanelOpen}
                            setIsFilterPanelOpen={setIsFilterPanelOpen}
                            globalFilterCount={globalFilterCount}
                            clearHeaderFilters={clearHeaderFilters}
                            showGlobalSalesFilters={showGlobalSalesFilters}
                            showCustomerHeaderFilters={showCustomerHeaderFilters}
                            textClass={textClass}
                            isDarkMode={isDarkMode}
                            filterPanelClass={filterPanelClass}
                            filterSectionClass={filterSectionClass}
                            filterSectionTitleClass={filterSectionTitleClass}
                            filterInputClass={filterInputClass}
                            drilldownPlatform={drilldownPlatform}
                            setDrilldownPlatform={setDrilldownPlatform}
                            selectedPlatform={selectedPlatform}
                            setSelectedPlatform={setSelectedPlatform}
                            selectedCountry={selectedCountry}
                            setSelectedCountry={setSelectedCountry}
                            orderStatusFilter={orderStatusFilter}
                            setOrderStatusFilter={setOrderStatusFilter}
                            headerPlatformOptions={headerPlatformOptions}
                            headerCountryOptions={headerCountryOptions}
                            customerSegmentFilter={customerSegmentFilter}
                            setCustomerSegmentFilter={setCustomerSegmentFilter}
                            customerSegmentFilterOptions={CUSTOMER_SEGMENT_FILTER_OPTIONS}
                            customerOriginFilter={customerOriginFilter}
                            setCustomerOriginFilter={setCustomerOriginFilter}
                            customerOriginOptions={customerOriginOptions}
                            customerGroupFilter={customerGroupFilter}
                            setCustomerGroupFilter={setCustomerGroupFilter}
                            customerGroupOptions={customerGroupOptions}
                            setCurrentPage={setCurrentPage}
                            setCustomerPage={setCustomerPage}
                            setCustomerVisibleLimit={setCustomerVisibleLimit}
                            resetCustomersRequestState={resetCustomersRequestState}
                            viewTitle={viewTitle}
                        />

                        {showHeaderDateRangePicker && (
                            <DateRangePicker
                                startDate={startDate}
                                endDate={endDate}
                                anchorDate={dataAnchorDate}
                                isDarkMode={isDarkMode}
                                onChange={(start, end, presetKey = 'custom') => {
                                    const normalizedRange = normalizeDateRange(start, end);
                                    const resolvedPreset = presetKey === 'custom'
                                        ? getPresetKeyForRange(normalizedRange.start, normalizedRange.end, dataAnchorDate)
                                        : presetKey;

                                    setStartDate(normalizedRange.start);
                                    setEndDate(normalizedRange.end);
                                    setDatePreset(resolvedPreset);
                                    setCurrentPage(1);
                                    setCustomerPage(1);
                                    setCustomerVisibleLimit(10);
                                    resetCustomersRequestState();
                                }}
                            />
                        )}
                        <button
                            onClick={handleThemeToggle}
                            className={`hidden h-9 w-9 items-center justify-center rounded-lg border transition-colors md:flex ${isDarkMode ? 'bg-[#292929] border-[#404040] hover:bg-[#404040]' : 'bg-white border-[#cbd5e1] hover:bg-[#f3f6fa] shadow-sm'}`}
                            title={themeToggleLabel}
                            aria-label={themeToggleLabel}
                        >
                            <MoonIcon size={20} style={{ color: isDarkMode ? '#E55C1A' : LIGHT_MODE_COLORS.ACCENT_PRIMARY }} />
                        </button>
                        <HeaderNotifications
                            notificationsRef={notificationsRef}
                            isNotificationsOpen={isNotificationsOpen}
                            setIsNotificationsOpen={setIsNotificationsOpen}
                            unreadNotificationCount={unreadNotificationCount}
                            notificationsWithReadState={notificationsWithReadState}
                            hasUnreadPanelNotifications={hasUnreadPanelNotifications}
                            markAllNotificationsRead={markAllNotificationsRead}
                            markNotificationRead={markNotificationRead}
                            openNotification={openNotification}
                            openNotificationSettings={openNotificationSettings}
                            notificationsEnabled={warningSettings?.boardNotificationsEnabled !== false && Object.values(userPreferences?.notificationCategories || NOTIFICATION_CATEGORY_DEFAULTS).some(Boolean)}
                            isDarkMode={isDarkMode}
                        />
                        <HeaderUserMenu
                            userMenuRef={userMenuRef}
                            isUserMenuOpen={isUserMenuOpen}
                            setIsUserMenuOpen={setIsUserMenuOpen}
                            currentUserEmail={currentUserEmail}
                            currentUserName={currentUserName}
                            currentUserInitials={currentUserInitials}
                            tenantLogoUrl={activeTenantLogoUrl}
                            activeTenantName={activeTenantName}
                            isAdmin={isAdmin}
                            isDarkMode={isDarkMode}
                            setCurrentView={setCurrentView}
                            handleSignOut={handleSignOut}
                        />
                    </div>
                </nav>

                <div className={`min-w-0 ${currentView === 'reports' || currentView === 'costs' || currentView === 'returns' ? 'p-4 lg:p-6 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col xl:overflow-y-auto xl:overflow-x-hidden' : currentView === 'warehouse' ? 'p-6 space-y-6 xl:min-h-0 xl:flex-1' : 'p-6 space-y-6'}`}>
                    <ActiveFiltersContext.Provider value={{ filters: activeFilterSummary, isDarkMode }}>
                    <React.Suspense key={activeTenantId + ':' + dataRefreshRevision} fallback={<LoadingSkeleton isDarkMode={isDarkMode} variant="content" />}>
                    {currentView === 'overview' && (
                        <Overview
                            kpiData={rpcOverviewData.kpiData}
                            kpiGrowth={rpcOverviewData.kpiGrowth}
                            comparisonKpiData={rpcOverviewData.comparisonKpiData}
                            comparisonPlatformDataAll={rpcOverviewData.comparisonPlatformDataAll}
                            comparisonDataStatus={overviewPreviousChannelsStatus}
                            chartData={rpcOverviewData.chartData}
                            previousChartData={overviewPreviousTimeseries}
                            previousChartDataStatus={overviewPreviousTimeseriesStatus}
                            chartRangeStart={appliedStartDate}
                            previousRangeStart={getPreviousDateRange(appliedStartDate, appliedEndDate, dataAnchorDate).prevStart}
                            platformDataAll={rpcOverviewData.platformDataAll}
                            displayData={displayData}
                            filteredFacts={filteredFacts}
                            isDarkMode={isDarkMode}
                            chartGrouping={chartGrouping}
                            chartGranularity={overviewChartGranularity}
                            setChartGrouping={setChartGrouping}
                            handleChartClick={handleChartClick}
                            handlePlatformClick={handlePlatformClick}
                            selectedPlatform={selectedPlatform}
                            drilldownPlatform={drilldownPlatform}
                            setDrilldownPlatform={setDrilldownPlatform}
                            currentPage={currentPage}
                            customerSummary={customerSummary}
                            returnsSummary={overviewReturnsSummary}
                            pageCount={pageCount}
                            setPage={setPage}
                            itemsPerPage={itemsPerPage}
                            statusDistribution={rpcOverviewData.statusDistribution}
                            sourceDistribution={rpcOverviewData.sourceDistribution}
                            topProducts={overviewDisplayTopProducts}
                            productSummaryLoading={overviewTopProductsLoading || productSummaryLoading}
                            rawArticles={rawArticles}
                            warningSettings={warningSettings}
                            selectedTrendProducts={overviewTrendProducts}
                            selectedTrendPlatformData={overviewTrendPlatformData}
                            selectedTrendLoading={overviewTrendDetailsLoading}
                            onTrendBucketChange={handleOverviewTrendBucketChange}
                            onOpenReturnsAnalysis={() => setCurrentView('returns')}
                            onPageFilterStateChange={handlePageFilterStateChange}
                            currentUserId={currentUser?.id || ''}
                        />
                    )}

                    {currentView === 'map' && (
                        <MapPage
                            activeTenantId={activeTenantId}
                            countrySalesData={countrySalesData}
                            germanySalesData={mapGermanyStates.length ? mapGermanyStates : germanySalesData}
                            plzDetailData={mapPlzDetail.length ? mapPlzDetail : plzDetailData}
                            mapScope={mapScope}
                            setMapScope={setMapScope}
                            activeBundesland={activeBundesland}
                            handleCountryClick={handleCountryClick}
                            handleBackToGermany={handleBackToGermany}
                            selectedCountry={selectedCountry}
                            isDarkMode={isDarkMode}
                            onPageFilterStateChange={handlePageFilterStateChange}
                            currentUserId={currentUser?.id || ''}
                        />
                    )}

                    {currentView === 'customers' && (
                        <Customers
                            activeTenantId={activeTenantId}
                            rawCustomers={rawCustomers}
                            customersLoading={customersDataLoading && rawCustomers.length === 0}
                            customersUpdating={customersDataLoading}
                            customerSummaryLoading={customerSummaryLoading}
                            customerFacetLoading={customerFacetLoading}

                            customersError={customersTableError}
                            onRetryCustomers={retryCustomersLoad}
                            customerSummary={customerSummary ? {
                                ...customerSummary,
                                acquisition: customerAcquisition
                            } : null}
                            customerSegmentFacetSummary={customerSegmentFacetSummary}
                            customerOriginFacetSummary={customerOriginFacetSummary}
                            customerTotalCount={customerTotalCount}
                            customerSearchTerm={customerSearchTerm}
                            setCustomerSearchTerm={setCustomerSearchTerm}
                            customerPage={customerPage}
                            setCustomerPage={setCustomerPage}
                            customerPageSize={customerPageSize}
                            customerVisibleLimit={customerVisibleLimit}
                            setCustomerVisibleLimit={(limit) => {
                                setCustomerVisibleLimit(limit);
                                setCustomerPage(1);
                                resetCustomersRequestState();
                            }}
                            startDate={appliedStartDate}
                            endDate={appliedEndDate}
                            selectedPlatform={selectedPlatform}
                            drilldownPlatform={drilldownPlatform}
                            selectedCountry={selectedCountry}
                            isDarkMode={isDarkMode}
                            customerSegmentFilter={customerSegmentFilter}
                            setCustomerSegmentFilter={setCustomerSegmentFilter}
                            customerOriginFilter={customerOriginFilter}
                            setCustomerOriginFilter={setCustomerOriginFilter}
                            customerGroupFilter={customerGroupFilter}
                            setCustomerGroupFilter={setCustomerGroupFilter}
                            countryNames={countryNames}
                            onPageFilterStateChange={handlePageFilterStateChange}
                            currentUserId={currentUser?.id || ''}
                        />
                    )}

                    {currentView === 'articles' && (
                        <Articles
                            rawArticles={rawArticles}
                            rawLagerbestand={rawLagerbestand}
                            rawArticleComponents={rawArticleComponents}
                            rawArticlePerformance={rawArticlePerformance}
                            catalogLoading={catalogDataLoading && !rawArticles.length}
                            performanceLoading={articlePerformanceLoading}
                            startDate={appliedStartDate}
                            endDate={appliedEndDate}
                            database={database}
                            activeTenantId={activeTenantId}
                            canWriteTenant={canWriteActiveTenant}
                            isDemoTenant={isActiveDemoTenant}
                            onArticleImageUrlsUpdated={applyArticleImageUrlUpdates}
                            onArticlePurchasePriceOverridesUpdated={applyArticlePurchasePriceOverrideUpdates}
                            alertFocus={notificationFocus}
                            isDarkMode={isDarkMode}
                            onPageFilterStateChange={handlePageFilterStateChange}
                            currentUserId={currentUser?.id || ''}
                        />
                    )}



                    {currentView === 'returns' && (
                        <Returns
                            key={activeTenantId}
                            database={database}
                            activeTenantId={activeTenantId}
                            startDate={appliedStartDate} endDate={appliedEndDate}
                            selectedPlatform={selectedPlatform}
                            drilldownPlatform={drilldownPlatform}
                            selectedCountry={selectedCountry}
                            isDarkMode={isDarkMode}
                            granularity={chartGrouping}
                            onPageFilterStateChange={handlePageFilterStateChange}
                            currentUserId={currentUser?.id || ''}
                        />
                    )}

                    {currentView === 'comparison' && (
                        <Comparison
                            database={database}
                            activeTenantId={activeTenantId}
                            rawFacts={rawFacts}
                            prevRawFacts={prevRawFacts}
                            topProducts={rpcTopProducts}
                            salesAnalysisSnapshot={salesAnalysisSnapshot}
                            isDarkMode={isDarkMode}
                            globalStartDate={startDate}
                            globalEndDate={endDate}
                            dataAnchorDate={dataAnchorDate}
                            globalSelectedPlatform={selectedPlatform}
                            globalDrilldownPlatform={drilldownPlatform}
                            globalSelectedCountry={selectedCountry}
                            productsLoading={overviewTopProductsLoading || !dateRangeReady}
                            deadStockData={deadStockData}
                            rawRetourePositions={rawRetourePositions}
                            returnsSummary={overviewReturnsSummary}
                            rawAmazonRetouren={rawAmazonRetouren}
                            rawArticles={rawArticles}
                            onPageFilterStateChange={handlePageFilterStateChange}
                            currentUserId={currentUser?.id || ''}
                        />
                    )}

                    {currentView === 'controllingReports' && (
                        <ControllingReports
                            key={activeTenantId}
                            database={database}
                            activeTenantId={activeTenantId}
                            isDarkMode={isDarkMode}
                            dataAnchorDate={dataAnchorDate}
                            onPageFilterStateChange={handlePageFilterStateChange}
                            currentUserId={currentUser?.id || ''}
                        />
                    )}

                    {currentView === 'costs' && (
                        <Costs
                            key={activeTenantId}
                            database={database}
                            activeTenantId={activeTenantId}
                            canWriteTenant={canWriteActiveTenant}
                            isDemoTenant={isActiveDemoTenant}
                            isDarkMode={isDarkMode}
                            currentRevenue={kpiData.revenue}
                            rawArticles={rawArticles}
                            startDate={String(appliedStartDate).slice(0, 10)}
                            endDate={toNextDayStartIso(appliedEndDate)}
                            selectedPlatform={selectedPlatform}
                            drilldownPlatform={drilldownPlatform}
                            selectedCountry={selectedCountry}
                            onPageFilterStateChange={handlePageFilterStateChange}
                            currentUserId={currentUser?.id || ''}
                        />
                    )}

                    {currentView === 'dataQuality' && (
                        <DataQuality
                            key={activeTenantId}
                            database={database}
                            activeTenantId={activeTenantId}
                            canWriteTenant={canWriteActiveTenant}
                            isDarkMode={isDarkMode}
                            rawArticles={rawArticles}
                            startDate={appliedStartDate}
                            endDate={appliedEndDate}
                            onNavigate={setCurrentView}
                            onArticleImageUrlsUpdated={applyArticleImageUrlUpdates}
                            onPageFilterStateChange={handlePageFilterStateChange}
                            currentUserId={currentUser?.id || ''}
                        />
                    )}

                    {currentView === 'reports' && (
                        <Reports key={activeTenantId} database={database} activeTenantId={activeTenantId} canWriteTenant={canWriteActiveTenant} isDemoTenant={isActiveDemoTenant} isDarkMode={isDarkMode} onPageFilterStateChange={handlePageFilterStateChange} currentUserId={currentUser?.id || ''} />
                    )}

                    {currentView === 'kpiWidgets' && (
                        <KpiWidgets
                            kpiData={rpcOverviewData.kpiData}
                            kpiGrowth={rpcOverviewData.kpiGrowth}
                            comparisonKpiData={rpcOverviewData.comparisonKpiData}
                            chartData={rpcOverviewData.chartData}
                            platformDataAll={rpcOverviewData.platformDataAll}
                            customerSummary={customerSummary}
                            customerSummaryLoading={customerSummaryLoading}
                            returnsSummary={overviewReturnsSummary}
                            rawArticles={rawArticles}
                            topProducts={overviewDisplayTopProducts}
                            isDarkMode={isDarkMode}
                            warningSettings={warningSettings}
                            database={database}
                            activeTenantId={activeTenantId}
                            currentUserId={currentUser?.id || ''}
                            isAdmin={isAdmin}
                            dataLoading={overviewLoading || !dateRangeReady}
                            catalogLoading={catalogDataLoading || !catalogDataLoaded}
                            topProductsLoading={overviewTopProductsLoading || !dateRangeReady}
                            returnsLoading={overviewReturnsLoading || !dateRangeReady}
                            canWriteTenant={canWriteActiveTenant}
                            isDemoTenant={isActiveDemoTenant}
                            onPageFilterStateChange={handlePageFilterStateChange}
                        />
                    )}

                    {currentView === 'warehouseOperations' && warehouseOperationsMode !== 'billing' && (
                        <WarehouseOperations
                            key={activeTenantId}
                            database={database}
                            activeTenantId={activeTenantId}
                            isDarkMode={isDarkMode}
                            mode={warehouseOperationsMode}
                            onModeChange={setWarehouseOperationsMode}
                            startDate={startDate}
                            endDate={endDate}
                            onPageFilterStateChange={handlePageFilterStateChange}
                            currentUserId={currentUser?.id || ''}
                        />
                    )}

                    {currentView === 'warehouseOperations' && warehouseOperationsMode === 'billing' && (
                        <DhlBillingPage
                            key={activeTenantId}
                            database={database}
                            activeTenantId={activeTenantId}
                            isDarkMode={isDarkMode}
                            canWriteTenant={canWriteActiveTenant}
                            startDate={startDate}
                            endDate={endDate}
                        />
                    )}

                    {currentView === 'profile' && (
                        <ProfileSettings
                            database={database}
                            currentUser={currentUser}
                            activeTenantId={activeTenantId}
                            availableTenants={availableTenants}
                            activeTenantName={activeTenantName}
                            isAdmin={isAdmin}
                            isDarkMode={isDarkMode}
                            userPreferences={userPreferences}
                            activeSection={profileSettingsSection}
                            integrationReturn={integrationReturn}
                            onSectionChange={setProfileSettingsSection}
                            onTenantLogoUpdated={handleTenantLogoUpdated}
                            panelNotificationsEnabled={warningSettings?.boardNotificationsEnabled !== false}
                            onOpenWarningSettings={isAdmin ? () => setCurrentView('admin') : null}
                            onUserPreferencesChange={updateUserPreferences}
                            onThemeChange={handleThemeChange}
                            onUserUpdated={setCurrentUser}
                            unreadNotificationCount={unreadNotificationCount}
                            onMarkAllNotificationsRead={markAllNotificationsRead}
                            onOpenDhlBilling={() => {
                                setWarehouseOperationsMode('billing');
                                handleViewChange('warehouseOperations');
                            }}
                        />
                    )}

                    {currentView === 'admin' && isAdmin && (
                        <Admin key={activeTenantId} database={database} activeTenantId={activeTenantId} isDarkMode={isDarkMode} currentUserEmail={currentUserEmail} onTenantNameUpdated={handleTenantNameUpdated} />
                    )}
                    {currentView === 'admin' && !isAdmin && (
                        <div className={`p-8 ${textClass}`}>Keine Berechtigung.</div>
                    )}

                    {currentView === 'warehouse' && (
                        <Warehouse
                            key={activeTenantId}
                            database={database}
                            isDarkMode={isDarkMode}
                            rawLagerbestand={rawLagerbestand}
                            rawArticles={rawArticles}
                            rawSupplierOrderPositions={rawSupplierOrderPositions}
                            rawPurchasePriceHistory={rawPurchasePriceHistory}
                            rawArticlePerformance={rawArticlePerformance}
                            articlePerformanceLoading={articlePerformanceLoading}
                            catalogLoading={catalogDataLoading || !catalogDataLoaded}
                            rawPositions={rawPositions}
                            activeTenantId={activeTenantId}
                            currentUserId={currentUser?.id || ''}
                            onPageFilterStateChange={handlePageFilterStateChange}
                        />
                    )}
                    </React.Suspense>
                    </ActiveFiltersContext.Provider>
                </div>
            </main>
        </div>
    );
}

export default function Dashboard() {
return (
        <DashboardErrorBoundary>
            <DashboardApp />
        </DashboardErrorBoundary>
    );
}
